import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Archive,
  Activity,
  ArrowDownToLine,
  ArrowUpFromLine,
  BookOpen,
  Boxes,
  Check,
  ChevronRight,
  CircleAlert,
  ClipboardCheck,
  Eye,
  FileDown,
  FilePenLine,
  FileSpreadsheet,
  HandCoins,
  Landmark,
  Languages,
  LogOut,
  Menu,
  PackageSearch,
  Paperclip,
  Plus,
  Printer,
  RefreshCw,
  RotateCcw,
  Save,
  Settings,
  Trash2,
  Upload,
  UserRound,
  WalletCards,
  X,
} from "lucide-react";
import { SearchableSelect } from "./components/SearchableSelect";
import { loginErrorMessage } from "./lib/auth";
import { calendarDateInTimeZone, formatDateTimeInTimeZone } from "./lib/date";
import {
  applyPriceSuggestion,
  decomposePackagingQuantity,
  draftDisplayTitle,
  orderFormFingerprint,
  preparePriceSuggestionLine,
} from "./lib/order";
import {
  resolveUnitHierarchy,
  unitConversionChanged,
  UnitHierarchyError,
} from "@shared/unit-conversion";
import { canSendStocktakeDraftRequest, initialStocktakeCounts } from "./lib/stocktake";
import { ApiError, focusFirstErrorField } from "./lib/api-error";
import {
  publishStocktakeUpdate,
  referencedOriginalDocumentIds,
  returnableDocumentHistoryPath,
  STOCKTAKE_UPDATE_CHANNEL,
  stocktakeIdFromPath,
  stocktakeTabOpenArguments,
  type StocktakeDocumentSide,
} from "./lib/stocktake-window";
import "./styles.css";

type Locale = "zh-CN" | "en";
type DocumentType = "sale" | "purchase" | "sale_return" | "purchase_return";
type Product = {
  id: string;
  code: string;
  name: string;
  alias?: string;
  defaultSupplierId?: string | null;
  baseUnit: string;
  maintenanceStatus?: string;
  sourceCode?: string;
  notes?: string;
  active?: number | boolean;
  units: Unit[];
};
type Unit = {
  id?: string;
  unitName: string;
  factor: number;
  parentUnitId?: string | null;
  unitsPerParent?: number | null;
  salePriceKhr: number;
  purchasePriceKhr: number;
  active: number | boolean;
  sortOrder?: number;
};
type EditableUnit = Omit<Unit, "active"> & {
  active: boolean;
  sortOrder: number;
  parentUnitId: string | null;
  unitsPerParent: number | null;
};
type Party = {
  id: string;
  name: string;
  contactName?: string;
  phone?: string;
  address?: string;
  notes?: string;
  active?: number | boolean;
};
type Line = {
  editorId: string;
  originalLineId?: string | null;
  productId: string;
  unitId: string;
  quantity: number;
  actualPriceKhr: number;
  notes: string;
  selected?: boolean;
  originalQuantity?: number;
  returnedQuantity?: number;
  remainingQuantity?: number;
  originalPriceKhr?: number;
  lineNo?: number;
  productCode?: string;
  productName?: string;
  unitName?: string;
  priceSource?: string;
  originalUnitName?: string;
  originalUnitId?: string;
  originalBaseQuantity?: number;
  returnedBaseQuantity?: number;
  remainingBaseQuantity?: number;
  originalSubtotalKhr?: number;
  returnedReferenceAmountKhr?: number;
  groupActualAmountKhr?: number;
  priceAdjustmentReason?: string;
};
type PersistedLine = Omit<Line, "editorId">;

function editableLine(line: PersistedLine): Line {
  return { ...line, editorId: crypto.randomUUID() };
}

function blankEditableLine(): Line {
  return editableLine({ productId: "", unitId: "", quantity: 1, actualPriceKhr: 0, notes: "" });
}

function persistedLine(line: Line): PersistedLine {
  const { editorId, ...persisted } = line;
  void editorId;
  return persisted;
}
type Draft = {
  id: string;
  documentType: DocumentType;
  title?: string;
  updatedAt: string;
  payloadJson?: string;
  payload?: Record<string, any>;
};
type OrderSeed = {
  token: string;
  mode: "revise" | "return" | "pending";
  document: any;
  sourceStocktakeId?: string;
};
type FinancialAccount = {
  id: string;
  accountCode: string;
  name: string;
  accountType: "cash" | "bank";
  balanceKhr: number;
  active: number | boolean;
};
type PackagingUnit = {
  id: string;
  unitName: string;
  factorMilli: number;
  sortOrder: number;
  active: number | boolean;
};
type ReturnPreview = {
  referenceTotalKhr: number;
  finalTotalKhr: number;
  paidAmountKhr: number;
  offsetAmountKhr: number;
  balanceKhr: number;
  returnStateFingerprint: string;
  lines: Array<{
    lineNo: number;
    originalLineId: string;
    referenceAmountKhr: number;
    actualAmountKhr: number;
    actualPriceKhr: number;
    priceAdjustmentReason: string;
  }>;
  offsets: Array<{
    documentId: string;
    documentNo: string;
    documentDate: string;
    balanceKhr: number;
    isOriginal: boolean;
    amountKhr: number;
  }>;
  formFingerprint: string;
};

function normalizedPaymentMethod(value: string | undefined) {
  if (value === "cash") return "cash";
  if (value === "bank" || value === "aba") return "bank";
  return "credit";
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

const messages = {
  zh: {
    app: "YQ 进存销系统",
    sales: "销售开单",
    purchases: "进货开单",
    history: "历史单据",
    inventory: "库存",
    receivable: "应收账款",
    payable: "应付账款",
    finance: "财务",
    operations: "操作日志",
    masters: "基础资料",
    settings: "设置",
    login: "登录",
    username: "账号",
    password: "密码",
    signIn: "进入系统",
    logout: "退出",
    newOrder: "新建单据",
    orderDate: "单据日期",
    customer: "客户",
    supplier: "供应商",
    product: "品名",
    unit: "单位",
    quantity: "数量",
    unitPrice: "实际单价 (KHR)",
    amount: "金额 (KHR)",
    paid: "已付金额 (KHR)",
    balance: "余额 (KHR)",
    payment: "付款方式",
    action: "操作",
    addLine: "增加明细",
    saveDraft: "保存草稿",
    confirm: "确认单据",
    draft: "草稿",
    empty: "暂无数据",
    refresh: "刷新",
    opening: "期初库存",
    openingHint: "首次使用必须先确认当前实际库存。",
    confirmOpening: "确认期初库存",
    search: "搜索",
    print: "预览 / 打印",
    load: "载入",
    delete: "删除",
    void: "作废",
    status: "状态",
    total: "最终总额",
  },
  en: {
    app: "YQ Trade System",
    sales: "Sales Order",
    purchases: "Purchase Order",
    history: "History",
    inventory: "Inventory",
    receivable: "Receivables",
    payable: "Payables",
    finance: "Finance",
    operations: "Operations log",
    masters: "Master Data",
    settings: "Settings",
    login: "Sign in",
    username: "Username",
    password: "Password",
    signIn: "Sign in",
    logout: "Sign out",
    newOrder: "New document",
    orderDate: "Document date",
    customer: "Customer",
    supplier: "Supplier",
    product: "Product",
    unit: "Unit",
    quantity: "Quantity",
    unitPrice: "Actual price (KHR)",
    amount: "Amount (KHR)",
    paid: "Paid (KHR)",
    balance: "Balance (KHR)",
    payment: "Payment method",
    action: "Action",
    addLine: "Add line",
    saveDraft: "Save draft",
    confirm: "Confirm",
    draft: "Draft",
    empty: "No data",
    refresh: "Refresh",
    opening: "Opening stock",
    openingHint: "Confirm the current physical stock before the first document.",
    confirmOpening: "Confirm opening stock",
    search: "Search",
    print: "Preview / print",
    load: "Load",
    delete: "Delete",
    void: "Void",
    status: "Status",
    total: "Final total",
  },
} as const;

function tr(locale: Locale, key: keyof (typeof messages)["zh"]) {
  return messages[locale === "zh-CN" ? "zh" : "en"][key];
}

function getCookie(name: string) {
  return (
    document.cookie
      .split("; ")
      .find((part) => part.startsWith(`${name}=`))
      ?.slice(name.length + 1) ?? ""
  );
}

async function api<T>(path: string, options: RequestInit = {}, operation?: string): Promise<T> {
  const method = options.method ?? "GET";
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  if (!["GET", "HEAD"].includes(method))
    headers.set("X-CSRF-Token", decodeURIComponent(getCookie("__Host-yq_csrf")));
  const response = await fetch(`/api/v1${path}`, { ...options, headers, credentials: "include" });
  const data = (await response.json().catch(() => ({}))) as {
    error?: {
      code?: string;
      message?: string;
      requestId?: string;
      fields?: Record<string, string[]>;
    };
  };
  if (!response.ok) {
    const error = new ApiError(
      data?.error?.code ?? "REQUEST_FAILED",
      data?.error?.message ?? `Request failed (${response.status})`,
      response.status,
      data?.error?.fields,
      data?.error?.requestId,
      path,
      operation,
    );
    focusFirstErrorField(error.fields);
    throw error;
  }
  return data as T;
}

async function apiForm<T>(path: string, form: FormData, operation?: string): Promise<T> {
  const headers = new Headers();
  headers.set("X-CSRF-Token", decodeURIComponent(getCookie("__Host-yq_csrf")));
  const response = await fetch(`/api/v1${path}`, {
    method: "POST",
    headers,
    body: form,
    credentials: "include",
  });
  const data = (await response.json().catch(() => ({}))) as {
    error?: {
      code?: string;
      message?: string;
      requestId?: string;
      fields?: Record<string, string[]>;
    };
  };
  if (!response.ok) {
    const error = new ApiError(
      data?.error?.code ?? "REQUEST_FAILED",
      data?.error?.message ?? `Request failed (${response.status})`,
      response.status,
      data?.error?.fields,
      data?.error?.requestId,
      path,
      operation,
    );
    focusFirstErrorField(error.fields);
    throw error;
  }
  return data as T;
}

function ReasonConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  danger = false,
  reasonRequired = true,
  locale,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  reasonRequired?: boolean;
  locale: Locale;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  if (!open) return null;
  const valid = !reasonRequired || reason.trim().length >= 2;
  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={() => {
        setReason("");
        onCancel();
      }}
    >
      <section
        className="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dialog-heading">
          <div>
            <div className="eyebrow">CONFIRM</div>
            <h2 id="confirm-dialog-title">{title}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            title={locale === "zh-CN" ? "关闭" : "Close"}
            onClick={() => {
              setReason("");
              onCancel();
            }}
          >
            <X size={18} />
          </button>
        </div>
        <div className="confirm-description">{description}</div>
        {reasonRequired && (
          <label>
            {locale === "zh-CN" ? "操作原因（必填）" : "Reason (required)"}
            <textarea
              autoFocus
              value={reason}
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
        )}
        <div className="dialog-actions">
          <button
            className="secondary-button"
            type="button"
            disabled={busy}
            onClick={() => {
              setReason("");
              onCancel();
            }}
          >
            {locale === "zh-CN" ? "取消" : "Cancel"}
          </button>
          <button
            className={danger ? "danger-button" : "primary-button"}
            type="button"
            disabled={busy || !valid}
            onClick={() => {
              const confirmedReason = reason.trim();
              setReason("");
              onConfirm(confirmedReason);
            }}
          >
            <Check size={16} />
            {busy ? "..." : confirmLabel}
          </button>
        </div>
      </section>
    </div>
  );
}

function money(value: number) {
  return new Intl.NumberFormat("en-US").format(Math.round(value));
}

function today() {
  return calendarDateInTimeZone();
}

function currentMonth() {
  return today().slice(0, 7);
}

function TurnstileBox({
  siteKey,
  locale,
  onToken,
}: {
  siteKey: string;
  locale: Locale;
  onToken: (token: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let widgetId = "";
    let cancelled = false;
    const render = () => {
      if (cancelled || !containerRef.current || !window.turnstile || widgetId) return;
      widgetId = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action: "login",
        language: locale === "zh-CN" ? "zh-cn" : "en",
        callback: (token: string) => onToken(token),
        "expired-callback": () => onToken(""),
        "error-callback": () => onToken(""),
      });
    };
    const existing = document.querySelector<HTMLScriptElement>('script[data-yq-turnstile="true"]');
    if (window.turnstile) render();
    else if (existing) existing.addEventListener("load", render, { once: true });
    else {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.yqTurnstile = "true";
      script.addEventListener("load", render, { once: true });
      document.head.appendChild(script);
    }
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [siteKey, locale, onToken]);
  return <div className="turnstile-box" ref={containerRef} />;
}

function Login({
  onLogin,
  locale,
  setLocale,
}: {
  onLogin: (user: any) => void;
  locale: Locale;
  setLocale: (value: Locale) => void;
}) {
  const [username, setUsername] = useState("yiqian");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<{ code: string; fallback: string }>();
  const [turnstileConfig, setTurnstileConfig] = useState<{
    enabled: boolean;
    siteKey: string | null;
  }>();
  const [turnstileToken, setTurnstileToken] = useState("");
  useEffect(() => {
    api<{ turnstileEnabled: boolean; turnstileSiteKey: string | null }>("/auth/config")
      .then((result) =>
        setTurnstileConfig({
          enabled: result.turnstileEnabled,
          siteKey: result.turnstileSiteKey,
        }),
      )
      .catch(() =>
        setError({ code: "SECURITY_CONFIG_LOAD_FAILED", fallback: "Security config failed." }),
      );
  }, []);
  const handleTurnstileToken = useCallback((token: string) => setTurnstileToken(token), []);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(undefined);
    if (!turnstileConfig) {
      setError({ code: "SECURITY_CONFIG_LOADING", fallback: "Loading security config." });
      return;
    }
    if (turnstileConfig.enabled && !turnstileConfig.siteKey) {
      setError({
        code: "SECURITY_CONFIG_UNAVAILABLE",
        fallback: "Security verification is unavailable.",
      });
      return;
    }
    if (turnstileConfig.enabled && !turnstileToken) {
      setError({ code: "TURNSTILE_REQUIRED", fallback: "Complete the security check first." });
      return;
    }
    try {
      const result = await api<{ user: any }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({
          username,
          password,
          turnstileToken: turnstileConfig.enabled ? turnstileToken : undefined,
        }),
      });
      onLogin(result.user);
    } catch (err) {
      setError({
        code: err instanceof ApiError ? err.code : "LOGIN_FAILED",
        fallback: err instanceof Error ? err.message : "Sign-in failed.",
      });
    }
  };
  return (
    <main className="login-shell">
      <div className="login-rail">
        <img className="brand-logo" src="/brand/yq-depo-logo-reverse.png" alt="YQ DEPO" />
        <p>Wholesale operations console</p>
        <div className="login-rule" />
      </div>
      <form className="login-card" onSubmit={submit}>
        <div className="eyebrow">YQ DEPO</div>
        <h1>{tr(locale, "app")}</h1>
        <p className="muted">
          {locale === "zh-CN"
            ? "销售、进货与库存的统一工作台"
            : "One workspace for sales, purchases and stock"}
        </p>
        <label>
          {tr(locale, "username")}
          <input
            id="login-username"
            name="username"
            autoFocus
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
          />
        </label>
        <label>
          {tr(locale, "password")}
          <input
            id="login-password"
            name="password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
          />
        </label>
        {turnstileConfig?.enabled && turnstileConfig.siteKey ? (
          <TurnstileBox
            siteKey={turnstileConfig.siteKey}
            locale={locale}
            onToken={handleTurnstileToken}
          />
        ) : turnstileConfig && !turnstileConfig.enabled ? (
          <div className="test-security-banner">
            <CircleAlert size={15} />
            {locale === "zh-CN"
              ? "测试环境：安全验证已关闭"
              : "Staging: security verification is off"}
          </div>
        ) : null}
        {error && (
          <div className="error-box">
            <CircleAlert size={16} />
            {loginErrorMessage(error.code, locale, error.fallback)}
          </div>
        )}
        <button className="primary-button" type="submit">
          <ChevronRight size={17} />
          {tr(locale, "signIn")}
        </button>
        <button
          className="language-button"
          type="button"
          onClick={() => setLocale(locale === "zh-CN" ? "en" : "zh-CN")}
        >
          <Languages size={15} />
          {locale === "zh-CN" ? "English" : "中文"}
        </button>
      </form>
    </main>
  );
}

function Shell({
  user,
  locale,
  setLocale,
  onLogout,
  page,
  onNavigate,
  environment,
  children,
}: any) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const nav = [
    { id: "sales", label: tr(locale, "sales"), icon: ArrowUpFromLine },
    { id: "purchase", label: tr(locale, "purchases"), icon: ArrowDownToLine },
    { id: "history", label: tr(locale, "history"), icon: BookOpen },
    { id: "inventory", label: tr(locale, "inventory"), icon: Boxes },
    { id: "receivable", label: tr(locale, "receivable"), icon: HandCoins },
    { id: "payable", label: tr(locale, "payable"), icon: Landmark },
    { id: "finance", label: tr(locale, "finance"), icon: WalletCards },
    ...(user.role === "admin"
      ? [{ id: "operations", label: tr(locale, "operations"), icon: Activity }]
      : []),
    { id: "masters", label: tr(locale, "masters"), icon: PackageSearch },
    { id: "settings", label: tr(locale, "settings"), icon: Settings },
  ];
  const navigate = (nextPage: string) => {
    if (onNavigate(nextPage) !== false) setMobileNavOpen(false);
  };
  return (
    <div className="app-shell">
      {mobileNavOpen && (
        <button
          className="mobile-nav-backdrop"
          type="button"
          aria-label={locale === "zh-CN" ? "关闭导航" : "Close navigation"}
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      <aside className={`sidebar ${mobileNavOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-brand">
          <img className="brand-logo small" src="/brand/yq-depo-logo-reverse.png" alt="" />
          <span>{tr(locale, "app")}</span>
          <button
            className="icon-button sidebar-close"
            type="button"
            title={locale === "zh-CN" ? "关闭导航" : "Close navigation"}
            aria-label={locale === "zh-CN" ? "关闭导航" : "Close navigation"}
            onClick={() => setMobileNavOpen(false)}
          >
            <X size={18} />
          </button>
        </div>
        <div className="side-label">WORKSPACE</div>
        {nav.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={`nav-button ${page === id ? "active" : ""}`}
            onClick={() => navigate(id)}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}
        <div className="sidebar-bottom">
          <div className="user-chip">
            <UserRound size={16} />
            <span>{user.displayName || user.username}</span>
          </div>
          <button className="nav-button" onClick={onLogout}>
            <LogOut size={17} />
            {tr(locale, "logout")}
          </button>
        </div>
      </aside>
      <main className="main-area">
        <header className="topbar">
          <div>
            <span className="env-tag">{String(environment || "unknown").toUpperCase()}</span>
            <span className="page-context">{nav.find((item) => item.id === page)?.label}</span>
          </div>
          <div className="top-actions">
            <button
              className="icon-button"
              title="Switch language"
              onClick={() => setLocale(locale === "zh-CN" ? "en" : "zh-CN")}
            >
              <Languages size={18} />
              <span>{locale === "zh-CN" ? "EN" : "中"}</span>
            </button>
            <span className="online-dot">● online</span>
            <button
              className="icon-button mobile-menu"
              type="button"
              title={locale === "zh-CN" ? "打开导航" : "Open navigation"}
              aria-label={locale === "zh-CN" ? "打开导航" : "Open navigation"}
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu size={18} />
            </button>
          </div>
        </header>
        <section className="content">{children}</section>
      </main>
    </div>
  );
}

function OrderPage({
  type,
  locale,
  notify,
  seed,
  onSeedConsumed,
  onDirtyChange,
  embedded = false,
  onCompleted,
}: {
  type: "sale" | "purchase";
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
  seed?: OrderSeed;
  onSeedConsumed: () => void;
  onDirtyChange: (dirty: boolean) => void;
  embedded?: boolean;
  onCompleted?: (result: any) => void | Promise<void>;
}) {
  const initialDocument = seed?.document;
  const isPendingSeed = seed?.mode === "pending";
  const isInitialReturn =
    seed?.mode === "return" ||
    (isPendingSeed && String(initialDocument?.documentType ?? "").endsWith("_return"));
  const initialLines: PersistedLine[] = (() => {
    if (isInitialReturn && initialDocument?.lines?.length) {
      const seeded: PersistedLine[] = initialDocument.lines.map((line: any) => ({
        originalLineId: isPendingSeed ? line.originalLineId : line.id,
        productId: line.productId,
        unitId: line.unitId,
        quantity: Number(isPendingSeed ? line.quantity : line.remainingQuantity),
        actualPriceKhr: Number(line.actualPriceKhr),
        notes: line.notes ?? "",
        selected: isPendingSeed ? Boolean(line.selected ?? true) : false,
        originalQuantity: Number(isPendingSeed ? line.originalQuantity : line.quantity),
        returnedQuantity: Number(line.returnedQuantity ?? 0),
        remainingQuantity: Number(line.remainingQuantity),
        originalPriceKhr: Number(line.originalPriceKhr ?? line.actualPriceKhr),
        lineNo: Number(line.lineNo),
        productCode: line.productCode,
        productName: line.productName,
        unitName: line.unitName,
        originalUnitName: line.originalUnitName ?? line.unitName,
        originalUnitId: line.originalUnitId ?? line.unitId,
        originalBaseQuantity: Number(
          line.originalBaseQuantity ?? line.baseQuantity ?? line.quantity * line.factor,
        ),
        returnedBaseQuantity: Number(line.returnedBaseQuantity ?? 0),
        remainingBaseQuantity: Number(
          line.remainingBaseQuantity ?? line.remainingQuantity * line.factor,
        ),
        originalSubtotalKhr: Number(line.originalSubtotalKhr ?? line.subtotalKhr),
        returnedReferenceAmountKhr: Number(line.returnedReferenceAmountKhr ?? 0),
        priceAdjustmentReason: line.priceAdjustmentReason ?? "",
      }));
      if (isPendingSeed) {
        const grouped = new Map<string, PersistedLine[]>();
        for (const line of seeded) {
          const key = line.originalLineId ?? "";
          const group = grouped.get(key) ?? [];
          group.push(line);
          grouped.set(key, group);
        }
        for (const group of grouped.values()) {
          const actual = group.reduce(
            (sum, line) =>
              sum +
              Number(
                initialDocument.lines.find(
                  (candidate: any) =>
                    candidate.lineNo === line.lineNo &&
                    candidate.originalLineId === line.originalLineId,
                )?.subtotalKhr ?? 0,
              ),
            0,
          );
          const reference = group.reduce(
            (sum, line) =>
              sum +
              Number(
                initialDocument.lines.find(
                  (candidate: any) =>
                    candidate.lineNo === line.lineNo &&
                    candidate.originalLineId === line.originalLineId,
                )?.referenceAmountKhr ?? 0,
              ),
            0,
          );
          if (actual < reference) group[0].groupActualAmountKhr = actual;
        }
      }
      return seeded;
    }
    if (initialDocument?.lines?.length) {
      return initialDocument.lines.map((line: any) => ({
        productId: line.productId,
        unitId: line.unitId,
        quantity: Number(line.quantity),
        actualPriceKhr: Number(line.actualPriceKhr),
        notes: line.notes ?? "",
      }));
    }
    return [{ productId: "", unitId: "", quantity: 1, actualPriceKhr: 0, notes: "" }];
  })();
  const [products, setProducts] = useState<Product[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [date, setDate] = useState(
    seed?.mode === "return" ? today() : (initialDocument?.documentDate ?? today()),
  );
  const [partyId, setPartyId] = useState(
    initialDocument
      ? type === "sale"
        ? initialDocument.customerId
        : initialDocument.supplierId
      : "",
  );
  const startingPaymentMethod =
    seed?.mode === "return"
      ? "cash"
      : normalizedPaymentMethod(initialDocument?.paymentMethod ?? "credit");
  const [paymentMethod, setPaymentMethod] = useState(startingPaymentMethod);
  const [accountId, setAccountId] = useState<string>(
    initialDocument?.settlements?.find(
      (settlement: any) => settlement.status === "posted" && settlement.accountId,
    )?.accountId ?? "",
  );
  const [paid, setPaid] = useState(
    seed?.mode === "revise"
      ? Number(initialDocument?.paidTotalKhr ?? 0)
      : isPendingSeed
        ? Number(initialDocument?.paidAmountKhr ?? 0)
        : 0,
  );
  const [lines, setLines] = useState<Line[]>(() => initialLines.map(editableLine));
  const priceRequestGenerations = useRef(new Map<string, number>());
  const [busy, setBusy] = useState(false);
  const [draftId, setDraftId] = useState<string | undefined>();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [selectedDraft, setSelectedDraft] = useState("");
  const [mode, setMode] = useState<"new" | "revise" | "return">(
    seed?.mode === "revise" ? "revise" : isInitialReturn ? "return" : "new",
  );
  const [returnMode, setReturnMode] = useState<"partial" | "full" | "">(
    isInitialReturn
      ? ((initialDocument?.payload?.returnMode ?? initialDocument?.returnMode ?? "") as
          "partial" | "full" | "")
      : "",
  );
  const [returnPreview, setReturnPreview] = useState<ReturnPreview>();
  const [revisionDocumentId, setRevisionDocumentId] = useState(
    seed?.mode === "revise" ? (initialDocument?.id ?? "") : "",
  );
  const [originalDocumentId, setOriginalDocumentId] = useState(
    isInitialReturn
      ? (initialDocument?.originalDocumentId ??
          (seed?.mode === "return" ? initialDocument?.id : "") ??
          "")
      : "",
  );
  const [pendingDocumentId, setPendingDocumentId] = useState(
    isPendingSeed ? (initialDocument?.pendingDocumentId ?? initialDocument?.id ?? "") : "",
  );
  const [sourceStocktakeId, setSourceStocktakeId] = useState(
    seed?.sourceStocktakeId ?? (isPendingSeed ? (initialDocument?.sourceStocktakeId ?? "") : ""),
  );
  const [activeStocktake, setActiveStocktake] = useState<any>();
  const [returnOriginalBalanceKhr, setReturnOriginalBalanceKhr] = useState(
    Number(
      initialDocument?.originalDocumentBalanceKhr ??
        (seed?.mode === "return" ? initialDocument?.balanceKhr : 0) ??
        0,
    ),
  );
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const activeDocumentType: DocumentType = mode === "return" ? `${type}_return` : type;
  const stocktakeTargetId = sourceStocktakeId || activeStocktake?.id || "";
  const isStocktakePendingFlow = Boolean(stocktakeTargetId) && mode !== "revise";
  const selectedLines = useMemo(
    () => (mode === "return" ? lines.filter((line) => line.selected) : lines),
    [lines, mode],
  );
  const allReturnGroups = useMemo(() => {
    const grouped = new Map<string, Line[]>();
    for (const line of lines) {
      const key = line.originalLineId ?? line.editorId;
      const group = grouped.get(key) ?? [];
      group.push(line);
      grouped.set(key, group);
    }
    return [...grouped.entries()].map(([originalLineId, groupLines]) => ({
      originalLineId,
      lines: groupLines,
    }));
  }, [lines]);
  const returnGroups = useMemo(() => {
    const grouped = new Map<string, Line[]>();
    for (const line of selectedLines) {
      const key = line.originalLineId ?? line.editorId;
      const group = grouped.get(key) ?? [];
      group.push(line);
      grouped.set(key, group);
    }
    return [...grouped.entries()].map(([originalLineId, groupLines]) => ({
      originalLineId,
      lines: groupLines,
    }));
  }, [selectedLines]);
  const estimatedReturnTotal = useMemo(
    () =>
      returnGroups.reduce((sum, group) => {
        const source = group.lines[0];
        const product = products.find((item) => item.id === source.productId);
        const requestedBase = group.lines.reduce((quantity, line) => {
          const factor = product?.units.find((unit) => unit.id === line.unitId)?.factor ?? 0;
          return quantity + line.quantity * factor;
        }, 0);
        const originalBase = Number(source.originalBaseQuantity ?? 0);
        const remainingBase = Number(source.remainingBaseQuantity ?? 0);
        const remainingReference = Math.max(
          0,
          Number(source.originalSubtotalKhr ?? 0) - Number(source.returnedReferenceAmountKhr ?? 0),
        );
        const reference =
          requestedBase === remainingBase
            ? remainingReference
            : originalBase > 0
              ? Math.min(
                  remainingReference,
                  Math.round(
                    (Number(source.originalSubtotalKhr ?? 0) * requestedBase) / originalBase,
                  ),
                )
              : 0;
        return sum + Math.min(reference, source.groupActualAmountKhr ?? reference);
      }, 0),
    [products, returnGroups],
  );
  const ordinaryTotal = useMemo(
    () =>
      selectedLines.reduce((sum, line) => sum + Math.round(line.actualPriceKhr * line.quantity), 0),
    [selectedLines],
  );
  const provisionalTotal = mode === "return" ? estimatedReturnTotal : ordinaryTotal;
  const provisionalPaid =
    mode === "return"
      ? 0
      : mode === "new" && paymentMethod !== "credit" && paid === 0
        ? provisionalTotal
        : paid;
  const currentFingerprint = useMemo(
    () =>
      orderFormFingerprint({
        documentType: activeDocumentType,
        documentDate: date,
        partyId,
        paymentMethod,
        accountId: embedded ? "" : accountId,
        paidAmountKhr: provisionalPaid,
        originalDocumentId,
        returnMode,
        lines,
      }),
    [
      accountId,
      activeDocumentType,
      date,
      embedded,
      lines,
      originalDocumentId,
      partyId,
      paymentMethod,
      provisionalPaid,
      returnMode,
    ],
  );
  const activeReturnPreview =
    returnPreview?.formFingerprint === currentFingerprint ? returnPreview : undefined;
  const total =
    mode === "return" ? (activeReturnPreview?.finalTotalKhr ?? provisionalTotal) : provisionalTotal;
  const returnOffset =
    mode === "return"
      ? (activeReturnPreview?.offsetAmountKhr ?? Math.min(total, returnOriginalBalanceKhr))
      : 0;
  const effectivePaid =
    mode === "return"
      ? (activeReturnPreview?.paidAmountKhr ?? Math.max(0, total - returnOffset))
      : provisionalPaid;
  const [baselineFingerprint, setBaselineFingerprint] = useState(currentFingerprint);
  const isDirty = embedded
    ? currentFingerprint !== baselineFingerprint
    : mode !== "new" || currentFingerprint !== baselineFingerprint;
  useEffect(() => onDirtyChange(isDirty), [isDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  const loadDrafts = () =>
    api<{ items: Draft[] }>("/documents/drafts")
      .then((data) =>
        setDrafts(
          data.items.filter(
            (draft) => draft.documentType === type || draft.documentType === `${type}_return`,
          ),
        ),
      )
      .catch((error) => notify(error.message, "error"));
  useEffect(() => {
    const draftRequest = embedded
      ? Promise.resolve({ items: [] as Draft[] })
      : api<{ items: Draft[] }>("/documents/drafts");
    Promise.all([
      api<{ items: Product[] }>("/masters/products?active=true"),
      api<{ items: Party[] }>(
        `/masters/${type === "sale" ? "customers" : "suppliers"}?active=true`,
      ),
      api<{ items: FinancialAccount[] }>("/finance/accounts"),
      draftRequest,
      api<{ activeStocktake?: any }>("/inventory/summary"),
    ])
      .then(([productData, partyData, accountData, draftData, inventorySummary]) => {
        setProducts(productData.items);
        setParties(partyData.items);
        setAccounts(accountData.items.filter((account) => Boolean(account.active)));
        setActiveStocktake(inventorySummary.activeStocktake);
        setAccountId((current) => {
          if (current || startingPaymentMethod === "credit") return current;
          const preferredType = startingPaymentMethod === "cash" ? "cash" : "bank";
          return (
            accountData.items.find(
              (account) => Boolean(account.active) && account.accountType === preferredType,
            )?.id ?? ""
          );
        });
        setDrafts(
          draftData.items.filter(
            (draft) => draft.documentType === type || draft.documentType === `${type}_return`,
          ),
        );
      })
      .catch((error) => notify(error.message, "error"));
  }, [embedded, type, notify, startingPaymentMethod]);
  const balance = total - effectivePaid - returnOffset;
  const setLine = (index: number, patch: Partial<Line>) =>
    setLines((current) =>
      current.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)),
    );
  const chooseReturnMode = (nextMode: "partial" | "full") => {
    setReturnMode(nextMode);
    setReturnPreview(undefined);
    if (nextMode === "partial") {
      setLines((current) => current.map((line) => ({ ...line, selected: false })));
      return;
    }
    setLines((current) => {
      const sources = new Map<string, Line>();
      for (const line of current) {
        const key = line.originalLineId ?? line.editorId;
        if (!sources.has(key)) sources.set(key, line);
      }
      return [...sources.values()].flatMap((source): Line[] => {
        const product = products.find((item) => item.id === source.productId);
        const units = [...(product?.units ?? [])]
          .filter((unit) => Boolean(unit.active) && unit.id && unit.factor > 0)
          .map((unit) => ({ id: unit.id!, factor: unit.factor }));
        const remaining = Number(source.remainingBaseQuantity ?? 0);
        if (remaining <= 0) return [{ ...source, selected: false }];
        const decomposition = decomposePackagingQuantity(remaining, units);
        const packaged: Line[] = decomposition.lines.flatMap((packaging, index): Line[] => {
          const unit = product?.units.find((candidate) => candidate.id === packaging.unitId);
          if (!unit?.id) return [];
          return [
            {
              ...source,
              editorId: crypto.randomUUID(),
              unitId: unit.id,
              unitName: unit.unitName,
              quantity: packaging.quantity,
              selected: true,
              groupActualAmountKhr: index === 0 ? source.groupActualAmountKhr : undefined,
            },
          ];
        });
        return packaged.length ? packaged : [{ ...source, selected: true }];
      });
    });
  };
  const setReturnGroupSelected = (originalLineId: string, selected: boolean) =>
    setLines((current) =>
      current.map((line) =>
        line.originalLineId === originalLineId ? { ...line, selected } : line,
      ),
    );
  const addReturnPackaging = (originalLineId: string) =>
    setLines((current) => {
      const sourceIndex = current.findIndex((line) => line.originalLineId === originalLineId);
      if (sourceIndex < 0) return current;
      const source = current[sourceIndex];
      const product = products.find((item) => item.id === source.productId);
      const used = new Set(
        current.filter((line) => line.originalLineId === originalLineId).map((line) => line.unitId),
      );
      const unit =
        product?.units.find(
          (candidate) => candidate.active && candidate.id && !used.has(candidate.id),
        ) ?? product?.units.find((candidate) => candidate.active && candidate.id);
      if (!unit?.id) return current;
      const next = editableLine({
        ...persistedLine(source),
        unitId: unit.id,
        unitName: unit.unitName,
        quantity: 1,
        selected: true,
        groupActualAmountKhr: undefined,
      });
      const lastGroupIndex = current.reduce(
        (found, line, index) => (line.originalLineId === originalLineId ? index : found),
        sourceIndex,
      );
      return [...current.slice(0, lastGroupIndex + 1), next, ...current.slice(lastGroupIndex + 1)];
    });
  const removeReturnPackaging = (editorId: string, originalLineId: string) =>
    setLines((current) => {
      const group = current.filter((line) => line.originalLineId === originalLineId);
      if (group.length <= 1) {
        return current.map((line) =>
          line.editorId === editorId ? { ...line, selected: false } : line,
        );
      }
      const removed = current.find((line) => line.editorId === editorId);
      let reassigned = false;
      return current
        .filter((line) => line.editorId !== editorId)
        .map((line) => {
          if (
            removed?.groupActualAmountKhr === undefined ||
            reassigned ||
            line.originalLineId !== originalLineId
          ) {
            return line;
          }
          reassigned = true;
          return { ...line, groupActualAmountKhr: removed.groupActualAmountKhr };
        });
    });
  const maintainedPrice = (product: Product | undefined, unitId: string) => {
    const unit = product?.units.find((item) => item.id === unitId);
    return type === "sale" ? (unit?.salePriceKhr ?? 0) : (unit?.purchasePriceKhr ?? 0);
  };
  const nextPriceRequestGeneration = (editorId: string) => {
    const next = (priceRequestGenerations.current.get(editorId) ?? 0) + 1;
    priceRequestGenerations.current.set(editorId, next);
    return next;
  };
  const suggestPrice = async (
    editorId: string,
    productId: string,
    unitId: string,
    selectedPartyId = partyId,
    resetToMaintainedPrice = true,
  ) => {
    if (!productId || !unitId) return;
    const requestGeneration = nextPriceRequestGeneration(editorId);
    const product = products.find((item) => item.id === productId);
    setLines((current) =>
      current.map((line) =>
        line.editorId === editorId
          ? preparePriceSuggestionLine(
              line,
              productId,
              unitId,
              maintainedPrice(product, unitId),
              resetToMaintainedPrice,
            )
          : line,
      ),
    );
    try {
      const result = await api<{ priceKhr: number; source: string }>(
        `/masters/products/${productId}/price-suggestion?documentType=${type}&unitId=${encodeURIComponent(unitId)}&partyId=${encodeURIComponent(selectedPartyId)}`,
      );
      setLines((current) =>
        current.map((line) =>
          line.editorId === editorId
            ? applyPriceSuggestion(
                line,
                {
                  productId,
                  unitId,
                  priceKhr: result.priceKhr,
                  source: result.source,
                },
                {
                  requestGeneration,
                  latestGeneration: priceRequestGenerations.current.get(editorId) ?? 0,
                },
              )
            : line,
        ),
      );
    } catch (error) {
      if (priceRequestGenerations.current.get(editorId) === requestGeneration) {
        notify(error instanceof Error ? error.message : "价格建议读取失败", "error");
      }
    }
  };
  const chooseProduct = (index: number, productId: string) => {
    const editorId = lines[index]?.editorId;
    if (!editorId) return;
    const product = products.find((item) => item.id === productId);
    const unit = product?.units.find((item) => item.active) ?? product?.units[0];
    if (!unit?.id) {
      nextPriceRequestGeneration(editorId);
      setLine(index, { productId, unitId: "", actualPriceKhr: 0, priceSource: undefined });
      return;
    }
    void suggestPrice(editorId, productId, unit.id);
  };
  const chooseUnit = (index: number, productId: string, unitId: string) => {
    const editorId = lines[index]?.editorId;
    if (editorId) void suggestPrice(editorId, productId, unitId);
  };
  const addPackagingLine = (index: number) => {
    const source = lines[index];
    const product = products.find((item) => item.id === source?.productId);
    if (!source || !product) return;
    const usedUnits = new Set(
      lines.filter((line) => line.productId === source.productId).map((line) => line.unitId),
    );
    const unit =
      product.units.find(
        (candidate) => candidate.active && candidate.id && !usedUnits.has(candidate.id),
      ) ?? product.units.find((candidate) => candidate.active && candidate.id);
    if (!unit?.id) return;
    const next = editableLine({
      productId: source.productId,
      unitId: unit.id,
      quantity: 1,
      actualPriceKhr: maintainedPrice(product, unit.id),
      notes: "",
      priceSource: "maintained_default",
    });
    setLines((current) => [...current.slice(0, index + 1), next, ...current.slice(index + 1)]);
    void suggestPrice(next.editorId, next.productId, next.unitId);
  };
  const chooseParty = (nextPartyId: string) => {
    setPartyId(nextPartyId);
    lines.forEach((line) => {
      if (line.productId && line.unitId) {
        void suggestPrice(line.editorId, line.productId, line.unitId, nextPartyId, false);
      }
    });
  };
  const accountForMethod = (method: string) => {
    if (method === "credit") return "";
    const preferredType = method === "cash" ? "cash" : "bank";
    return (
      accounts.find((account) => account.accountType === preferredType)?.id ?? accounts[0]?.id ?? ""
    );
  };
  const choosePaymentMethod = (method: string, defaultToTotal = true) => {
    setPaymentMethod(method);
    if (mode !== "return") {
      if (method === "credit") setPaid(0);
      else if (defaultToTotal && paid <= 0) setPaid(total);
    }
    setAccountId((current) => {
      if (method === "credit") return "";
      const currentAccount = accounts.find((account) => account.id === current);
      const preferredType = method === "cash" ? "cash" : "bank";
      return currentAccount?.accountType === preferredType ? current : accountForMethod(method);
    });
  };
  const reset = () => {
    const nextDate = today();
    const nextLines = [blankEditableLine()];
    priceRequestGenerations.current.clear();
    setBaselineFingerprint(
      orderFormFingerprint({
        documentType: type,
        documentDate: nextDate,
        partyId: "",
        paymentMethod: "credit",
        accountId: "",
        paidAmountKhr: 0,
        originalDocumentId: "",
        returnMode: "",
        lines: nextLines,
      }),
    );
    setDate(nextDate);
    setPartyId("");
    setPaymentMethod("credit");
    setAccountId("");
    setPaid(0);
    setLines(nextLines);
    setDraftId(undefined);
    setSelectedDraft("");
    setMode("new");
    setReturnMode("");
    setReturnPreview(undefined);
    setRevisionDocumentId("");
    setOriginalDocumentId("");
    setPendingDocumentId("");
    setSourceStocktakeId("");
    setReturnOriginalBalanceKhr(0);
    onSeedConsumed();
  };
  const requestReset = () => {
    if (
      isDirty &&
      !window.confirm(
        locale === "zh-CN"
          ? "当前单据有未保存修改，确认放弃并新建空白单据？"
          : "Discard the unsaved changes and start a blank document?",
      )
    )
      return;
    reset();
  };
  const applyPayload = (payload: Record<string, any>) => {
    const payloadType = String(payload.documentType ?? type) as DocumentType;
    const nextMode = payloadType.endsWith("_return") ? "return" : "new";
    const nextDate = payload.documentDate ?? today();
    const nextPartyId = payload[type === "sale" ? "customerId" : "supplierId"] ?? "";
    const nextPaymentMethod = normalizedPaymentMethod(payload.paymentMethod ?? "credit");
    const nextAccountId = payload.accountId ?? accountForMethod(nextPaymentMethod);
    const nextPaid = Number(payload.paidAmountKhr ?? 0);
    const nextOriginalDocumentId = payload.originalDocumentId ?? "";
    const nextReturnMode = (payload.returnMode ?? "") as "partial" | "full" | "";
    const nextOriginalBalanceKhr = Number(payload.originalDocumentBalanceKhr ?? 0);
    const nextLines: Line[] =
      Array.isArray(payload.lines) && payload.lines.length
        ? payload.lines.map((line) => editableLine(line as PersistedLine))
        : [blankEditableLine()];
    priceRequestGenerations.current.clear();
    setBaselineFingerprint(
      orderFormFingerprint({
        documentType: nextMode === "return" ? `${type}_return` : type,
        documentDate: nextDate,
        partyId: nextPartyId,
        paymentMethod: nextPaymentMethod,
        accountId: nextAccountId,
        paidAmountKhr: nextPaid,
        originalDocumentId: nextOriginalDocumentId,
        returnMode: nextReturnMode,
        lines: nextLines,
      }),
    );
    setMode(nextMode);
    setReturnMode(nextReturnMode);
    setReturnPreview(undefined);
    setDate(nextDate);
    setPartyId(nextPartyId);
    setPaymentMethod(nextPaymentMethod);
    setAccountId(nextAccountId);
    setPaid(nextPaid);
    setOriginalDocumentId(nextOriginalDocumentId);
    setReturnOriginalBalanceKhr(nextOriginalBalanceKhr);
    setRevisionDocumentId("");
    setPendingDocumentId("");
    setSourceStocktakeId("");
    setLines(nextLines);
  };
  const loadDraft = async () => {
    if (!selectedDraft) return;
    if (
      isDirty &&
      !window.confirm(
        locale === "zh-CN"
          ? "载入草稿会覆盖当前未保存修改，是否继续？"
          : "Loading the draft will replace the current unsaved changes. Continue?",
      )
    )
      return;
    setBusy(true);
    try {
      const result = await api<{ payload: Record<string, any> }>(
        `/documents/drafts/${selectedDraft}`,
      );
      applyPayload(result.payload);
      setDraftId(selectedDraft);
      notify(locale === "zh-CN" ? "草稿已载入" : "Draft loaded");
    } catch (error) {
      notify(error instanceof Error ? error.message : "载入失败", "error");
    } finally {
      setBusy(false);
    }
  };
  const deleteDraft = async () => {
    if (
      !selectedDraft ||
      !window.confirm(
        locale === "zh-CN"
          ? "确认删除这个草稿？删除后不可恢复。"
          : "Delete this draft? This cannot be undone.",
      )
    )
      return;
    try {
      await api(`/documents/drafts/${selectedDraft}`, { method: "DELETE", body: "{}" });
      notify(locale === "zh-CN" ? "草稿已删除" : "Draft deleted");
      setDrafts((current) => current.filter((draft) => draft.id !== selectedDraft));
      reset();
    } catch (error) {
      notify(error instanceof Error ? error.message : "删除失败", "error");
    }
  };
  const saveDraft = async () => {
    if (mode === "revise" || pendingDocumentId) {
      notify(
        locale === "zh-CN"
          ? "历史修订或盘点待处理单据不能另存为普通草稿。"
          : "Revisions and stocktake pending documents cannot be saved as drafts.",
        "error",
      );
      return;
    }
    setBusy(true);
    try {
      const result = await api<{ id: string }>(
        "/documents/drafts",
        {
          method: "POST",
          body: JSON.stringify({
            id: draftId,
            documentType: activeDocumentType,
            title: `${date} ${parties.find((party) => party.id === partyId)?.name ?? ""}`.trim(),
            payload: {
              documentType: activeDocumentType,
              documentDate: date,
              [type === "sale" ? "customerId" : "supplierId"]: partyId || null,
              originalDocumentId: originalDocumentId || null,
              returnMode: mode === "return" ? returnMode : undefined,
              paymentMethod,
              accountId: effectivePaid > 0 ? accountId : null,
              paidAmountKhr: effectivePaid,
              originalDocumentBalanceKhr: returnOriginalBalanceKhr,
              lines: lines.map(persistedLine),
            },
          }),
        },
        locale === "zh-CN" ? "单据草稿保存失败" : "Document draft save failed",
      );
      setDraftId(result.id);
      setSelectedDraft(result.id);
      setBaselineFingerprint(currentFingerprint);
      await loadDrafts();
      notify(tr(locale, "saveDraft"), "success");
    } catch (error) {
      notify(error instanceof Error ? error.message : "保存失败", "error");
    } finally {
      setBusy(false);
    }
  };
  const buildDocumentPayload = (reason: string, preview?: ReturnPreview) => {
    const returnLinePositions = new Map<string, number>();
    return {
      documentType: activeDocumentType,
      documentDate: date,
      [type === "sale" ? "customerId" : "supplierId"]: partyId,
      originalDocumentId: originalDocumentId || null,
      returnMode: mode === "return" ? returnMode : undefined,
      returnStateFingerprint: mode === "return" ? preview?.returnStateFingerprint : undefined,
      paymentMethod,
      accountId: mode === "return" ? accountId || null : effectivePaid > 0 ? accountId : null,
      paidAmountKhr: mode === "return" ? (preview?.paidAmountKhr ?? 0) : effectivePaid,
      finalTotalKhr: mode === "return" ? preview?.finalTotalKhr : undefined,
      settlementReason: reason,
      lines: selectedLines.map((line) => {
        const groupKey = line.originalLineId ?? line.editorId;
        const groupPosition = returnLinePositions.get(groupKey) ?? 0;
        returnLinePositions.set(groupKey, groupPosition + 1);
        const sourceLine = lines.find(
          (candidate) => candidate.originalLineId === line.originalLineId && candidate.selected,
        );
        const adjustedAmount = sourceLine?.groupActualAmountKhr;
        return {
          originalLineId: line.originalLineId ?? null,
          productId: line.productId,
          unitId: line.unitId,
          quantity: line.quantity,
          actualPriceKhr: mode === "return" ? 0 : line.actualPriceKhr,
          actualAmountKhr:
            mode === "return" && adjustedAmount !== undefined
              ? groupPosition === 0
                ? adjustedAmount
                : 0
              : undefined,
          priceAdjustmentReason:
            mode === "return" ? (sourceLine?.priceAdjustmentReason ?? "") : undefined,
          notes: line.notes,
        };
      }),
    };
  };
  const validateOrder = () => {
    if (
      !partyId ||
      !selectedLines.length ||
      selectedLines.some(
        (line) =>
          !line.productId || !line.unitId || line.quantity <= 0 || !Number.isInteger(line.quantity),
      )
    ) {
      notify(
        locale === "zh-CN" ? "请完善对象和明细。" : "Complete the party and line items.",
        "error",
      );
      return false;
    }
    if (mode === "return" && !returnMode) {
      notify(
        locale === "zh-CN" ? "请先选择部分退货或整单退货。" : "Select a return mode.",
        "error",
      );
      return false;
    }
    if (
      mode === "return" &&
      returnGroups.some((group) => {
        const source = group.lines[0];
        const product = products.find((item) => item.id === source.productId);
        const requestedBase = group.lines.reduce((sum, line) => {
          const factor = product?.units.find((unit) => unit.id === line.unitId)?.factor ?? 0;
          return sum + factor * line.quantity;
        }, 0);
        return requestedBase <= 0 || requestedBase > Number(source.remainingBaseQuantity ?? 0);
      })
    ) {
      notify(
        locale === "zh-CN"
          ? "退货包装数量超过原单行剩余可退数量。"
          : "Return packaging exceeds the remaining quantity.",
        "error",
      );
      return false;
    }
    if (
      mode === "return" &&
      returnGroups.some((group) => {
        const source = group.lines[0];
        return (
          source.groupActualAmountKhr !== undefined &&
          (source.groupActualAmountKhr < 0 ||
            (source.priceAdjustmentReason ?? "").trim().length < 2)
        );
      })
    ) {
      notify(
        locale === "zh-CN"
          ? "手工调整退货金额时必须填写调价原因。"
          : "A reason is required when adjusting a return amount.",
        "error",
      );
      return false;
    }
    if (effectivePaid > 0 && !accountId) {
      notify(locale === "zh-CN" ? "请选择资金账户。" : "Select a financial account.", "error");
      return false;
    }
    if (mode !== "return" && paid > total) {
      notify(
        locale === "zh-CN" ? "已付金额不能超过总额。" : "Paid amount cannot exceed total.",
        "error",
      );
      return false;
    }
    return true;
  };
  const executeConfirm = async (reason = "") => {
    if (!validateOrder()) return;
    if (mode === "return" && !activeReturnPreview) {
      notify(
        locale === "zh-CN" ? "退货预览已失效，请重新确认。" : "Return preview expired.",
        "error",
      );
      setShowOrderConfirmation(false);
      return;
    }
    setBusy(true);
    try {
      const payload = buildDocumentPayload(reason, activeReturnPreview);
      const operation =
        mode === "revise"
          ? locale === "zh-CN"
            ? "历史单据修订失败"
            : "Document revision failed"
          : stocktakeTargetId
            ? locale === "zh-CN"
              ? "盘点差异单据保存失败"
              : "Stocktake reconciliation document save failed"
            : locale === "zh-CN"
              ? activeDocumentType === "purchase"
                ? "进货单确认失败"
                : activeDocumentType === "sale"
                  ? "销售单确认失败"
                  : activeDocumentType === "purchase_return"
                    ? "进货退货确认失败"
                    : "销售退货确认失败"
              : activeDocumentType === "purchase"
                ? "Purchase confirmation failed"
                : activeDocumentType === "sale"
                  ? "Sale confirmation failed"
                  : activeDocumentType === "purchase_return"
                    ? "Purchase return confirmation failed"
                    : "Sale return confirmation failed";
      let result: any;
      const targetStocktakeId = stocktakeTargetId;
      if (mode === "revise") {
        result = await api<{ documentId: string; versionNo: number; printStatus: string }>(
          `/documents/${revisionDocumentId}/revise`,
          { method: "POST", body: JSON.stringify(payload) },
          operation,
        );
      } else if (targetStocktakeId) {
        result = await api<{ pendingDocument: any }>(
          pendingDocumentId
            ? `/inventory/stocktakes/${targetStocktakeId}/pending-documents/${pendingDocumentId}`
            : `/inventory/stocktakes/${targetStocktakeId}/pending-documents`,
          {
            method: pendingDocumentId ? "PUT" : "POST",
            body: JSON.stringify({ document: payload }),
          },
          operation,
        );
      } else {
        result = await api<{ documentId: string; documentNo: string; printStatus: string }>(
          "/documents/confirm",
          {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify(payload),
          },
          operation,
        );
      }
      if (!embedded && draftId)
        await api(`/documents/drafts/${draftId}`, { method: "DELETE", body: "{}" }).catch(
          () => undefined,
        );
      if (result.pendingDocument) {
        notify(
          `${result.pendingDocument.temporaryNo} · ${locale === "zh-CN" ? "已保存为盘点待处理单据" : "Saved as a stocktake pending document"}`,
          "success",
        );
      } else {
        notify(
          "documentNo" in result
            ? `${result.documentNo} · ${result.printStatus}`
            : `${locale === "zh-CN" ? "已更新至版本" : "Updated to version"} ${result.versionNo}`,
          "success",
        );
      }
      if (result.printStatus === "ready" && result.documentId)
        window.open(`/api/v1/documents/${result.documentId}/print`, "_blank", "noopener");
      if (embedded) {
        setBaselineFingerprint(currentFingerprint);
        await onCompleted?.(result);
      } else {
        await loadDrafts();
        reset();
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : "确认失败", "error");
    } finally {
      setBusy(false);
      setShowOrderConfirmation(false);
    }
  };
  const confirm = async () => {
    if (!validateOrder()) return;
    if (mode !== "return") {
      setShowOrderConfirmation(true);
      return;
    }
    setBusy(true);
    try {
      const preview = await api<Omit<ReturnPreview, "formFingerprint">>(
        "/documents/returns/preview",
        {
          method: "POST",
          body: JSON.stringify({
            document: buildDocumentPayload(""),
            sourceStocktakeId: stocktakeTargetId || undefined,
          }),
        },
        locale === "zh-CN" ? "退货预览失败" : "Return preview failed",
      );
      setReturnPreview({ ...preview, formFingerprint: currentFingerprint });
      if (preview.paidAmountKhr > 0 && !accountId) {
        notify(locale === "zh-CN" ? "请选择资金账户。" : "Select a financial account.", "error");
        return;
      }
      setShowOrderConfirmation(true);
    } catch (error) {
      notify(error instanceof Error ? error.message : "退货预览失败", "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`page-stack ${embedded ? "embedded-order-page" : ""}`}>
      {!embedded && (
        <div className="page-heading">
          <div>
            <div className="eyebrow">{type === "sale" ? "OUTBOUND" : "INBOUND"}</div>
            <h1>
              {pendingDocumentId
                ? locale === "zh-CN"
                  ? "修改盘点待处理单据"
                  : "Edit stocktake pending document"
                : mode === "revise"
                  ? locale === "zh-CN"
                    ? "修改历史单据"
                    : "Revise document"
                  : mode === "return"
                    ? locale === "zh-CN"
                      ? "创建退货单"
                      : "Create return"
                    : type === "sale"
                      ? tr(locale, "sales")
                      : tr(locale, "purchases")}
            </h1>
            <p className="muted">
              {isStocktakePendingFlow
                ? locale === "zh-CN"
                  ? "确认后先保存为盘点待处理单据；盘点最终确认前不更新库存、应收应付或资金流水。"
                  : "Confirmation saves a pending stocktake document without posting stock or finance."
                : locale === "zh-CN"
                  ? "填写完成后确认，库存和打印快照会同时更新。"
                  : "Confirm once to update stock and archive the print snapshot."}
            </p>
          </div>
          <button className="secondary-button" onClick={requestReset}>
            <Plus size={16} />
            {tr(locale, "newOrder")}
          </button>
        </div>
      )}
      {isStocktakePendingFlow && (
        <div className="mode-banner stocktake-lock-banner">
          <ClipboardCheck size={17} />
          <strong>
            {locale === "zh-CN"
              ? `${activeStocktake?.stocktakeNo ?? initialDocument?.stocktakeNo ?? "当前盘点"} 正在盘点，本单只保存为待处理记录。`
              : "An active stocktake is in progress; this document remains pending."}
          </strong>
        </div>
      )}
      {mode !== "new" && (
        <div className="mode-banner">
          <RotateCcw size={17} />
          <strong>
            {mode === "revise"
              ? locale === "zh-CN"
                ? "正在修改已确认单据；确认后保留旧版本。"
                : "Revising a confirmed document; the old version remains archived."
              : locale === "zh-CN"
                ? "正在根据原单创建退货；默认不选择任何明细。"
                : "Creating a return from the original; no lines are selected by default."}
          </strong>
        </div>
      )}
      {mode === "return" && (
        <section
          className="return-mode-panel"
          aria-label={locale === "zh-CN" ? "退货范围" : "Return scope"}
        >
          <div>
            <strong>{locale === "zh-CN" ? "退货范围" : "Return scope"}</strong>
            <span className="muted">
              {locale === "zh-CN"
                ? "先选择范围，再按实际包装填写退回数量。"
                : "Choose the scope, then enter the returned packaging."}
            </span>
          </div>
          <div className="segmented-control return-mode-control">
            <button
              type="button"
              className={returnMode === "partial" ? "active" : ""}
              onClick={() => chooseReturnMode("partial")}
            >
              {locale === "zh-CN" ? "部分退货" : "Partial return"}
            </button>
            <button
              type="button"
              className={returnMode === "full" ? "active" : ""}
              onClick={() => chooseReturnMode("full")}
            >
              {locale === "zh-CN" ? "整单退货" : "Full return"}
            </button>
          </div>
        </section>
      )}
      {!embedded && (
        <div className="draft-toolbar">
          <label>
            {tr(locale, "draft")}
            <SearchableSelect
              id={`${type}-draft`}
              name="draftId"
              ariaLabel={tr(locale, "draft")}
              value={selectedDraft}
              disabled={mode !== "new" || Boolean(pendingDocumentId)}
              onChange={setSelectedDraft}
              placeholder="--"
              searchPlaceholder={locale === "zh-CN" ? "搜索草稿" : "Search drafts"}
              emptyText={locale === "zh-CN" ? "没有匹配草稿" : "No matching drafts"}
              options={drafts.map((draft) => {
                const displayTitle = draftDisplayTitle(draft, parties);
                return {
                  value: draft.id,
                  label: `${displayTitle} · ${formatDateTimeInTimeZone(draft.updatedAt, locale)}`,
                  keywords: `${draft.title ?? ""} ${displayTitle} ${draft.documentType} ${draft.updatedAt}`,
                };
              })}
            />
          </label>
          <button
            className="secondary-button"
            disabled={mode !== "new" || Boolean(pendingDocumentId) || !selectedDraft || busy}
            onClick={loadDraft}
          >
            <FilePenLine size={15} />
            {tr(locale, "load")}
          </button>
          <button
            className="danger-button"
            disabled={mode !== "new" || Boolean(pendingDocumentId) || !selectedDraft || busy}
            onClick={deleteDraft}
          >
            <Trash2 size={15} />
            {tr(locale, "delete")}
          </button>
        </div>
      )}
      <div className="form-grid order-form-grid">
        <label>
          {tr(locale, "orderDate")}
          <input
            id={`${type}-document-date`}
            name="documentDate"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
        <label>
          {type === "sale" ? tr(locale, "customer") : tr(locale, "supplier")}
          <SearchableSelect
            id={`${type}-party`}
            name={type === "sale" ? "customerId" : "supplierId"}
            ariaLabel={type === "sale" ? tr(locale, "customer") : tr(locale, "supplier")}
            value={partyId}
            onChange={chooseParty}
            placeholder="--"
            searchPlaceholder={
              type === "sale"
                ? locale === "zh-CN"
                  ? "搜索客户"
                  : "Search customers"
                : locale === "zh-CN"
                  ? "搜索供应商"
                  : "Search suppliers"
            }
            emptyText={locale === "zh-CN" ? "没有匹配对象" : "No matching party"}
            options={parties.map((party) => ({
              value: party.id,
              label: party.name,
              keywords: [party.contactName, party.phone, party.address, party.notes]
                .filter(Boolean)
                .join(" "),
            }))}
          />
        </label>
        <label>
          {mode === "return"
            ? locale === "zh-CN"
              ? type === "sale"
                ? "退款方式"
                : "追收方式"
              : type === "sale"
                ? "Refund method"
                : "Recovery method"
            : tr(locale, "payment")}
          <select
            id={`${type}-payment-method`}
            name="paymentMethod"
            value={paymentMethod}
            onChange={(event) => choosePaymentMethod(event.target.value)}
          >
            {mode !== "return" && <option value="credit">赊账 / Credit</option>}
            <option value="cash">现金 / Cash</option>
            <option value="bank">银行 / Bank</option>
          </select>
        </label>
        <div className="auto-account-note">
          <span>{locale === "zh-CN" ? "入账账户" : "Posting account"}</span>
          <strong>
            {paymentMethod === "credit"
              ? locale === "zh-CN"
                ? "不产生资金流水"
                : "No fund transaction"
              : accounts.find((account) => account.id === accountId)?.name || "-"}
          </strong>
        </div>
      </div>
      <div className="table-panel">
        <div className="panel-header">
          <div>
            <h2>{locale === "zh-CN" ? "订单明细" : "Line items"}</h2>
            <span className="muted">
              {mode === "return"
                ? `${selectedLines.length}/${lines.length} selected`
                : `${lines.length} lines`}
            </span>
          </div>
          {mode !== "return" && (
            <button
              className="secondary-button"
              onClick={() => setLines((current) => [...current, blankEditableLine()])}
            >
              <Plus size={15} />
              {tr(locale, "addLine")}
            </button>
          )}
        </div>
        {mode === "return" ? (
          <div className="order-table-wrap">
            <table className="order-table return-order-table">
              <thead>
                <tr>
                  <th>{locale === "zh-CN" ? "选" : "Use"}</th>
                  <th>#</th>
                  <th>{tr(locale, "product")}</th>
                  <th>{locale === "zh-CN" ? "原数量" : "Original"}</th>
                  <th>{locale === "zh-CN" ? "已退" : "Returned"}</th>
                  <th>{locale === "zh-CN" ? "可退" : "Remaining"}</th>
                  <th>{locale === "zh-CN" ? "包装数量" : "Package qty"}</th>
                  <th>{tr(locale, "unit")}</th>
                  <th>{locale === "zh-CN" ? "折合最小单位" : "Base quantity"}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {allReturnGroups.flatMap((group, groupIndex) => {
                  const source = group.lines[0];
                  const product = products.find((item) => item.id === source.productId);
                  const selected = group.lines.some((line) => line.selected);
                  const previewLines = activeReturnPreview?.lines.filter(
                    (line) => line.originalLineId === group.originalLineId,
                  );
                  const referenceAmount = previewLines?.reduce(
                    (sum, line) => sum + Number(line.referenceAmountKhr),
                    0,
                  );
                  const actualAmount = previewLines?.reduce(
                    (sum, line) => sum + Number(line.actualAmountKhr),
                    0,
                  );
                  const detailRows = group.lines.map((line, packageIndex) => {
                    const index = lines.findIndex(
                      (candidate) => candidate.editorId === line.editorId,
                    );
                    const unit = product?.units.find((candidate) => candidate.id === line.unitId);
                    return (
                      <tr key={line.editorId} className={!selected ? "muted-row" : ""}>
                        {packageIndex === 0 && (
                          <>
                            <td rowSpan={group.lines.length}>
                              <input
                                id={`${type}-return-group-${groupIndex}-selected`}
                                type="checkbox"
                                checked={selected}
                                disabled={
                                  !returnMode ||
                                  returnMode === "full" ||
                                  Number(source.remainingBaseQuantity ?? 0) <= 0
                                }
                                onChange={(event) =>
                                  setReturnGroupSelected(group.originalLineId, event.target.checked)
                                }
                              />
                            </td>
                            <td className="row-number" rowSpan={group.lines.length}>
                              {source.lineNo ?? groupIndex + 1}
                            </td>
                            <td rowSpan={group.lines.length}>
                              <strong>
                                {product
                                  ? `${product.code} · ${product.name}`
                                  : `${source.productCode ?? ""} · ${source.productName ?? source.productId}`}
                              </strong>
                            </td>
                            <td className="numeric" rowSpan={group.lines.length}>
                              {source.originalQuantity} {source.originalUnitName}
                            </td>
                            <td className="numeric" rowSpan={group.lines.length}>
                              {source.returnedQuantity} {source.originalUnitName}
                            </td>
                            <td className="numeric stock-value" rowSpan={group.lines.length}>
                              {source.remainingBaseQuantity} {product?.baseUnit ?? ""}
                            </td>
                          </>
                        )}
                        <td>
                          <input
                            id={`${type}-return-line-${index}-quantity`}
                            className="numeric"
                            type="number"
                            min="1"
                            step="1"
                            value={line.quantity}
                            disabled={!selected || !returnMode}
                            onChange={(event) =>
                              setLine(index, {
                                quantity: Math.max(0, Number(event.target.value) || 0),
                              })
                            }
                          />
                        </td>
                        <td>
                          <SearchableSelect
                            id={`${type}-return-line-${index}-unit`}
                            name={`returnLines.${index}.unitId`}
                            ariaLabel={`${tr(locale, "unit")} ${index + 1}`}
                            value={line.unitId}
                            disabled={!selected || !returnMode}
                            onChange={(unitId) => setLine(index, { unitId })}
                            placeholder="--"
                            searchPlaceholder={locale === "zh-CN" ? "搜索单位" : "Search units"}
                            emptyText={locale === "zh-CN" ? "没有可用单位" : "No available units"}
                            options={(product?.units ?? [])
                              .filter((candidate) => Boolean(candidate.active) && candidate.id)
                              .map((candidate) => ({
                                value: candidate.id!,
                                label: `${candidate.unitName} · ${candidate.factor} ${product?.baseUnit ?? ""}`,
                              }))}
                          />
                        </td>
                        <td className="numeric">
                          {money(line.quantity * Number(unit?.factor ?? 0))}{" "}
                          {product?.baseUnit ?? ""}
                        </td>
                        <td>
                          <button
                            className="icon-button danger"
                            type="button"
                            title={tr(locale, "delete")}
                            disabled={!selected || !returnMode}
                            onClick={() =>
                              removeReturnPackaging(line.editorId, group.originalLineId)
                            }
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  });
                  const adjustmentRow = (
                    <tr
                      key={`${group.originalLineId}-adjustment`}
                      className={`return-adjustment-row ${!selected ? "muted-row" : ""}`}
                    >
                      <td colSpan={10}>
                        <div className="return-adjustment-controls">
                          <button
                            className="secondary-button"
                            type="button"
                            disabled={!selected || !returnMode}
                            onClick={() => addReturnPackaging(group.originalLineId)}
                          >
                            <Plus size={15} />
                            {locale === "zh-CN" ? "增加包装单位" : "Add package unit"}
                          </button>
                          <span>
                            {locale === "zh-CN" ? "参考金额" : "Reference"}:{" "}
                            {referenceAmount === undefined
                              ? "确认时计算"
                              : `${money(referenceAmount)} KHR`}
                          </span>
                          <label>
                            {locale === "zh-CN" ? "实际退货金额" : "Actual return amount"}
                            <input
                              type="number"
                              min="0"
                              max={referenceAmount}
                              value={source.groupActualAmountKhr ?? ""}
                              placeholder={
                                actualAmount === undefined
                                  ? locale === "zh-CN"
                                    ? "默认等于参考金额"
                                    : "Defaults to reference"
                                  : String(actualAmount)
                              }
                              disabled={!selected || !returnMode}
                              onChange={(event) => {
                                const value = event.target.value;
                                setLines((current) => {
                                  let first = true;
                                  return current.map((candidate) => {
                                    if (candidate.originalLineId !== group.originalLineId)
                                      return candidate;
                                    if (!first) return candidate;
                                    first = false;
                                    return {
                                      ...candidate,
                                      groupActualAmountKhr:
                                        value === "" ? undefined : Math.max(0, Number(value)),
                                    };
                                  });
                                });
                              }}
                            />
                          </label>
                          <label className="return-adjustment-reason">
                            {locale === "zh-CN" ? "调价原因" : "Adjustment reason"}
                            <input
                              type="text"
                              maxLength={500}
                              value={source.priceAdjustmentReason ?? ""}
                              disabled={
                                !selected ||
                                !returnMode ||
                                source.groupActualAmountKhr === undefined
                              }
                              onChange={(event) =>
                                setLines((current) =>
                                  current.map((candidate) =>
                                    candidate.originalLineId === group.originalLineId
                                      ? { ...candidate, priceAdjustmentReason: event.target.value }
                                      : candidate,
                                  ),
                                )
                              }
                            />
                          </label>
                        </div>
                      </td>
                    </tr>
                  );
                  return [...detailRows, adjustmentRow];
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="order-table-wrap">
            <table className="order-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{tr(locale, "product")}</th>
                  <th>{tr(locale, "unit")}</th>
                  <th>{tr(locale, "quantity")}</th>
                  <th>{tr(locale, "unitPrice")}</th>
                  <th>{tr(locale, "amount")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const product = products.find((item) => item.id === line.productId);
                  return (
                    <tr key={line.editorId}>
                      <td className="row-number">{index + 1}</td>
                      <td>
                        <SearchableSelect
                          id={`${type}-line-${index}-product`}
                          name={`lines.${index}.productId`}
                          ariaLabel={`${tr(locale, "product")} ${index + 1}`}
                          value={line.productId}
                          onChange={(productId) => chooseProduct(index, productId)}
                          placeholder="--"
                          searchPlaceholder={
                            locale === "zh-CN" ? "搜索编码或品名" : "Search code or name"
                          }
                          emptyText={locale === "zh-CN" ? "没有匹配产品" : "No matching products"}
                          options={products.map((item) => ({
                            value: item.id,
                            label: `${item.code} · ${item.name}`,
                            keywords: `${item.alias ?? ""} ${item.baseUnit}`,
                          }))}
                        />
                      </td>
                      <td>
                        <SearchableSelect
                          id={`${type}-line-${index}-unit`}
                          name={`lines.${index}.unitId`}
                          ariaLabel={`${tr(locale, "unit")} ${index + 1}`}
                          value={line.unitId}
                          onChange={(unitId) => chooseUnit(index, line.productId, unitId)}
                          placeholder="--"
                          searchPlaceholder={locale === "zh-CN" ? "搜索单位" : "Search units"}
                          emptyText={locale === "zh-CN" ? "没有可用单位" : "No available units"}
                          options={(product?.units ?? [])
                            .filter((unit) => unit.active)
                            .map((unit) => ({
                              value: unit.id ?? "",
                              label: unit.unitName,
                              keywords: String(unit.factor),
                            }))}
                        />
                      </td>
                      <td>
                        <input
                          id={`${type}-line-${index}-quantity`}
                          name={`lines.${index}.quantity`}
                          className="numeric"
                          type="number"
                          min="1"
                          step="1"
                          value={line.quantity}
                          onChange={(event) =>
                            setLine(index, { quantity: Number(event.target.value) })
                          }
                        />
                      </td>
                      <td>
                        <div className="price-field">
                          <input
                            id={`${type}-line-${index}-price`}
                            name={`lines.${index}.actualPriceKhr`}
                            className="numeric"
                            type="number"
                            min="0"
                            step="1"
                            value={line.actualPriceKhr}
                            onChange={(event) =>
                              setLine(index, {
                                actualPriceKhr: Number(event.target.value),
                                priceSource: "manual",
                              })
                            }
                          />
                          {line.priceSource && (
                            <small>
                              {{
                                party_recent: locale === "zh-CN" ? "对象最近价" : "Party recent",
                                global_recent: locale === "zh-CN" ? "最近成交价" : "Recent price",
                                maintained_default:
                                  locale === "zh-CN" ? "维护默认价" : "Maintained default",
                                manual: locale === "zh-CN" ? "手工价" : "Manual",
                              }[line.priceSource] ?? line.priceSource}
                            </small>
                          )}
                        </div>
                      </td>
                      <td className="amount-cell">
                        {money(Math.round(line.actualPriceKhr * line.quantity))}
                      </td>
                      <td>
                        <div className="line-action-buttons">
                          <button
                            className="icon-button"
                            type="button"
                            title={locale === "zh-CN" ? "同商品增加包装单位" : "Add package unit"}
                            disabled={!line.productId}
                            onClick={() => addPackagingLine(index)}
                          >
                            <Plus size={16} />
                          </button>
                          <button
                            className="icon-button danger"
                            type="button"
                            title={tr(locale, "delete")}
                            onClick={() =>
                              setLines((current) =>
                                current.length === 1
                                  ? current
                                  : current.filter((_, lineIndex) => lineIndex !== index),
                              )
                            }
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="order-footer">
        <div className="payment-input">
          {mode === "return" ? (
            <>
              <div className="balance-line">
                <span>{locale === "zh-CN" ? "自动冲抵未结单据" : "Applied to open documents"}</span>
                <strong>{money(returnOffset)} KHR</strong>
              </div>
              <div className="balance-line">
                <span>
                  {type === "sale"
                    ? "Immediate refund / 立即退款"
                    : "Immediate recovery / 立即追收"}
                </span>
                <strong>{money(effectivePaid)} KHR</strong>
              </div>
            </>
          ) : (
            <label>
              {tr(locale, "paid")}
              <input
                id={`${type}-paid-amount`}
                name="paidAmountKhr"
                type="number"
                min="0"
                max={total}
                value={effectivePaid}
                disabled={mode === "revise"}
                onChange={(event) => {
                  const nextPaid = Math.max(0, Number(event.target.value) || 0);
                  setPaid(nextPaid);
                  if (nextPaid === 0) choosePaymentMethod("credit");
                  else if (paymentMethod === "credit") choosePaymentMethod("cash", false);
                }}
              />
            </label>
          )}
          <div className="balance-line">
            <span>{tr(locale, "balance")}</span>
            <strong className={balance < 0 ? "negative" : ""}>{money(balance)} KHR</strong>
          </div>
        </div>
        <div className="total-block">
          <span>{tr(locale, "total")}</span>
          <strong>{money(total)} KHR</strong>
        </div>
        <div className="action-row">
          {!embedded && (
            <button
              className="secondary-button"
              disabled={busy || mode === "revise" || Boolean(pendingDocumentId)}
              onClick={saveDraft}
            >
              <Save size={16} />
              {tr(locale, "saveDraft")}
            </button>
          )}
          <button className="primary-button" disabled={busy} onClick={confirm}>
            <Check size={17} />
            {busy
              ? "..."
              : isStocktakePendingFlow
                ? pendingDocumentId
                  ? locale === "zh-CN"
                    ? "确认更新待处理单据"
                    : "Update pending document"
                  : locale === "zh-CN"
                    ? "确认保存待处理单据"
                    : "Save pending document"
                : mode === "revise"
                  ? locale === "zh-CN"
                    ? "确认修改"
                    : "Confirm revision"
                  : mode === "return"
                    ? locale === "zh-CN"
                      ? "确认退货"
                      : "Confirm return"
                    : tr(locale, "confirm")}
          </button>
        </div>
      </div>
      <ReasonConfirmDialog
        open={showOrderConfirmation}
        locale={locale}
        busy={busy}
        title={
          isStocktakePendingFlow
            ? locale === "zh-CN"
              ? pendingDocumentId
                ? "确认更新盘点待处理单据"
                : "确认保存盘点待处理单据"
              : "Confirm stocktake pending document"
            : mode === "return"
              ? locale === "zh-CN"
                ? "确认退货及即时结算"
                : "Confirm return and immediate settlement"
              : mode === "revise"
                ? locale === "zh-CN"
                  ? "确认修改历史单据"
                  : "Confirm document revision"
                : effectivePaid > 0
                  ? locale === "zh-CN"
                    ? "确认已付款单据"
                    : "Confirm paid document"
                  : locale === "zh-CN"
                    ? "确认赊账单据"
                    : "Confirm credit document"
        }
        description={
          isStocktakePendingFlow ? (
            locale === "zh-CN" ? (
              "确认后只保存临时记录，不生成正式单号，不更新库存、应收应付或资金流水；盘点最终确认时才统一执行。"
            ) : (
              "This saves a temporary record only. Posting occurs when the stocktake is confirmed."
            )
          ) : mode === "return" ? (
            <div className="return-confirm-summary">
              <p>
                {type === "sale" ? "退款" : "追收"} {money(effectivePaid)} KHR；
                {locale === "zh-CN" ? "自动冲抵" : "offset"} {money(returnOffset)} KHR。
              </p>
              {activeReturnPreview?.offsets.length ? (
                <ul>
                  {activeReturnPreview.offsets.map((offset) => (
                    <li key={offset.documentId}>
                      <strong>{offset.documentNo}</strong>
                      <span>{offset.documentDate}</span>
                      <span>{money(offset.amountKhr)} KHR</span>
                      {offset.isOriginal && (
                        <small>{locale === "zh-CN" ? "原单" : "Original"}</small>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">
                  {locale === "zh-CN" ? "没有可冲抵的未结单据。" : "No open document to offset."}
                </p>
              )}
            </div>
          ) : effectivePaid > 0 ? (
            locale === "zh-CN" ? (
              "该操作会写入资金流水、更新库存并生成打印快照。"
            ) : (
              "This posts the fund transaction, updates stock and creates the print snapshot."
            )
          ) : locale === "zh-CN" ? (
            "该操作会生成应收或应付、更新库存并生成打印快照；当前不会产生资金流水。"
          ) : (
            "This creates a receivable or payable, updates stock and creates the print snapshot without a fund transaction."
          )
        }
        confirmLabel={locale === "zh-CN" ? "确认执行" : "Confirm action"}
        danger={mode === "return"}
        reasonRequired={mode === "return" || mode === "revise" || effectivePaid > 0}
        onCancel={() => setShowOrderConfirmation(false)}
        onConfirm={(reason) => void executeConfirm(reason)}
      />
    </div>
  );
}

function HistoryPage({
  locale,
  notify,
  onEdit,
}: {
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
  onEdit: (mode: "revise" | "return", document: any) => void;
}) {
  const [items, setItems] = useState<any[]>([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<any>();
  const load = () =>
    api<{ items: any[] }>(`/documents/history?q=${encodeURIComponent(query)}`)
      .then((data) => setItems(data.items))
      .catch((error) => notify(error.message, "error"));
  useEffect(() => {
    api<{ items: any[] }>("/documents/history?q=")
      .then((data) => setItems(data.items))
      .catch((error) => notify(error.message, "error"));
  }, [notify]);
  const openPrint = (id: string) =>
    window.open(`/api/v1/documents/${id}/print`, "_blank", "noopener");
  return (
    <div className="page-stack">
      <div className="page-heading">
        <div>
          <div className="eyebrow">ARCHIVE</div>
          <h1>{tr(locale, "history")}</h1>
          <p className="muted">
            {locale === "zh-CN"
              ? "点击行后查看完整单据、资金流水和打印快照。"
              : "Select a row to inspect the document, settlements and print snapshot."}
          </p>
        </div>
        <button className="secondary-button" onClick={load}>
          <RefreshCw size={16} />
          {tr(locale, "refresh")}
        </button>
      </div>
      <div className="toolbar">
        <input
          id="history-search"
          name="historySearch"
          placeholder={`${tr(locale, "search")}...`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && load()}
        />
        <button className="secondary-button" onClick={load}>
          <PackageSearch size={15} />
          {tr(locale, "search")}
        </button>
      </div>
      <div className="table-panel">
        <table className="data-table">
          <thead>
            <tr>
              <th>编号 / No.</th>
              <th>日期</th>
              <th>{locale === "zh-CN" ? "对象" : "Party"}</th>
              <th>{tr(locale, "total")}</th>
              <th>{tr(locale, "status")}</th>
              <th>{tr(locale, "action")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                className={selected?.id === item.id ? "selected-row" : ""}
                onClick={() => setSelected(item)}
              >
                <td>
                  <strong>{item.documentNo}</strong>
                  <small>V{item.versionNo}</small>
                </td>
                <td>{item.documentDate}</td>
                <td>{item.partyName}</td>
                <td>{money(item.finalTotalKhr)} KHR</td>
                <td>
                  <span
                    className={`status-pill ${item.status === "void" ? "danger-pill" : item.printStatus === "ready" ? "success-pill" : "warning-pill"}`}
                  >
                    {item.status === "void"
                      ? "作废"
                      : item.printStatus === "ready"
                        ? "已归档"
                        : "打印待生成"}
                  </span>
                </td>
                <td>
                  <button
                    className="icon-button"
                    title={tr(locale, "print")}
                    onClick={(event) => {
                      event.stopPropagation();
                      openPrint(item.id);
                    }}
                  >
                    <FileDown size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr>
                <td colSpan={6} className="empty-cell">
                  {tr(locale, "empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {selected && (
        <DocumentDrawer
          item={selected}
          locale={locale}
          notify={notify}
          onClose={() => setSelected(undefined)}
          onRefresh={load}
          onEdit={onEdit}
        />
      )}
    </div>
  );
}

function DocumentDrawer({ item, locale, notify, onClose, onRefresh, onEdit }: any) {
  const [detail, setDetail] = useState<any>();
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [settlementAmount, setSettlementAmount] = useState(0);
  const [settlementAccountId, setSettlementAccountId] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingAction, setPendingAction] = useState<
    { kind: "void" } | { kind: "settlement" } | { kind: "reverse"; settlement: any } | undefined
  >();
  const loadDetail = useCallback(
    () =>
      Promise.all([
        api<{ document: any }>(`/documents/${item.id}`),
        api<{ items: FinancialAccount[] }>("/finance/accounts"),
      ])
        .then(([documentData, accountData]) => {
          const activeAccounts = accountData.items.filter((account) => Boolean(account.active));
          setDetail(documentData.document);
          setAccounts(activeAccounts);
          setSettlementAccountId((current) =>
            activeAccounts.some((account) => account.id === current)
              ? current
              : (activeAccounts.find((account) => account.accountType === "cash")?.id ?? ""),
          );
        })
        .catch((error) => notify(error.message, "error")),
    [item.id, notify],
  );
  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);
  const settlementLimit = detail
    ? detail.status === "void"
      ? Number(detail.counterRemainingKhr)
      : Number(detail.balanceKhr)
    : 0;
  const settlementLabel = detail
    ? detail.status === "void"
      ? detail.counterDirection === "outflow"
        ? locale === "zh-CN"
          ? "退款"
          : "Refund"
        : locale === "zh-CN"
          ? "追收"
          : "Recovery"
      : detail.documentType === "sale"
        ? locale === "zh-CN"
          ? "收款"
          : "Customer payment"
        : locale === "zh-CN"
          ? "付款"
          : "Supplier payment"
    : "";
  const requestSettlement = () => {
    if (settlementAmount <= 0 || settlementAmount > settlementLimit) {
      notify(
        locale === "zh-CN"
          ? "请输入不超过剩余金额的有效数值。"
          : "Enter an amount within the remaining limit.",
        "error",
      );
      return;
    }
    if (!settlementAccountId) {
      notify(locale === "zh-CN" ? "请选择资金账户。" : "Select a financial account.", "error");
      return;
    }
    setPendingAction({ kind: "settlement" });
  };
  const executeAction = async (reason: string) => {
    if (!detail || !pendingAction) return;
    setBusy(true);
    try {
      if (pendingAction.kind === "void") {
        await api(`/documents/${item.id}/void`, {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ reason }),
        });
        notify(locale === "zh-CN" ? "单据已作废" : "Document voided", "success");
      } else if (pendingAction.kind === "settlement") {
        const account = accounts.find((item) => item.id === settlementAccountId);
        if (detail.status === "confirmed") {
          const side = detail.documentType === "sale" ? "receivable" : "payable";
          await api("/finance/settlement-batches", {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify({
              side,
              partyId: side === "receivable" ? detail.customerId : detail.supplierId,
              amountKhr: settlementAmount,
              accountId: settlementAccountId,
              reason,
              allocations: [{ documentId: item.id, amountKhr: settlementAmount }],
            }),
          });
        } else {
          await api(`/documents/${item.id}/settlements`, {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify({
              amountKhr: settlementAmount,
              paymentMethod: account?.accountType === "bank" ? "aba" : "cash",
              accountId: settlementAccountId,
              reason,
            }),
          });
        }
        setSettlementAmount(0);
        notify(`${settlementLabel} ${money(settlementAmount)} KHR`, "success");
      } else {
        const reversalPath = pendingAction.settlement.settlementBatchId
          ? `/finance/settlement-batches/${pendingAction.settlement.settlementBatchId}/reverse`
          : `/settlements/${pendingAction.settlement.id}/reverse`;
        await api(reversalPath, {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ reason }),
        });
        notify(locale === "zh-CN" ? "结算记录已冲销" : "Settlement reversed", "success");
      }
      setPendingAction(undefined);
      await loadDetail();
      onRefresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : "操作失败", "error");
    } finally {
      setBusy(false);
    }
  };
  const retryPrint = async () => {
    if (
      !window.confirm(
        locale === "zh-CN"
          ? detail?.printStatus === "ready"
            ? "确认更新打印文件？不会改变库存或财务数据。"
            : "确认重新生成打印快照？"
          : detail?.printStatus === "ready"
            ? "Update the print file? Stock and finance data will not change."
            : "Regenerate the print snapshot?",
      )
    )
      return;
    try {
      await api(`/documents/${item.id}/print/retry`, { method: "POST", body: "{}" });
      await loadDetail();
      onRefresh();
      notify(locale === "zh-CN" ? "打印快照已生成" : "Print snapshot regenerated");
    } catch (error) {
      notify(error instanceof Error ? error.message : "打印快照生成失败", "error");
    }
  };
  const retryReceipt = async (settlementId: string) => {
    if (!window.confirm(locale === "zh-CN" ? "确认重新生成该回执？" : "Regenerate this receipt?"))
      return;
    try {
      await api(`/settlements/${settlementId}/receipt/retry`, { method: "POST", body: "{}" });
      await loadDetail();
      notify(locale === "zh-CN" ? "回执已生成" : "Receipt regenerated");
    } catch (error) {
      notify(error instanceof Error ? error.message : "回执生成失败", "error");
    }
  };
  const purposeLabel = (purpose: string) =>
    ({
      document_payment: locale === "zh-CN" ? "单据收付款" : "Document payment",
      return_offset: locale === "zh-CN" ? "原单欠款抵扣" : "Original balance offset",
      return_settlement: locale === "zh-CN" ? "退货即时结算" : "Immediate return settlement",
      void_counter: locale === "zh-CN" ? "作废退款/追收" : "Void counter-settlement",
    })[purpose] ?? purpose;
  const confirmationTitle =
    pendingAction?.kind === "void"
      ? locale === "zh-CN"
        ? "确认作废单据"
        : "Confirm document void"
      : pendingAction?.kind === "reverse"
        ? locale === "zh-CN"
          ? "确认冲销结算"
          : "Confirm settlement reversal"
        : `${locale === "zh-CN" ? "确认" : "Confirm"} ${settlementLabel}`;
  const confirmationDescription =
    pendingAction?.kind === "void"
      ? locale === "zh-CN"
        ? "库存将反向调整；已实际发生的资金不会删除，并会转为待退款或待追收。"
        : "Stock will be reversed. Actual fund history remains and becomes a pending counter-settlement."
      : pendingAction?.kind === "reverse"
        ? `${purposeLabel(pendingAction.settlement.purpose)} · ${money(pendingAction.settlement.amountKhr)} KHR`
        : `${settlementLabel} ${money(settlementAmount)} KHR`;
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose}>
        <aside className="drawer" onClick={(event) => event.stopPropagation()}>
          <div className="drawer-heading">
            <div>
              <div className="eyebrow">{item.documentNo}</div>
              <h2>{item.partyName}</h2>
            </div>
            <button
              className="icon-button"
              title={locale === "zh-CN" ? "关闭" : "Close"}
              onClick={onClose}
            >
              <X size={18} />
            </button>
          </div>
          {detail ? (
            <>
              <div className="detail-grid settlement-detail-grid">
                <span>
                  日期 / Date<strong>{detail.documentDate}</strong>
                </span>
                <span>
                  总额 / Total<strong>{money(detail.finalTotalKhr)} KHR</strong>
                </span>
                <span>
                  实际收付 / Funds<strong>{money(detail.paidTotalKhr)} KHR</strong>
                </span>
                <span>
                  欠款抵扣 / Offset<strong>{money(detail.offsetTotalKhr)} KHR</strong>
                </span>
                <span>
                  余额 / Balance<strong>{money(detail.balanceKhr)} KHR</strong>
                </span>
                <span>
                  待退款/追收 / Pending
                  <strong className={detail.counterRemainingKhr > 0 ? "negative" : ""}>
                    {money(detail.counterRemainingKhr)} KHR
                  </strong>
                </span>
              </div>
              {detail.voidReason && (
                <div className="void-notice">
                  <strong>作废原因</strong>
                  <span>{detail.voidReason}</span>
                </div>
              )}
              <div className="drawer-lines">
                {detail.lines.map((line: any) => (
                  <div className="drawer-line" key={line.id}>
                    <span>{line.lineNo}</span>
                    <strong>{line.productName}</strong>
                    <span>
                      {line.quantity} {line.unitName}
                    </span>
                    <span>{money(line.subtotalKhr)}</span>
                  </div>
                ))}
              </div>
              {settlementLimit > 0 &&
                (detail.status === "void" ||
                  ["sale", "purchase"].includes(detail.documentType)) && (
                  <div className="payment-post">
                    <label>
                      {settlementLabel} (KHR)
                      <input
                        id="settlement-amount"
                        name="settlementAmountKhr"
                        type="number"
                        min="1"
                        max={settlementLimit}
                        value={settlementAmount}
                        onChange={(event) =>
                          setSettlementAmount(Math.max(0, Number(event.target.value) || 0))
                        }
                      />
                    </label>
                    <label>
                      {locale === "zh-CN" ? "资金账户" : "Financial account"}
                      <SearchableSelect
                        id="settlement-account"
                        name="settlementAccountId"
                        ariaLabel={locale === "zh-CN" ? "资金账户" : "Financial account"}
                        value={settlementAccountId}
                        onChange={setSettlementAccountId}
                        placeholder="--"
                        searchPlaceholder={locale === "zh-CN" ? "搜索账户" : "Search accounts"}
                        emptyText={locale === "zh-CN" ? "没有可用账户" : "No available accounts"}
                        options={accounts.map((account) => ({
                          value: account.id,
                          label: `${account.name} · ${money(Number(account.balanceKhr))} KHR`,
                          keywords: `${account.accountCode} ${account.accountType}`,
                        }))}
                      />
                    </label>
                    <button className="primary-button" onClick={requestSettlement}>
                      <Check size={16} />
                      {locale === "zh-CN" ? "登记" : "Post"}
                    </button>
                  </div>
                )}
              <section className="settlement-timeline">
                <div className="section-heading-row">
                  <h3>{locale === "zh-CN" ? "资金流水" : "Financial timeline"}</h3>
                  <span className="count-tag">{detail.settlements.length}</span>
                </div>
                {detail.settlements.map((settlement: any) => (
                  <article
                    className={`settlement-entry ${settlement.status === "reversed" ? "reversed" : ""}`}
                    key={settlement.id}
                  >
                    <div className="settlement-entry-main">
                      <div>
                        <strong>{purposeLabel(settlement.purpose)}</strong>
                        <span>
                          {settlement.createdByName} ·{" "}
                          {formatDateTimeInTimeZone(settlement.occurredAt, locale)}
                        </span>
                        {settlement.accountName && <span>{settlement.accountName}</span>}
                        {settlement.settlementBatchNo && (
                          <span>
                            {locale === "zh-CN" ? "收付款批次" : "Settlement batch"}:{" "}
                            {settlement.settlementBatchNo}
                          </span>
                        )}
                      </div>
                      <div className={`settlement-amount ${settlement.direction}`}>
                        {settlement.direction === "outflow"
                          ? "-"
                          : settlement.direction === "inflow"
                            ? "+"
                            : ""}
                        {money(settlement.amountKhr)} KHR
                      </div>
                    </div>
                    <p>{settlement.reason}</p>
                    {settlement.status === "reversed" && (
                      <small>
                        {locale === "zh-CN" ? "已冲销" : "Reversed"}: {settlement.reversalReason}
                      </small>
                    )}
                    <div className="settlement-entry-actions">
                      {settlement.receiptStatus === "ready" && (
                        <button
                          className="secondary-button"
                          onClick={() =>
                            window.open(
                              `/api/v1/settlements/${settlement.id}/receipt`,
                              "_blank",
                              "noopener",
                            )
                          }
                        >
                          <FileDown size={14} />
                          {settlement.receiptNo}
                        </button>
                      )}
                      {settlement.receiptStatus === "failed" && (
                        <button
                          className="secondary-button"
                          onClick={() => void retryReceipt(settlement.id)}
                        >
                          <RefreshCw size={14} />
                          {locale === "zh-CN" ? "重试回执" : "Retry receipt"}
                        </button>
                      )}
                      {settlement.status === "posted" &&
                        ["document_payment", "void_counter"].includes(settlement.purpose) && (
                          <button
                            className="danger-button"
                            onClick={() => setPendingAction({ kind: "reverse", settlement })}
                          >
                            <RotateCcw size={14} />
                            {locale === "zh-CN" ? "冲销" : "Reverse"}
                          </button>
                        )}
                    </div>
                  </article>
                ))}
                {!detail.settlements.length && (
                  <div className="empty-cell">{tr(locale, "empty")}</div>
                )}
              </section>
              <div className="action-row drawer-actions">
                <button
                  className="secondary-button"
                  onClick={() =>
                    window.open(`/api/v1/documents/${item.id}/print`, "_blank", "noopener")
                  }
                >
                  <FileDown size={16} />
                  {tr(locale, "print")}
                </button>
                {detail.canRevise && (
                  <button className="secondary-button" onClick={() => onEdit("revise", detail)}>
                    <FilePenLine size={16} />
                    {locale === "zh-CN" ? "修改" : "Revise"}
                  </button>
                )}
                {detail.canReturn && (
                  <button className="secondary-button" onClick={() => onEdit("return", detail)}>
                    <RotateCcw size={16} />
                    {locale === "zh-CN" ? "退货" : "Return"}
                  </button>
                )}
                <button className="secondary-button" onClick={() => void retryPrint()}>
                  <RefreshCw size={16} />
                  {detail.printStatus === "ready"
                    ? locale === "zh-CN"
                      ? "更新打印文件"
                      : "Update print file"
                    : locale === "zh-CN"
                      ? "重试打印"
                      : "Retry print"}
                </button>
                {detail.status === "confirmed" && (
                  <button
                    className="danger-button"
                    onClick={() => setPendingAction({ kind: "void" })}
                  >
                    <Trash2 size={16} />
                    {tr(locale, "void")}
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="loading-line">
              <RefreshCw className="spin" size={17} />
              Loading
            </div>
          )}
        </aside>
      </div>
      <ReasonConfirmDialog
        open={Boolean(pendingAction)}
        locale={locale}
        busy={busy}
        title={confirmationTitle}
        description={confirmationDescription}
        confirmLabel={locale === "zh-CN" ? "确认执行" : "Confirm action"}
        danger={pendingAction?.kind === "void" || pendingAction?.kind === "reverse"}
        onCancel={() => !busy && setPendingAction(undefined)}
        onConfirm={(reason) => void executeAction(reason)}
      />
    </>
  );
}

function localDateTimeInputValue() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function oldestAllocations(documents: any[], amountKhr: number) {
  let remaining = Math.max(0, Math.round(amountKhr));
  return documents.map((document) => {
    const amount = Math.min(remaining, Number(document.balanceKhr));
    remaining -= amount;
    return { documentId: document.id, amountKhr: amount };
  });
}

function SettlementEntryDialog({
  side,
  party,
  accounts,
  locale,
  notify,
  onClose,
  onCreated,
}: {
  side: "receivable" | "payable";
  party: any;
  accounts: FinancialAccount[];
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [stage, setStage] = useState<"edit" | "review">("edit");
  const [busy, setBusy] = useState(false);
  const [requestKey] = useState(() => crypto.randomUUID());
  const [amountKhr, setAmountKhr] = useState(Number(party.outstandingKhr));
  const [accountId, setAccountId] = useState(
    accounts.find((account) => account.accountType === "cash")?.id ?? accounts[0]?.id ?? "",
  );
  const [occurredAt, setOccurredAt] = useState(localDateTimeInputValue());
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [allocations, setAllocations] = useState(() =>
    oldestAllocations(party.documents, Number(party.outstandingKhr)),
  );
  const actionLabel =
    side === "receivable"
      ? locale === "zh-CN"
        ? "收款"
        : "Collection"
      : locale === "zh-CN"
        ? "付款"
        : "Payment";
  const allocationTotal = allocations.reduce((sum, item) => sum + Number(item.amountKhr), 0);
  const changeAmount = (value: number) => {
    const next = Math.max(0, Math.round(value || 0));
    setAmountKhr(next);
    setAllocations(oldestAllocations(party.documents, next));
  };
  const review = () => {
    if (amountKhr <= 0 || amountKhr > Number(party.outstandingKhr)) {
      notify(locale === "zh-CN" ? "收付款金额超出未结余额。" : "Amount exceeds balance.", "error");
      return;
    }
    if (!accountId) {
      notify(locale === "zh-CN" ? "请选择资金账户。" : "Select an account.", "error");
      return;
    }
    if (reason.trim().length < 2) {
      notify(locale === "zh-CN" ? "请填写至少两个字的原因。" : "Enter a reason.", "error");
      return;
    }
    if (allocationTotal !== amountKhr) {
      notify(
        locale === "zh-CN" ? "单据分配合计必须等于收付款金额。" : "Allocation total must match.",
        "error",
      );
      return;
    }
    const invalid = allocations.some((allocation) => {
      const document = party.documents.find((item: any) => item.id === allocation.documentId);
      return allocation.amountKhr < 0 || allocation.amountKhr > Number(document?.balanceKhr ?? 0);
    });
    if (invalid) {
      notify(locale === "zh-CN" ? "单据分配金额无效。" : "Invalid allocation.", "error");
      return;
    }
    setStage("review");
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const result = await api<{ batchNo: string; receiptStatus: string }>(
        "/finance/settlement-batches",
        {
          method: "POST",
          headers: { "Idempotency-Key": requestKey },
          body: JSON.stringify({
            side,
            partyId: party.partyId,
            accountId,
            amountKhr,
            occurredAt: new Date(occurredAt).toISOString(),
            reason: reason.trim(),
            notes: notes.trim(),
            allocations: allocations.filter((allocation) => allocation.amountKhr > 0),
          }),
        },
        locale === "zh-CN"
          ? side === "receivable"
            ? "收款登记失败"
            : "付款登记失败"
          : side === "receivable"
            ? "Collection failed"
            : "Payment failed",
      );
      notify(`${result.batchNo} · ${actionLabel} ${money(amountKhr)} KHR`, "success");
      await onCreated();
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : "收付款失败", "error");
    } finally {
      setBusy(false);
    }
  };
  const account = accounts.find((item) => item.id === accountId);
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={() => !busy && onClose()}>
      <section
        className="confirm-dialog settlement-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settlement-entry-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dialog-heading">
          <div>
            <div className="eyebrow">{side === "receivable" ? "RECEIVABLE" : "PAYABLE"}</div>
            <h2 id="settlement-entry-title">
              {stage === "edit"
                ? actionLabel
                : locale === "zh-CN"
                  ? `核对${actionLabel}`
                  : `Review ${actionLabel}`}
            </h2>
          </div>
          <button className="icon-button" type="button" title="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        {stage === "edit" ? (
          <>
            <div className="settlement-party-banner">
              <strong>{party.partyName}</strong>
              <span>{money(Number(party.outstandingKhr))} KHR</span>
            </div>
            <div className="finance-form-grid settlement-dialog-form">
              <label>
                {locale === "zh-CN" ? `${actionLabel}金额 (KHR)` : `${actionLabel} amount (KHR)`}
                <input
                  id="settlement-batch-amount"
                  type="number"
                  min="1"
                  max={party.outstandingKhr}
                  step="1"
                  value={amountKhr}
                  onChange={(event) => changeAmount(Number(event.target.value))}
                />
              </label>
              <label>
                {locale === "zh-CN" ? "资金账户" : "Account"}
                <SearchableSelect
                  id="settlement-batch-account"
                  name="settlementAccountId"
                  ariaLabel={locale === "zh-CN" ? "资金账户" : "Account"}
                  value={accountId}
                  onChange={setAccountId}
                  placeholder="--"
                  searchPlaceholder={locale === "zh-CN" ? "搜索账户" : "Search accounts"}
                  emptyText={locale === "zh-CN" ? "没有可用账户" : "No available accounts"}
                  options={accounts.map((item) => ({
                    value: item.id,
                    label: `${item.name} · ${money(Number(item.balanceKhr))} KHR`,
                    keywords: `${item.accountCode} ${item.accountType}`,
                  }))}
                />
              </label>
              <label>
                {locale === "zh-CN" ? "发生时间" : "Occurred at"}
                <input
                  id="settlement-batch-occurred-at"
                  type="datetime-local"
                  value={occurredAt}
                  onChange={(event) => setOccurredAt(event.target.value)}
                />
              </label>
              <label className="finance-reason-field">
                {locale === "zh-CN" ? "原因（必填）" : "Reason (required)"}
                <input
                  id="settlement-batch-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
              <label className="finance-notes-field">
                {locale === "zh-CN" ? "备注" : "Notes"}
                <input
                  id="settlement-batch-notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </label>
            </div>
            <div className="allocation-editor">
              <div className="section-heading-row">
                <h3>{locale === "zh-CN" ? "分配到未结单据" : "Allocate to open documents"}</h3>
                <strong className={allocationTotal === amountKhr ? "" : "negative"}>
                  {money(allocationTotal)} / {money(amountKhr)}
                </strong>
              </div>
              {party.documents.map((document: any, index: number) => (
                <label className="allocation-row" key={document.id}>
                  <span>
                    <strong>{document.documentNo}</strong>
                    <small>{document.documentDate}</small>
                  </span>
                  <span>{money(Number(document.balanceKhr))} KHR</span>
                  <input
                    aria-label={`${document.documentNo} allocation`}
                    type="number"
                    min="0"
                    max={document.balanceKhr}
                    step="1"
                    value={allocations[index]?.amountKhr ?? 0}
                    onChange={(event) =>
                      setAllocations((current) =>
                        current.map((allocation, allocationIndex) =>
                          allocationIndex === index
                            ? {
                                ...allocation,
                                amountKhr: Math.max(0, Number(event.target.value) || 0),
                              }
                            : allocation,
                        ),
                      )
                    }
                  />
                </label>
              ))}
            </div>
          </>
        ) : (
          <div className="review-grid">
            <span>
              {locale === "zh-CN" ? "对象" : "Party"}
              <strong>{party.partyName}</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "金额" : "Amount"}
              <strong>{money(amountKhr)} KHR</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "账户" : "Account"}
              <strong>{account?.name}</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "时间" : "Time"}
              <strong>{occurredAt}</strong>
            </span>
            <span className="review-wide">
              {locale === "zh-CN" ? "原因" : "Reason"}
              <strong>{reason}</strong>
            </span>
            <span className="review-wide">
              {locale === "zh-CN" ? "分配" : "Allocations"}
              <strong>
                {allocations
                  .filter((allocation) => allocation.amountKhr > 0)
                  .map((allocation) => {
                    const document = party.documents.find(
                      (item: any) => item.id === allocation.documentId,
                    );
                    return `${document?.documentNo}: ${money(allocation.amountKhr)}`;
                  })
                  .join(" · ")}
              </strong>
            </span>
          </div>
        )}
        <div className="dialog-actions">
          <button
            className="secondary-button"
            type="button"
            disabled={busy}
            onClick={() => (stage === "review" ? setStage("edit") : onClose())}
          >
            {stage === "review"
              ? locale === "zh-CN"
                ? "返回修改"
                : "Back"
              : locale === "zh-CN"
                ? "取消"
                : "Cancel"}
          </button>
          <button
            className="primary-button"
            type="button"
            disabled={busy}
            onClick={() => (stage === "edit" ? review() : void confirm())}
          >
            <Check size={16} />
            {busy
              ? "..."
              : stage === "edit"
                ? locale === "zh-CN"
                  ? "保存并核对"
                  : "Save and review"
                : locale === "zh-CN"
                  ? `确认${actionLabel}`
                  : `Confirm ${actionLabel}`}
          </button>
        </div>
      </section>
    </div>
  );
}

function SettlementAccountsPage({
  side,
  locale,
  notify,
}: {
  side: "receivable" | "payable";
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
}) {
  const [view, setView] = useState<"open" | "history">("open");
  const [query, setQuery] = useState("");
  const [openData, setOpenData] = useState<any>({
    items: [],
    totalOutstandingKhr: 0,
    partyCount: 0,
  });
  const [batches, setBatches] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [settlingParty, setSettlingParty] = useState<any>();
  const [reversingBatch, setReversingBatch] = useState<any>();
  const [busy, setBusy] = useState(false);
  const load = useCallback(
    async (search = "") => {
      try {
        const [balanceData, batchData, accountData] = await Promise.all([
          api<any>(`/finance/open-balances?side=${side}&q=${encodeURIComponent(search)}`),
          api<{ items: any[] }>(
            `/finance/settlement-batches?side=${side}&q=${encodeURIComponent(search)}`,
          ),
          api<{ items: FinancialAccount[] }>("/finance/accounts"),
        ]);
        setOpenData(balanceData);
        setBatches(batchData.items);
        setAccounts(accountData.items.filter((account) => Boolean(account.active)));
      } catch (error) {
        notify(error instanceof Error ? error.message : "账款资料读取失败", "error");
      }
    },
    [notify, side],
  );
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const actionLabel =
    side === "receivable"
      ? locale === "zh-CN"
        ? "收款"
        : "Collect"
      : locale === "zh-CN"
        ? "付款"
        : "Pay";
  const reverse = async (reason: string) => {
    if (!reversingBatch) return;
    setBusy(true);
    try {
      await api(`/finance/settlement-batches/${reversingBatch.id}/reverse`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ reason }),
      });
      notify(locale === "zh-CN" ? "收付款批次已冲销" : "Settlement batch reversed", "success");
      setReversingBatch(undefined);
      await load(query);
    } catch (error) {
      notify(error instanceof Error ? error.message : "冲销失败", "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="page-stack">
      <div className="page-heading">
        <div>
          <div className="eyebrow">{side === "receivable" ? "RECEIVABLE" : "PAYABLE"}</div>
          <h1>
            {side === "receivable"
              ? locale === "zh-CN"
                ? "应收账款"
                : "Accounts receivable"
              : locale === "zh-CN"
                ? "应付账款"
                : "Accounts payable"}
          </h1>
        </div>
        <button className="secondary-button" type="button" onClick={() => void load(query)}>
          <RefreshCw size={16} />
          {tr(locale, "refresh")}
        </button>
      </div>
      <div className="summary-strip receivable-summary">
        <div>
          <span>{locale === "zh-CN" ? "未结总额" : "Outstanding"}</span>
          <strong>{money(Number(openData.totalOutstandingKhr))} KHR</strong>
        </div>
        <div>
          <span>{side === "receivable" ? tr(locale, "customer") : tr(locale, "supplier")}</span>
          <strong>{openData.partyCount}</strong>
        </div>
        <div>
          <span>{locale === "zh-CN" ? "收付款批次" : "Settlement batches"}</span>
          <strong>{batches.length}</strong>
        </div>
      </div>
      <div className="settlement-page-toolbar">
        <div className="segmented-control settlement-view-control">
          <button
            type="button"
            className={view === "open" ? "active" : ""}
            onClick={() => setView("open")}
          >
            {locale === "zh-CN" ? "未结账款" : "Open balances"}
          </button>
          <button
            type="button"
            className={view === "history" ? "active" : ""}
            onClick={() => setView("history")}
          >
            {locale === "zh-CN" ? "收付款记录" : "Settlement history"}
          </button>
        </div>
        <div className="toolbar compact-search-toolbar">
          <input
            id={`${side}-search`}
            value={query}
            placeholder={locale === "zh-CN" ? "搜索对象或单号" : "Search party or document"}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && void load(query)}
          />
          <button className="secondary-button" type="button" onClick={() => void load(query)}>
            <PackageSearch size={15} />
            {tr(locale, "search")}
          </button>
        </div>
      </div>
      {view === "open" ? (
        <div className="table-panel party-balance-list">
          {openData.items.map((party: any) => {
            const isOpen = expanded.has(party.partyId);
            return (
              <article key={party.partyId} className="party-balance-row">
                <button
                  className="party-balance-summary"
                  type="button"
                  onClick={() =>
                    setExpanded((current) => {
                      const next = new Set(current);
                      if (next.has(party.partyId)) next.delete(party.partyId);
                      else next.add(party.partyId);
                      return next;
                    })
                  }
                >
                  <ChevronRight className={isOpen ? "expanded" : ""} size={18} />
                  <span>
                    <strong>{party.partyName}</strong>
                    <small>{party.contactName || party.phone || "-"}</small>
                  </span>
                  <span>
                    {party.documentCount} {locale === "zh-CN" ? "张未结单" : "open documents"}
                  </span>
                  <strong>{money(Number(party.outstandingKhr))} KHR</strong>
                </button>
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => setSettlingParty(party)}
                >
                  <HandCoins size={16} />
                  {actionLabel}
                </button>
                {isOpen && (
                  <div className="party-document-list">
                    {party.documents.map((document: any) => (
                      <div key={document.id}>
                        <strong>{document.documentNo}</strong>
                        <span>{document.documentDate}</span>
                        <span>{money(Number(document.balanceKhr))} KHR</span>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            );
          })}
          {!openData.items.length && <div className="empty-cell">{tr(locale, "empty")}</div>}
        </div>
      ) : (
        <div className="table-panel">
          <table className="data-table settlement-history-table">
            <thead>
              <tr>
                <th>{locale === "zh-CN" ? "批次号" : "Batch"}</th>
                <th>{locale === "zh-CN" ? "时间" : "Time"}</th>
                <th>{locale === "zh-CN" ? "对象" : "Party"}</th>
                <th>{locale === "zh-CN" ? "账户" : "Account"}</th>
                <th>{locale === "zh-CN" ? "金额" : "Amount"}</th>
                <th>{locale === "zh-CN" ? "状态" : "Status"}</th>
                <th>{tr(locale, "action")}</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr key={batch.id} className={batch.status === "reversed" ? "muted-row" : ""}>
                  <td
                    data-label={locale === "zh-CN" ? "临时号 / 正式号" : "Temporary / official no."}
                  >
                    <strong>{batch.batchNo}</strong>
                    <small>{batch.reason}</small>
                  </td>
                  <td>{formatDateTimeInTimeZone(batch.occurredAt, locale)}</td>
                  <td>{batch.partyName}</td>
                  <td>{batch.accountName}</td>
                  <td>{money(Number(batch.amountKhr))} KHR</td>
                  <td>{batch.status === "reversed" ? "已冲销" : "已入账"}</td>
                  <td data-label={locale === "zh-CN" ? "业务日期" : "Business date"}>
                    <div className="table-actions">
                      {batch.receiptStatus === "ready" && (
                        <button
                          className="icon-button"
                          type="button"
                          title={locale === "zh-CN" ? "打印收款凭证" : "Print receipt"}
                          onClick={() =>
                            window.open(
                              `/api/v1/finance/settlement-batches/${batch.id}/receipt`,
                              "_blank",
                              "noopener",
                            )
                          }
                        >
                          <Printer size={16} />
                        </button>
                      )}
                      {batch.receiptStatus === "failed" && (
                        <button
                          className="icon-button"
                          type="button"
                          title={locale === "zh-CN" ? "重试凭证" : "Retry receipt"}
                          onClick={async () => {
                            try {
                              await api(`/finance/settlement-batches/${batch.id}/receipt`, {
                                method: "POST",
                                body: "{}",
                              });
                              await load(query);
                            } catch (error) {
                              notify(
                                error instanceof Error ? error.message : "凭证生成失败",
                                "error",
                              );
                            }
                          }}
                        >
                          <RefreshCw size={16} />
                        </button>
                      )}
                      {batch.status === "posted" && (
                        <button
                          className="icon-button danger"
                          type="button"
                          title={locale === "zh-CN" ? "整批冲销" : "Reverse batch"}
                          onClick={() => setReversingBatch(batch)}
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!batches.length && (
                <tr>
                  <td colSpan={7} className="empty-cell">
                    {tr(locale, "empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {settlingParty && (
        <SettlementEntryDialog
          key={settlingParty.partyId}
          side={side}
          party={settlingParty}
          accounts={accounts}
          locale={locale}
          notify={notify}
          onClose={() => setSettlingParty(undefined)}
          onCreated={async () => load(query)}
        />
      )}
      <ReasonConfirmDialog
        open={Boolean(reversingBatch)}
        title={locale === "zh-CN" ? "确认整批冲销" : "Reverse settlement batch"}
        description={
          reversingBatch
            ? `${reversingBatch.batchNo} · ${reversingBatch.partyName} · ${money(Number(reversingBatch.amountKhr))} KHR`
            : ""
        }
        confirmLabel={locale === "zh-CN" ? "确认冲销" : "Confirm reversal"}
        danger
        locale={locale}
        busy={busy}
        onCancel={() => !busy && setReversingBatch(undefined)}
        onConfirm={(reason) => void reverse(reason)}
      />
    </div>
  );
}

function financeTypeLabel(type: string, locale: Locale) {
  const labels: Record<string, [string, string]> = {
    manual_income: ["其他收入", "Other income"],
    administrative_expense: ["行政支出", "Administrative expense"],
    transfer: ["账户转账", "Account transfer"],
    document_settlement: ["单据收付款", "Document settlement"],
    return_settlement: ["退货结算", "Return settlement"],
    reversal: ["冲销", "Reversal"],
  };
  return labels[type]?.[locale === "zh-CN" ? 0 : 1] ?? type;
}

function accountEntriesText(value: string) {
  return String(value ?? "")
    .split(" | ")
    .filter(Boolean)
    .map((entry) => {
      const match = entry.match(/^(.*):(-?\d+)$/);
      return match
        ? `${match[1]} ${Number(match[2]) >= 0 ? "+" : ""}${money(Number(match[2]))}`
        : entry;
    })
    .join(" · ");
}

function FinanceEntryDialog({
  accounts,
  locale,
  notify,
  onClose,
  onCreated,
}: {
  accounts: FinancialAccount[];
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [stage, setStage] = useState<"edit" | "review">("edit");
  const [busy, setBusy] = useState(false);
  const [requestKey] = useState(() => crypto.randomUUID());
  const [form, setForm] = useState({
    transactionType: "",
    amountKhr: 0,
    occurredAt: "",
    accountId:
      accounts.find((account) => account.accountType === "cash")?.id ?? accounts[0]?.id ?? "",
    toAccountId: "",
    category: "",
    partyName: "",
    reason: "",
    notes: "",
  });
  const review = () => {
    if (!form.transactionType) {
      notify(locale === "zh-CN" ? "请选择流水类型。" : "Select a transaction type.", "error");
      return;
    }
    if (!Number.isInteger(form.amountKhr) || form.amountKhr <= 0 || !form.accountId) {
      notify(
        locale === "zh-CN" ? "请填写有效金额和资金账户。" : "Enter an amount and account.",
        "error",
      );
      return;
    }
    if (form.transactionType === "transfer" && !form.toAccountId) {
      notify(locale === "zh-CN" ? "请选择转入账户。" : "Select the destination account.", "error");
      return;
    }
    if (form.reason.trim().length < 2) {
      notify(
        locale === "zh-CN" ? "请填写至少两个字的业务原因。" : "Enter a business reason.",
        "error",
      );
      return;
    }
    setStage("review");
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const result = await api<{ transactionNo: string }>(
        "/finance/transactions",
        {
          method: "POST",
          headers: { "Idempotency-Key": requestKey },
          body: JSON.stringify({
            ...form,
            occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
            toAccountId: form.transactionType === "transfer" ? form.toAccountId : null,
            amountKhr: Math.round(form.amountKhr),
          }),
        },
        locale === "zh-CN" ? "资金流水登记失败" : "Cash transaction failed",
      );
      notify(
        `${result.transactionNo} · ${financeTypeLabel(form.transactionType, locale)}`,
        "success",
      );
      await onCreated();
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : "登记失败", "error");
    } finally {
      setBusy(false);
    }
  };
  const selectedAccount = accounts.find((account) => account.id === form.accountId);
  const selectedToAccount = accounts.find((account) => account.id === form.toAccountId);
  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={() => !busy && onClose()}>
      <section
        className="confirm-dialog finance-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="finance-entry-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dialog-heading">
          <div>
            <div className="eyebrow">CASH ENTRY</div>
            <h2 id="finance-entry-title">
              {stage === "edit"
                ? locale === "zh-CN"
                  ? "登记资金流水"
                  : "Post transaction"
                : locale === "zh-CN"
                  ? "核对资金流水"
                  : "Review transaction"}
            </h2>
          </div>
          <button className="icon-button" type="button" title="Close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        {stage === "edit" ? (
          <>
            <div className="segmented-control finance-type-control finance-dialog-types">
              {(["administrative_expense", "manual_income", "transfer"] as const).map((type) => (
                <button
                  type="button"
                  key={type}
                  className={form.transactionType === type ? "active" : ""}
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      transactionType: type,
                      toAccountId: type === "transfer" ? current.toAccountId : "",
                    }))
                  }
                >
                  {financeTypeLabel(type, locale)}
                </button>
              ))}
            </div>
            <div className="finance-form-grid finance-dialog-form">
              <label>
                {locale === "zh-CN" ? "金额 (KHR)" : "Amount (KHR)"}
                <input
                  id="finance-amount"
                  type="number"
                  min="1"
                  step="1"
                  value={form.amountKhr}
                  onChange={(event) => setForm({ ...form, amountKhr: Number(event.target.value) })}
                />
              </label>
              <label>
                {form.transactionType === "transfer"
                  ? locale === "zh-CN"
                    ? "转出账户"
                    : "From account"
                  : locale === "zh-CN"
                    ? "资金账户"
                    : "Account"}
                <SearchableSelect
                  id="finance-account"
                  name="accountId"
                  ariaLabel={locale === "zh-CN" ? "资金账户" : "Account"}
                  value={form.accountId}
                  onChange={(accountId) => setForm({ ...form, accountId })}
                  placeholder="--"
                  searchPlaceholder={locale === "zh-CN" ? "搜索账户" : "Search accounts"}
                  emptyText={locale === "zh-CN" ? "没有可用账户" : "No available accounts"}
                  options={accounts.map((account) => ({
                    value: account.id,
                    label: `${account.name} · ${money(Number(account.balanceKhr))} KHR`,
                    keywords: account.accountCode,
                  }))}
                />
              </label>
              {form.transactionType === "transfer" && (
                <label>
                  {locale === "zh-CN" ? "转入账户" : "To account"}
                  <SearchableSelect
                    id="finance-to-account"
                    name="toAccountId"
                    ariaLabel={locale === "zh-CN" ? "转入账户" : "To account"}
                    value={form.toAccountId}
                    onChange={(toAccountId) => setForm({ ...form, toAccountId })}
                    placeholder="--"
                    searchPlaceholder={locale === "zh-CN" ? "搜索账户" : "Search accounts"}
                    emptyText={locale === "zh-CN" ? "没有其他账户" : "No other account"}
                    options={accounts
                      .filter((account) => account.id !== form.accountId)
                      .map((account) => ({
                        value: account.id,
                        label: `${account.name} · ${money(Number(account.balanceKhr))} KHR`,
                        keywords: account.accountCode,
                      }))}
                  />
                </label>
              )}
              <label>
                {locale === "zh-CN" ? "发生时间" : "Occurred at"}
                <input
                  id="finance-occurred-at"
                  type="datetime-local"
                  value={form.occurredAt}
                  onChange={(event) => setForm({ ...form, occurredAt: event.target.value })}
                />
              </label>
              <label>
                {locale === "zh-CN" ? "类别" : "Category"}
                <input
                  id="finance-category"
                  value={form.category}
                  onChange={(event) => setForm({ ...form, category: event.target.value })}
                />
              </label>
              <label>
                {locale === "zh-CN" ? "对象" : "Party"}
                <input
                  id="finance-party"
                  value={form.partyName}
                  onChange={(event) => setForm({ ...form, partyName: event.target.value })}
                />
              </label>
              <label className="finance-reason-field">
                {locale === "zh-CN" ? "业务原因" : "Reason"}
                <input
                  id="finance-reason"
                  value={form.reason}
                  onChange={(event) => setForm({ ...form, reason: event.target.value })}
                />
              </label>
              <label className="finance-notes-field">
                {locale === "zh-CN" ? "备注" : "Notes"}
                <input
                  id="finance-notes"
                  value={form.notes}
                  onChange={(event) => setForm({ ...form, notes: event.target.value })}
                />
              </label>
            </div>
          </>
        ) : (
          <div className="review-grid">
            <span>
              {locale === "zh-CN" ? "类型" : "Type"}
              <strong>{financeTypeLabel(form.transactionType, locale)}</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "金额" : "Amount"}
              <strong>{money(form.amountKhr)} KHR</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "账户" : "Account"}
              <strong>
                {selectedAccount?.name}
                {selectedToAccount ? ` → ${selectedToAccount.name}` : ""}
              </strong>
            </span>
            <span>
              {locale === "zh-CN" ? "时间" : "Time"}
              <strong>{form.occurredAt || (locale === "zh-CN" ? "当前时间" : "Now")}</strong>
            </span>
            <span className="review-wide">
              {locale === "zh-CN" ? "原因" : "Reason"}
              <strong>{form.reason}</strong>
            </span>
            {form.notes && (
              <span className="review-wide">
                {locale === "zh-CN" ? "备注" : "Notes"}
                <strong>{form.notes}</strong>
              </span>
            )}
          </div>
        )}
        <div className="dialog-actions">
          <button
            className="secondary-button"
            type="button"
            disabled={busy}
            onClick={() => (stage === "review" ? setStage("edit") : onClose())}
          >
            {stage === "review"
              ? locale === "zh-CN"
                ? "返回修改"
                : "Back"
              : locale === "zh-CN"
                ? "取消"
                : "Cancel"}
          </button>
          <button
            className="primary-button"
            type="button"
            disabled={busy}
            onClick={() => (stage === "edit" ? review() : void confirm())}
          >
            <Check size={16} />
            {busy
              ? "..."
              : stage === "edit"
                ? locale === "zh-CN"
                  ? "保存并核对"
                  : "Save and review"
                : locale === "zh-CN"
                  ? "确认登记"
                  : "Confirm posting"}
          </button>
        </div>
      </section>
    </div>
  );
}

function FinancePage({
  locale,
  notify,
}: {
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
}) {
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [summary, setSummary] = useState<any>();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [selected, setSelected] = useState<any>();
  const [showEntry, setShowEntry] = useState(false);
  const [pendingCounters, setPendingCounters] = useState<any[]>([]);
  const load = useCallback(
    async (search = "", transactionType = "", accountId = "") => {
      try {
        const [accountData, summaryData, transactionData, counterData] = await Promise.all([
          api<{ items: FinancialAccount[] }>("/finance/accounts"),
          api<any>("/finance/summary"),
          api<{ items: any[] }>(
            `/finance/transactions?q=${encodeURIComponent(search)}&type=${encodeURIComponent(transactionType)}&accountId=${encodeURIComponent(accountId)}`,
          ),
          api<{ items: any[] }>("/finance/pending-counter-settlements"),
        ]);
        const activeAccounts = accountData.items.filter((account) => Boolean(account.active));
        setAccounts(activeAccounts);
        setSummary(summaryData);
        setTransactions(transactionData.items);
        setPendingCounters(counterData.items);
      } catch (error) {
        notify(error instanceof Error ? error.message : "财务资料读取失败", "error");
      }
    },
    [notify],
  );
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const refresh = () => load(query, typeFilter, accountFilter);
  const totals = summary?.totals ?? {};
  return (
    <div className="page-stack">
      <div className="page-heading">
        <div>
          <div className="eyebrow">CASH LEDGER</div>
          <h1>{tr(locale, "finance")}</h1>
          <p className="muted">
            {locale === "zh-CN"
              ? "记录实际资金流入、流出和账户转账。"
              : "Actual cash, bank and transfer ledger."}
          </p>
        </div>
        <div className="heading-actions">
          <button className="primary-button" type="button" onClick={() => setShowEntry(true)}>
            <Plus size={16} />
            {locale === "zh-CN" ? "登记资金流水" : "Post transaction"}
          </button>
          <button className="secondary-button" onClick={() => void refresh()}>
            <RefreshCw size={16} />
            {tr(locale, "refresh")}
          </button>
        </div>
      </div>
      <div className="summary-strip finance-summary">
        <div>
          <span>{locale === "zh-CN" ? "本月流入" : "Month inflow"}</span>
          <strong>{money(Number(totals.totalInflowKhr ?? 0))}</strong>
        </div>
        <div>
          <span>{locale === "zh-CN" ? "本月流出" : "Month outflow"}</span>
          <strong>{money(Number(totals.totalOutflowKhr ?? 0))}</strong>
        </div>
        {accounts.map((account) => (
          <div key={account.id}>
            <span>{account.name}</span>
            <strong>{money(Number(account.balanceKhr))}</strong>
          </div>
        ))}
      </div>
      {pendingCounters.length > 0 && (
        <section className="table-panel pending-counter-panel">
          <div className="panel-header">
            <h2>{locale === "zh-CN" ? "待退款 / 待追收" : "Pending refunds / recoveries"}</h2>
            <span className="count-tag">{pendingCounters.length}</span>
          </div>
          <div className="compact-ledger-list">
            {pendingCounters.map((item) => (
              <div key={item.id}>
                <strong>{item.documentNo}</strong>
                <span>{item.partyName}</span>
                <span>{item.direction === "outflow" ? "待退款" : "待追收"}</span>
                <strong>{money(Number(item.remainingKhr))} KHR</strong>
              </div>
            ))}
          </div>
        </section>
      )}
      <div className="toolbar finance-toolbar">
        <input
          id="finance-search"
          name="financeSearch"
          placeholder={locale === "zh-CN" ? "搜索流水号、对象、原因或单号" : "Search transactions"}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === "Enter" && void refresh()}
        />
        <select
          id="finance-type-filter"
          name="financeTypeFilter"
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
        >
          <option value="">{locale === "zh-CN" ? "全部类型" : "All types"}</option>
          {[
            "administrative_expense",
            "manual_income",
            "transfer",
            "document_settlement",
            "return_settlement",
            "reversal",
          ].map((type) => (
            <option key={type} value={type}>
              {financeTypeLabel(type, locale)}
            </option>
          ))}
        </select>
        <select
          id="finance-account-filter"
          name="financeAccountFilter"
          value={accountFilter}
          onChange={(event) => setAccountFilter(event.target.value)}
        >
          <option value="">{locale === "zh-CN" ? "全部账户" : "All accounts"}</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}
            </option>
          ))}
        </select>
        <button className="secondary-button" onClick={() => void refresh()}>
          <PackageSearch size={15} />
          {tr(locale, "search")}
        </button>
      </div>
      <div className="table-panel">
        <table className="data-table finance-table">
          <thead>
            <tr>
              <th>{locale === "zh-CN" ? "流水号" : "No."}</th>
              <th>{locale === "zh-CN" ? "时间" : "Time"}</th>
              <th>{locale === "zh-CN" ? "类型" : "Type"}</th>
              <th>{locale === "zh-CN" ? "账户" : "Accounts"}</th>
              <th>{locale === "zh-CN" ? "对象 / 原因" : "Party / reason"}</th>
              <th>{locale === "zh-CN" ? "金额" : "Amount"}</th>
              <th>{tr(locale, "action")}</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((transaction) => (
              <tr
                key={transaction.id}
                className={`clickable-row ${transaction.reversed ? "muted-row" : ""}`}
                onClick={() => setSelected(transaction)}
              >
                <td>
                  <strong>{transaction.transactionNo}</strong>
                  {transaction.documentNo && <small>{transaction.documentNo}</small>}
                  {transaction.settlementBatchNo && <small>{transaction.settlementBatchNo}</small>}
                </td>
                <td>{formatDateTimeInTimeZone(transaction.occurredAt, locale)}</td>
                <td>{financeTypeLabel(transaction.transactionType, locale)}</td>
                <td>{accountEntriesText(transaction.accountEntries)}</td>
                <td>
                  {transaction.partyName || "-"}
                  <small>{transaction.reason}</small>
                </td>
                <td className={`finance-amount ${transaction.direction}`}>
                  {transaction.direction === "inflow"
                    ? "+"
                    : transaction.direction === "outflow"
                      ? "-"
                      : ""}
                  {money(Number(transaction.amountKhr))}
                </td>
                <td>
                  <button
                    className="icon-button"
                    type="button"
                    title={locale === "zh-CN" ? "查看财务流水" : "View transaction"}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelected(transaction);
                    }}
                  >
                    <Eye size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {!transactions.length && (
              <tr>
                <td colSpan={7} className="empty-cell">
                  {tr(locale, "empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {selected && (
        <FinanceTransactionDrawer
          item={selected}
          locale={locale}
          notify={notify}
          onClose={() => setSelected(undefined)}
          onRefresh={async () => {
            await refresh();
            setSelected(undefined);
          }}
        />
      )}
      {showEntry && (
        <FinanceEntryDialog
          accounts={accounts}
          locale={locale}
          notify={notify}
          onClose={() => setShowEntry(false)}
          onCreated={async () => {
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function FinanceTransactionDrawer({ item, locale, notify, onClose, onRefresh }: any) {
  const [attachments, setAttachments] = useState<any[]>([]);
  const [file, setFile] = useState<File>();
  const [busy, setBusy] = useState(false);
  const [confirmReverse, setConfirmReverse] = useState(false);
  const loadAttachments = useCallback(
    () =>
      api<{ items: any[] }>(`/finance/transactions/${item.id}/attachments`)
        .then((data) => setAttachments(data.items))
        .catch((error) => notify(error.message, "error")),
    [item.id, notify],
  );
  useEffect(() => {
    void loadAttachments();
  }, [loadAttachments]);
  const upload = async () => {
    if (!file) {
      notify(locale === "zh-CN" ? "请选择图片或 PDF。" : "Select an image or PDF.", "error");
      return;
    }
    if (!window.confirm(locale === "zh-CN" ? "确认上传该附件？" : "Upload this attachment?"))
      return;
    setBusy(true);
    try {
      const form = new FormData();
      form.set("file", file);
      await apiForm(`/finance/transactions/${item.id}/attachments`, form);
      setFile(undefined);
      await loadAttachments();
      notify(locale === "zh-CN" ? "附件已上传" : "Attachment uploaded", "success");
    } catch (error) {
      notify(error instanceof Error ? error.message : "上传失败", "error");
    } finally {
      setBusy(false);
    }
  };
  const reverse = async (reason: string) => {
    setBusy(true);
    try {
      await api(`/finance/transactions/${item.id}/reverse`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ reason }),
      });
      notify(locale === "zh-CN" ? "财务流水已冲销" : "Transaction reversed", "success");
      setConfirmReverse(false);
      await onRefresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : "冲销失败", "error");
    } finally {
      setBusy(false);
    }
  };
  const canReverse =
    !item.reversed &&
    item.transactionType !== "reversal" &&
    !item.settlementId &&
    !item.settlementBatchNo;
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose}>
        <aside className="drawer" onClick={(event) => event.stopPropagation()}>
          <div className="drawer-heading">
            <div>
              <div className="eyebrow">{item.transactionNo}</div>
              <h2>{financeTypeLabel(item.transactionType, locale)}</h2>
            </div>
            <button className="icon-button" type="button" title="Close" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
          <div className="detail-grid">
            <span>
              {locale === "zh-CN" ? "发生时间" : "Occurred at"}
              <strong>{formatDateTimeInTimeZone(item.occurredAt, locale)}</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "金额" : "Amount"}
              <strong>{money(Number(item.amountKhr))} KHR</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "账户" : "Accounts"}
              <strong>{accountEntriesText(item.accountEntries)}</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "对象" : "Party"}
              <strong>{item.partyName || "-"}</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "类别" : "Category"}
              <strong>{item.category || "-"}</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "登记人" : "Created by"}
              <strong>{item.createdByName}</strong>
            </span>
            {item.settlementBatchNo && (
              <span>
                {locale === "zh-CN" ? "收付款批次" : "Settlement batch"}
                <strong>{item.settlementBatchNo}</strong>
              </span>
            )}
          </div>
          <div className="finance-reason-block">
            <strong>{locale === "zh-CN" ? "业务原因" : "Reason"}</strong>
            <span>{item.reason}</span>
            {item.notes && <small>{item.notes}</small>}
          </div>
          <section className="settlement-timeline">
            <div className="section-heading-row">
              <h3>{locale === "zh-CN" ? "附件" : "Attachments"}</h3>
              <span className="count-tag">{attachments.length}</span>
            </div>
            <div className="attachment-upload">
              <label>
                <Paperclip size={15} />
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(event) => setFile(event.target.files?.[0])}
                />
              </label>
              <button
                className="secondary-button"
                type="button"
                disabled={busy || !file}
                onClick={upload}
              >
                <Upload size={15} />
                {locale === "zh-CN" ? "确认上传" : "Upload"}
              </button>
            </div>
            <div className="attachment-list">
              {attachments.map((attachment) => (
                <button
                  className="attachment-row"
                  type="button"
                  key={attachment.id}
                  onClick={() =>
                    window.open(
                      `/api/v1/finance/attachments/${attachment.id}`,
                      "_blank",
                      "noopener",
                    )
                  }
                >
                  <Paperclip size={14} />
                  <span>{attachment.fileName}</span>
                  <small>{Math.ceil(Number(attachment.sizeBytes) / 1024)} KB</small>
                </button>
              ))}
              {!attachments.length && <div className="empty-cell">{tr(locale, "empty")}</div>}
            </div>
          </section>
          {canReverse && (
            <div className="action-row drawer-actions">
              <button
                className="danger-button"
                type="button"
                onClick={() => setConfirmReverse(true)}
              >
                <RotateCcw size={15} />
                {locale === "zh-CN" ? "冲销流水" : "Reverse"}
              </button>
            </div>
          )}
        </aside>
      </div>
      <ReasonConfirmDialog
        open={confirmReverse}
        locale={locale}
        busy={busy}
        title={locale === "zh-CN" ? "确认冲销财务流水" : "Confirm transaction reversal"}
        description={`${item.transactionNo} · ${money(Number(item.amountKhr))} KHR`}
        confirmLabel={locale === "zh-CN" ? "确认冲销" : "Reverse"}
        danger
        onCancel={() => !busy && setConfirmReverse(false)}
        onConfirm={(reason) => void reverse(reason)}
      />
    </>
  );
}

function StocktakeOrderDialog({
  type,
  seed,
  locale,
  notify,
  onClose,
  onCompleted,
}: {
  type: StocktakeDocumentSide;
  seed: OrderSeed;
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
  onClose: () => void;
  onCompleted: (result: any) => void | Promise<void>;
}) {
  const [dirty, setDirty] = useState(false);
  const documentType = String(seed.document?.documentType ?? type) as DocumentType;
  const title =
    seed.mode === "pending" && seed.document?.pendingDocumentId
      ? locale === "zh-CN"
        ? "修改差异处理单据"
        : "Edit reconciliation document"
      : documentType === "sale_return"
        ? locale === "zh-CN"
          ? "销售退货"
          : "Sale return"
        : documentType === "purchase_return"
          ? locale === "zh-CN"
            ? "进货退货"
            : "Purchase return"
          : type === "sale"
            ? locale === "zh-CN"
              ? "补销售单"
              : "Backdated sale"
            : locale === "zh-CN"
              ? "补进货单"
              : "Backdated purchase";
  const requestClose = () => {
    if (
      dirty &&
      !window.confirm(
        locale === "zh-CN"
          ? "当前单据有未保存修改，确认关闭？"
          : "This document has unsaved changes. Close it?",
      )
    )
      return;
    onClose();
  };
  useEffect(() => {
    if (!dirty) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);
  return (
    <div
      className="dialog-backdrop order-dialog-backdrop"
      role="presentation"
      onMouseDown={requestClose}
    >
      <section
        className="stocktake-order-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stocktake-order-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dialog-heading stocktake-order-dialog-heading">
          <div>
            <div className="eyebrow">RECONCILIATION</div>
            <h2 id="stocktake-order-dialog-title">{title}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            title={locale === "zh-CN" ? "关闭" : "Close"}
            aria-label={locale === "zh-CN" ? "关闭" : "Close"}
            onClick={requestClose}
          >
            <X size={18} />
          </button>
        </div>
        <div className="stocktake-order-dialog-body">
          <OrderPage
            key={seed.token}
            type={type}
            locale={locale}
            notify={notify}
            seed={seed}
            embedded
            onSeedConsumed={() => undefined}
            onDirtyChange={setDirty}
            onCompleted={onCompleted}
          />
        </div>
      </section>
    </div>
  );
}

function StocktakeReturnSourceDialog({
  type,
  locale,
  notify,
  excludedDocumentIds,
  onClose,
  onSelect,
}: {
  type: StocktakeDocumentSide;
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
  excludedDocumentIds: Set<string>;
  onClose: () => void;
  onSelect: (document: any) => void | Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const load = useCallback(
    async (search: string) => {
      setLoading(true);
      try {
        const result = await api<{ items: any[] }>(returnableDocumentHistoryPath(type, search));
        setItems(result.items.filter((item) => !excludedDocumentIds.has(item.id)));
      } catch (error) {
        notify(error instanceof Error ? error.message : "历史原单读取失败", "error");
      } finally {
        setLoading(false);
      }
    },
    [excludedDocumentIds, notify, type],
  );
  useEffect(() => {
    const timer = window.setTimeout(() => void load(""), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const selectOriginal = async (item: any) => {
    setSelectedId(item.id);
    try {
      const result = await api<{ document: any }>(`/documents/${item.id}`);
      if (!result.document.canReturn) {
        notify(
          locale === "zh-CN" ? "该原单已经没有可退数量。" : "No returnable quantity remains.",
          "error",
        );
        await load(query);
        return;
      }
      await onSelect(result.document);
    } catch (error) {
      notify(error instanceof Error ? error.message : "原单读取失败", "error");
    } finally {
      setSelectedId("");
    }
  };
  return (
    <div
      className="dialog-backdrop return-source-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="return-source-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="return-source-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dialog-heading">
          <div>
            <div className="eyebrow">ORIGINAL DOCUMENT</div>
            <h2 id="return-source-dialog-title">
              {locale === "zh-CN"
                ? type === "sale"
                  ? "选择销售原单"
                  : "选择进货原单"
                : type === "sale"
                  ? "Select original sale"
                  : "Select original purchase"}
            </h2>
          </div>
          <button
            className="icon-button"
            type="button"
            title={locale === "zh-CN" ? "关闭" : "Close"}
            aria-label={locale === "zh-CN" ? "关闭" : "Close"}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>
        <div className="toolbar return-source-toolbar">
          <input
            autoFocus
            aria-label={locale === "zh-CN" ? "搜索历史原单" : "Search original documents"}
            placeholder={locale === "zh-CN" ? "搜索单号或客户/供应商" : "Search number or party"}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && void load(query)}
          />
          <button
            className="secondary-button"
            type="button"
            disabled={loading}
            onClick={() => void load(query)}
          >
            <PackageSearch size={15} />
            {tr(locale, "search")}
          </button>
        </div>
        <div className="return-source-table-wrap">
          <table className="data-table compact return-source-table">
            <thead>
              <tr>
                <th>{locale === "zh-CN" ? "单号" : "Document no."}</th>
                <th>{locale === "zh-CN" ? "日期" : "Date"}</th>
                <th>{locale === "zh-CN" ? "对象" : "Party"}</th>
                <th>{locale === "zh-CN" ? "金额" : "Amount"}</th>
                <th>{tr(locale, "action")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} data-return-source-document-id={item.id}>
                  <td data-label={locale === "zh-CN" ? "单号" : "Document no."}>
                    <strong>{item.documentNo}</strong>
                  </td>
                  <td data-label={locale === "zh-CN" ? "日期" : "Date"}>{item.documentDate}</td>
                  <td data-label={locale === "zh-CN" ? "对象" : "Party"}>{item.partyName}</td>
                  <td data-label={locale === "zh-CN" ? "金额" : "Amount"}>
                    {money(Number(item.finalTotalKhr))} KHR
                  </td>
                  <td data-label={tr(locale, "action")}>
                    <button
                      className="primary-button compact-button"
                      type="button"
                      disabled={Boolean(selectedId)}
                      onClick={() => void selectOriginal(item)}
                    >
                      <ChevronRight size={15} />
                      {selectedId === item.id ? "..." : locale === "zh-CN" ? "选择" : "Select"}
                    </button>
                  </td>
                </tr>
              ))}
              {!items.length && !loading && (
                <tr>
                  <td className="empty-cell" colSpan={5}>
                    {locale === "zh-CN" ? "没有可退原单" : "No returnable documents"}
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td className="empty-cell" colSpan={5}>
                    <RefreshCw className="spin" size={16} />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function InventoryPage({
  locale,
  notify,
}: {
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
}) {
  const [items, setItems] = useState<any[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);
  const [stocktakes, setStocktakes] = useState<any[]>([]);
  const [exports, setExports] = useState<any[]>([]);
  const [openingItems, setOpeningItems] = useState<Record<string, number>>({});
  const [showOpening, setShowOpening] = useState(false);
  const [periodKey, setPeriodKey] = useState(currentMonth());
  const [query, setQuery] = useState("");
  const queryRef = useRef("");
  const [selectedItem, setSelectedItem] = useState<any>();
  const [view, setView] = useState<"balance" | "stocktakes" | "periods">("balance");
  const [stocktakeDate, setStocktakeDate] = useState(today());
  const [stocktakeNotes, setStocktakeNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const fetchInventory = useCallback(
    (search = "") =>
      Promise.all([
        api<{ items: any[] }>(`/inventory?q=${encodeURIComponent(search)}`),
        api<{ items: any[] }>("/inventory/periods"),
        api<{ items: any[] }>("/inventory/stocktakes"),
        api<{ items: any[] }>("/inventory/exports"),
      ])
        .then(([stock, period, stocktakeData, exportData]) => {
          setItems(stock.items);
          setPeriods(period.items);
          setStocktakes(stocktakeData.items);
          setExports(exportData.items);
          return { stock: stock.items, stocktakes: stocktakeData.items };
        })
        .catch((error) => notify(error.message, "error")),
    [notify],
  );
  const load = () => fetchInventory(query);
  useEffect(() => {
    void fetchInventory();
  }, [fetchInventory]);
  useEffect(() => {
    queryRef.current = query;
  }, [query]);
  useEffect(() => {
    const refresh = () => void fetchInventory(queryRef.current);
    const channel =
      typeof BroadcastChannel === "undefined"
        ? undefined
        : new BroadcastChannel(STOCKTAKE_UPDATE_CHANNEL);
    channel?.addEventListener("message", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      channel?.removeEventListener("message", refresh);
      channel?.close();
      window.removeEventListener("focus", refresh);
    };
  }, [fetchInventory]);
  const selectedPeriod = periods.find((period) => period.periodKey === periodKey);
  const activeStocktake = stocktakes.find((stocktake) => stocktake.status === "active");
  const confirmOpening = async () => {
    if (
      selectedPeriod?.openingConfirmed ||
      !window.confirm(
        locale === "zh-CN"
          ? `确认以当前数量建立 ${periodKey} 期初库存？确认后不能重复执行。`
          : `Confirm these opening balances for ${periodKey}? This cannot be repeated.`,
      )
    )
      return;
    try {
      await api("/inventory/periods/opening", {
        method: "POST",
        body: JSON.stringify({
          periodKey,
          items: items.map((item) => ({
            productId: item.id,
            quantity: Math.round(Number(openingItems[item.id] ?? item.quantity ?? 0)),
          })),
        }),
      });
      notify(locale === "zh-CN" ? "期初库存已确认" : "Opening stock confirmed", "success");
      setShowOpening(false);
      await load();
    } catch (error) {
      notify(error instanceof Error ? error.message : "期初确认失败", "error");
    }
  };
  const closePeriod = async () => {
    const openPeriod = periods.find(
      (period) => period.status === "open" && period.openingConfirmed,
    );
    if (
      !openPeriod ||
      !window.confirm(
        locale === "zh-CN"
          ? `确认月结 ${openPeriod.periodKey}？月结后该月单据不可修改或作废。`
          : `Close ${openPeriod.periodKey}? Documents in it can no longer be changed or voided.`,
      )
    )
      return;
    try {
      await api(`/inventory/periods/${openPeriod.periodKey}/close`, { method: "POST", body: "{}" });
      notify(locale === "zh-CN" ? "月结完成" : "Period closed");
      await load();
    } catch (error) {
      notify(error instanceof Error ? error.message : "月结失败", "error");
    }
  };
  const createStocktake = async () => {
    if (
      activeStocktake ||
      !window.confirm(
        locale === "zh-CN"
          ? `确认开始 ${stocktakeDate} 的库存盘点？盘点期间正式库存变更会暂停。`
          : `Start the ${stocktakeDate} stocktake? Inventory-changing actions will pause.`,
      )
    )
      return;
    setBusy(true);
    try {
      const result = await api<{ stocktake: any }>("/inventory/stocktakes", {
        method: "POST",
        body: JSON.stringify({ stocktakeDate, notes: stocktakeNotes }),
      });
      setStocktakeNotes("");
      setView("stocktakes");
      await fetchInventory(query);
      notify(
        `${result.stocktake.stocktakeNo} · ${locale === "zh-CN" ? "盘点已开始" : "Stocktake started"}`,
      );
      openStocktake(result.stocktake.id);
    } catch (error) {
      notify(error instanceof Error ? error.message : "盘点创建失败", "error");
    } finally {
      setBusy(false);
    }
  };
  const openStocktake = (id: string) => {
    const openArguments = stocktakeTabOpenArguments(id);
    const stocktakeTab = window.open(...openArguments);
    if (stocktakeTab) stocktakeTab.focus();
    else window.location.assign(openArguments[0]);
  };
  const createExport = async () => {
    if (
      !window.confirm(
        locale === "zh-CN" ? "确认生成当前库存快照？" : "Create an inventory snapshot?",
      )
    )
      return;
    setBusy(true);
    try {
      const result = await api<{ id: string; exportNo: string }>("/inventory/exports", {
        method: "POST",
        body: "{}",
      });
      await fetchInventory(query);
      window.open(`/api/v1/inventory/exports/${result.id}/html`, "_blank", "noopener");
      notify(`${result.exportNo} · ${locale === "zh-CN" ? "快照已生成" : "Snapshot created"}`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "快照生成失败", "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="page-stack">
      <div className="page-heading">
        <div>
          <div className="eyebrow">STOCK LEDGER</div>
          <h1>{tr(locale, "inventory")}</h1>
          <p className="muted">
            {locale === "zh-CN"
              ? "库存以包装组合显示，基本单位数量仍可追溯到每一笔流水。"
              : "Balances are shown as packaging combinations with a traceable base quantity."}
          </p>
        </div>
        <div className="action-row">
          <button className="secondary-button" disabled={busy} onClick={() => void createExport()}>
            <FileSpreadsheet size={16} />
            {locale === "zh-CN" ? "库存快照" : "Snapshot"}
          </button>
          <button
            className="secondary-button"
            disabled={
              busy ||
              Boolean(activeStocktake) ||
              !periods.some((period) => period.status === "open")
            }
            onClick={() => setView("stocktakes")}
          >
            <ClipboardCheck size={16} />
            {activeStocktake
              ? activeStocktake.stocktakeNo
              : locale === "zh-CN"
                ? "开始盘点"
                : "Stocktake"}
          </button>
          <button
            className="secondary-button"
            disabled={periods.length > 0}
            onClick={() => setShowOpening(!showOpening)}
          >
            <Archive size={16} />
            {periods.length > 0
              ? locale === "zh-CN"
                ? "期初已确认"
                : "Opening locked"
              : tr(locale, "opening")}
          </button>
          <button
            className="danger-button"
            disabled={
              !periods.some((period) => period.status === "open" && period.openingConfirmed)
            }
            onClick={closePeriod}
          >
            <Check size={16} />
            {locale === "zh-CN" ? "月结" : "Close month"}
          </button>
        </div>
      </div>
      <div className="segmented-control inventory-view-control">
        <button className={view === "balance" ? "active" : ""} onClick={() => setView("balance")}>
          {locale === "zh-CN" ? "库存余额" : "Balances"}
        </button>
        <button
          className={view === "stocktakes" ? "active" : ""}
          onClick={() => setView("stocktakes")}
        >
          {locale === "zh-CN" ? "库存盘点" : "Stocktakes"}
        </button>
        <button className={view === "periods" ? "active" : ""} onClick={() => setView("periods")}>
          {locale === "zh-CN" ? "月份与快照" : "Periods & exports"}
        </button>
      </div>
      {view === "balance" && showOpening && (
        <div className="notice-panel">
          <div>
            <strong>{tr(locale, "opening")}</strong>
            <p>{tr(locale, "openingHint")}</p>
          </div>
          <label className="period-picker">
            {locale === "zh-CN" ? "月份" : "Month"}
            <input
              id="opening-period"
              name="periodKey"
              type="month"
              value={periodKey}
              onChange={(event) => setPeriodKey(event.target.value)}
            />
          </label>
          {selectedPeriod?.openingConfirmed ? (
            <div className="error-box">
              <CircleAlert size={16} />
              {locale === "zh-CN"
                ? "该月份期初已经确认，不能重复覆盖。"
                : "Opening balances for this month are already confirmed."}
            </div>
          ) : (
            <>
              <div className="opening-grid">
                {items.map((item) => (
                  <label key={item.id}>
                    {item.code} · {item.name}
                    <input
                      id={`opening-${item.id}`}
                      name={`opening.${item.id}`}
                      type="number"
                      min="0"
                      step="1"
                      value={openingItems[item.id] ?? item.quantity ?? 0}
                      onChange={(event) =>
                        setOpeningItems((current) => ({
                          ...current,
                          [item.id]: Number(event.target.value),
                        }))
                      }
                    />
                  </label>
                ))}
              </div>
              <button className="primary-button" onClick={confirmOpening}>
                <Check size={16} />
                {tr(locale, "confirmOpening")}
              </button>
            </>
          )}
        </div>
      )}
      {view === "balance" && (
        <>
          <div className="summary-strip">
            <div>
              <span>Products</span>
              <strong>{items.length}</strong>
            </div>
            <div>
              <span>Stocked</span>
              <strong>{items.filter((item) => Number(item.quantityMilli) > 0).length}</strong>
            </div>
            <div>
              <span>Empty</span>
              <strong>{items.filter((item) => Number(item.quantityMilli) <= 0).length}</strong>
            </div>
          </div>
          <div className="toolbar inventory-toolbar">
            <input
              id="inventory-search"
              name="inventorySearch"
              placeholder={locale === "zh-CN" ? "搜索编码或品名" : "Search code or product"}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && void load()}
            />
            <button className="secondary-button" onClick={() => void load()}>
              <PackageSearch size={15} />
              {tr(locale, "search")}
            </button>
          </div>
          <div className="table-panel">
            <table className="data-table">
              <thead>
                <tr>
                  <th>编码 / Code</th>
                  <th>{tr(locale, "product")}</th>
                  <th>{tr(locale, "unit")}</th>
                  <th>{locale === "zh-CN" ? "库存" : "Balance"}</th>
                  <th>Updated</th>
                  <th>{tr(locale, "action")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="clickable-row" onClick={() => setSelectedItem(item)}>
                    <td>
                      <strong>{item.code}</strong>
                    </td>
                    <td>{item.name}</td>
                    <td>{item.baseUnit}</td>
                    <td className={Number(item.quantityMilli) <= 0 ? "negative" : "stock-value"}>
                      <strong>{item.packaging?.text ?? `${item.quantity} ${item.baseUnit}`}</strong>
                      <small>
                        {item.quantity} {item.baseUnit}
                        {item.packaging?.warning ? ` · ${item.packaging.warning}` : ""}
                      </small>
                    </td>
                    <td>{formatDateTimeInTimeZone(item.updatedAt, locale)}</td>
                    <td>
                      <button
                        className="icon-button"
                        type="button"
                        title={locale === "zh-CN" ? "查看库存流水" : "View stock movements"}
                        aria-label={locale === "zh-CN" ? "查看库存流水" : "View stock movements"}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedItem(item);
                        }}
                      >
                        <BookOpen size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {!items.length && (
                  <tr>
                    <td className="empty-cell" colSpan={6}>
                      {tr(locale, "empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {view === "stocktakes" && (
        <>
          {activeStocktake && (
            <div className="mode-banner stocktake-lock-banner">
              <ClipboardCheck size={17} />
              <strong>
                {locale === "zh-CN"
                  ? `${activeStocktake.stocktakeNo} 正在盘点，正式库存变更已暂停。`
                  : `${activeStocktake.stocktakeNo} is active; inventory changes are paused.`}
              </strong>
            </div>
          )}
          {!activeStocktake && (
            <section className="form-panel stocktake-create-panel">
              <div className="two-column-form">
                <label>
                  {locale === "zh-CN" ? "盘点日期" : "Stocktake date"}
                  <input
                    id="stocktake-date"
                    name="stocktakeDate"
                    type="date"
                    value={stocktakeDate}
                    onChange={(event) => setStocktakeDate(event.target.value)}
                  />
                </label>
                <label>
                  {locale === "zh-CN" ? "备注" : "Notes"}
                  <input
                    id="stocktake-notes"
                    name="stocktakeNotes"
                    value={stocktakeNotes}
                    onChange={(event) => setStocktakeNotes(event.target.value)}
                  />
                </label>
              </div>
              <div className="action-row">
                <button
                  className="primary-button"
                  disabled={busy}
                  onClick={() => void createStocktake()}
                >
                  <ClipboardCheck size={16} />
                  {locale === "zh-CN" ? "确认开始盘点" : "Start stocktake"}
                </button>
              </div>
            </section>
          )}
          <div className="table-panel">
            <div className="panel-header">
              <h2>{locale === "zh-CN" ? "盘点记录" : "Stocktake history"}</h2>
            </div>
            <table className="data-table compact">
              <thead>
                <tr>
                  <th>{locale === "zh-CN" ? "盘点号" : "No."}</th>
                  <th>{locale === "zh-CN" ? "日期" : "Date"}</th>
                  <th>{tr(locale, "status")}</th>
                  <th>{locale === "zh-CN" ? "完成进度" : "Progress"}</th>
                  <th>{locale === "zh-CN" ? "差异" : "Variance"}</th>
                  <th>{tr(locale, "action")}</th>
                </tr>
              </thead>
              <tbody>
                {stocktakes.map((stocktake) => (
                  <tr
                    key={stocktake.id}
                    className="clickable-row"
                    onClick={() => openStocktake(stocktake.id)}
                  >
                    <td>
                      <strong>{stocktake.stocktakeNo}</strong>
                    </td>
                    <td>{stocktake.stocktakeDate}</td>
                    <td>
                      <span
                        className={`status-pill ${
                          stocktake.status === "confirmed"
                            ? "success-pill"
                            : stocktake.status === "cancelled"
                              ? "danger-pill"
                              : "warning-pill"
                        }`}
                      >
                        {stocktake.status}
                      </span>
                    </td>
                    <td>
                      {Number(stocktake.countedCount)}/{Number(stocktake.lineCount)}
                    </td>
                    <td>{Number(stocktake.varianceCount)}</td>
                    <td>
                      <button
                        className="icon-button"
                        type="button"
                        title={locale === "zh-CN" ? "查看盘点" : "View stocktake"}
                        onClick={(event) => {
                          event.stopPropagation();
                          openStocktake(stocktake.id);
                        }}
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {!stocktakes.length && (
                  <tr>
                    <td className="empty-cell" colSpan={6}>
                      {tr(locale, "empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {view === "periods" && (
        <>
          <div className="table-panel">
            <div className="panel-header">
              <h2>{locale === "zh-CN" ? "月份状态" : "Accounting periods"}</h2>
            </div>
            <table className="data-table compact">
              <thead>
                <tr>
                  <th>{locale === "zh-CN" ? "月份" : "Month"}</th>
                  <th>{tr(locale, "status")}</th>
                  <th>{locale === "zh-CN" ? "期初" : "Opening"}</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((period) => (
                  <tr key={period.periodKey}>
                    <td>{period.periodKey}</td>
                    <td>{period.status === "open" ? "Open" : "Closed"}</td>
                    <td>{period.openingConfirmed ? "Confirmed" : "Pending"}</td>
                  </tr>
                ))}
                {!periods.length && (
                  <tr>
                    <td className="empty-cell" colSpan={3}>
                      {tr(locale, "empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="table-panel">
            <div className="panel-header">
              <h2>{locale === "zh-CN" ? "库存快照" : "Inventory snapshots"}</h2>
              <button
                className="secondary-button"
                disabled={busy}
                onClick={() => void createExport()}
              >
                <FileSpreadsheet size={15} />
                {locale === "zh-CN" ? "生成快照" : "Create snapshot"}
              </button>
            </div>
            <table className="data-table compact">
              <thead>
                <tr>
                  <th>{locale === "zh-CN" ? "快照号" : "No."}</th>
                  <th>{locale === "zh-CN" ? "生成时间" : "Created"}</th>
                  <th>HTML</th>
                  <th>XLSX</th>
                </tr>
              </thead>
              <tbody>
                {exports.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.exportNo}</strong>
                    </td>
                    <td>{formatDateTimeInTimeZone(item.snapshotAt, locale)}</td>
                    <td>
                      <button
                        className="icon-button"
                        type="button"
                        title={locale === "zh-CN" ? "预览打印" : "Preview and print"}
                        onClick={() =>
                          window.open(
                            `/api/v1/inventory/exports/${item.id}/html`,
                            "_blank",
                            "noopener",
                          )
                        }
                      >
                        <Printer size={16} />
                      </button>
                    </td>
                    <td>
                      <button
                        className="icon-button"
                        type="button"
                        title={locale === "zh-CN" ? "下载 Excel" : "Download Excel"}
                        onClick={() =>
                          window.open(
                            `/api/v1/inventory/exports/${item.id}/xlsx`,
                            "_blank",
                            "noopener",
                          )
                        }
                      >
                        <FileSpreadsheet size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {!exports.length && (
                  <tr>
                    <td className="empty-cell" colSpan={4}>
                      {tr(locale, "empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {selectedItem && (
        <InventoryMovementDrawer
          item={selectedItem}
          locale={locale}
          notify={notify}
          onClose={() => setSelectedItem(undefined)}
        />
      )}
    </div>
  );
}

function initialStocktakeReasons(stocktake: any) {
  const draftReasons = stocktake.draft?.reasons ?? {};
  return Object.fromEntries(
    (stocktake.lines ?? []).map((line: any) => [
      line.productId,
      Object.hasOwn(draftReasons, line.productId)
        ? String(draftReasons[line.productId] ?? "")
        : (line.varianceReason ?? ""),
    ]),
  ) as Record<string, string>;
}

function StocktakeWorkspace({
  stocktake,
  locale,
  notify,
  onClose,
  onUpdated,
  onFinished,
  showClose = true,
}: any) {
  const [counts, setCounts] = useState<Record<string, Record<string, string>>>(() =>
    initialStocktakeCounts(stocktake),
  );
  const [reasons, setReasons] = useState<Record<string, string>>(() =>
    initialStocktakeReasons(stocktake),
  );
  const [artifacts, setArtifacts] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [draftStatus, setDraftStatus] = useState<"saved" | "saving" | "error">("saved");
  const [residualOpenProductId, setResidualOpenProductId] = useState<string>();
  const draftTimerRef = useRef<number | undefined>(undefined);
  const draftRequestRef = useRef(0);
  const draftEditRevisionRef = useRef(0);
  const draftInFlightRef = useRef<Promise<void> | null>(null);
  const draftPausedRef = useRef(false);
  const [draftPaused, setDraftPaused] = useState(false);
  const [pendingAction, setPendingAction] = useState<"confirm" | "cancel">();
  const [orderDialog, setOrderDialog] = useState<{
    type: StocktakeDocumentSide;
    seed: OrderSeed;
  }>();
  const [returnSourceType, setReturnSourceType] = useState<StocktakeDocumentSide>();
  const loadArtifacts = useCallback(
    () =>
      api<{ items: any[] }>(`/inventory/stocktakes/${stocktake.id}/artifacts`)
        .then((data) => setArtifacts(data.items))
        .catch((error) => notify(error.message, "error")),
    [notify, stocktake.id],
  );
  useEffect(() => {
    void loadArtifacts();
  }, [loadArtifacts]);
  const activeUnits = (line: any): PackagingUnit[] =>
    (line.units ?? [])
      .filter((unit: PackagingUnit) => Boolean(unit.active))
      .sort(
        (left: PackagingUnit, right: PackagingUnit) =>
          right.factorMilli - left.factorMilli || left.sortOrder - right.sortOrder,
      );
  const countedMilli = (line: any) =>
    activeUnits(line).reduce((sum, unit) => {
      const raw = counts[line.productId]?.[unit.id];
      return raw === undefined || raw === "" ? sum : sum + Number(raw) * Number(unit.factorMilli);
    }, 0);
  const residualMilli = (line: any) =>
    countedMilli(line) - Number(line.systemQuantityMilli) - Number(line.pendingQuantityMilli ?? 0);
  const savedCounts = initialStocktakeCounts(stocktake);
  const stocktakeFingerprint = (
    countValues: Record<string, Record<string, string>>,
    reasonValues: Record<string, string>,
  ) =>
    JSON.stringify(
      (stocktake.lines ?? []).map((line: any) => ({
        productId: line.productId,
        counts: activeUnits(line).map((unit) => [
          unit.id,
          countValues[line.productId]?.[unit.id] ?? "",
        ]),
        reason: reasonValues[line.productId]?.trim() ?? "",
      })),
    );
  const hasUnsavedChanges =
    stocktake.status === "active" &&
    stocktakeFingerprint(counts, reasons) !==
      stocktakeFingerprint(savedCounts, initialStocktakeReasons(stocktake));
  const incompleteCount = (stocktake.lines ?? []).filter((line: any) => !line.countedAt).length;
  const unresolvedCount = (stocktake.lines ?? []).filter(
    (line: any) => residualMilli(line) !== 0,
  ).length;
  const pendingDocuments = useMemo(
    () => stocktake.pendingDocuments ?? [],
    [stocktake.pendingDocuments],
  );
  const openPendingDocuments = pendingDocuments.filter(
    (document: any) => document.status === "pending",
  );
  const clearDraftTimer = useCallback(() => {
    if (draftTimerRef.current !== undefined) {
      window.clearTimeout(draftTimerRef.current);
      draftTimerRef.current = undefined;
    }
  }, []);
  const waitForDraftRequests = useCallback(async () => {
    while (draftInFlightRef.current) {
      const request = draftInFlightRef.current;
      await request;
      if (draftInFlightRef.current === request) break;
    }
  }, []);
  const pauseDraftAutosave = useCallback(() => {
    draftPausedRef.current = true;
    clearDraftTimer();
    draftRequestRef.current += 1;
    setDraftPaused(true);
  }, [clearDraftTimer]);
  const resumeDraftAutosave = useCallback(() => {
    draftPausedRef.current = false;
    setDraftPaused(false);
  }, []);
  const persistDraft = useCallback(
    async (manual = false) => {
      if (stocktake.status !== "active" || draftPausedRef.current) return;
      if (
        manual &&
        !window.confirm(
          locale === "zh-CN" ? "确认保存当前盘点草稿？" : "Save the current stocktake draft?",
        )
      )
        return;
      const editRevision = draftEditRevisionRef.current;
      const draftCounts = counts;
      const draftReasons = reasons;
      const requestId = ++draftRequestRef.current;
      const run = async () => {
        if (
          !canSendStocktakeDraftRequest({
            requestId,
            currentRequestId: draftRequestRef.current,
            editRevision,
            currentEditRevision: draftEditRevisionRef.current,
            paused: draftPausedRef.current,
            stocktakeStatus: stocktake.status,
          })
        )
          return;
        setDraftStatus("saving");
        try {
          if (
            !canSendStocktakeDraftRequest({
              requestId,
              currentRequestId: draftRequestRef.current,
              editRevision,
              currentEditRevision: draftEditRevisionRef.current,
              paused: draftPausedRef.current,
              stocktakeStatus: stocktake.status,
            })
          )
            return;
          const result = await api<{ stocktake: any }>(
            `/inventory/stocktakes/${stocktake.id}/draft`,
            {
              method: "PUT",
              body: JSON.stringify({ counts: draftCounts, reasons: draftReasons }),
            },
            locale === "zh-CN" ? "盘点草稿保存失败" : "Stocktake draft save failed",
          );
          if (
            requestId !== draftRequestRef.current ||
            editRevision !== draftEditRevisionRef.current ||
            draftPausedRef.current
          )
            return;
          setDraftDirty(false);
          setDraftStatus("saved");
          await onUpdated(result.stocktake);
        } catch (error) {
          if (
            requestId === draftRequestRef.current &&
            editRevision === draftEditRevisionRef.current &&
            !draftPausedRef.current
          ) {
            setDraftStatus("error");
            notify(
              error instanceof Error
                ? error.message
                : locale === "zh-CN"
                  ? "盘点草稿保存失败，请重试。"
                  : "Stocktake draft could not be saved. Try again.",
              "error",
            );
          }
        }
      };
      const previous = draftInFlightRef.current ?? Promise.resolve();
      const queued = previous.then(run, run);
      const wrapped = queued.finally(() => {
        if (draftInFlightRef.current === wrapped) draftInFlightRef.current = null;
      });
      draftInFlightRef.current = wrapped;
      await wrapped;
    },
    [counts, locale, notify, onUpdated, reasons, stocktake.id, stocktake.status],
  );
  useEffect(() => {
    clearDraftTimer();
    if (!draftDirty || stocktake.status !== "active" || draftPaused) return;
    draftTimerRef.current = window.setTimeout(() => void persistDraft(), 750);
    return clearDraftTimer;
  }, [clearDraftTimer, draftDirty, draftPaused, persistDraft, stocktake.status]);
  const saveCounts = async () => {
    const payloadLines: any[] = [];
    for (const line of stocktake.lines ?? []) {
      const lineCounts = activeUnits(line)
        .map((unit) => ({ unitId: unit.id, raw: counts[line.productId]?.[unit.id] ?? "" }))
        .filter((item) => item.raw !== "");
      if (!lineCounts.length) {
        notify(
          locale === "zh-CN"
            ? `${line.code} ${line.name} 尚未填写盘点数量。`
            : `${line.code} ${line.name} has no count.`,
          "error",
        );
        return;
      }
      if (lineCounts.some((item) => !Number.isInteger(Number(item.raw)) || Number(item.raw) < 0)) {
        notify(
          locale === "zh-CN" ? "包装数量必须是非负整数。" : "Package counts must be whole numbers.",
          "error",
        );
        return;
      }
      const varianceReason = reasons[line.productId]?.trim() ?? "";
      payloadLines.push({
        productId: line.productId,
        counts: lineCounts.map((item) => ({ unitId: item.unitId, count: Number(item.raw) })),
        varianceReason,
      });
    }
    if (
      !window.confirm(
        locale === "zh-CN" ? "确认保存本次盘点数量？" : "Save these stocktake counts?",
      )
    )
      return;
    setBusy(true);
    pauseDraftAutosave();
    try {
      await waitForDraftRequests();
      const result = await api<{ stocktake: any }>(
        `/inventory/stocktakes/${stocktake.id}/counts`,
        {
          method: "PUT",
          body: JSON.stringify({ lines: payloadLines }),
        },
        locale === "zh-CN" ? "盘点数量保存失败" : "Stocktake count save failed",
      );
      await onUpdated(result.stocktake);
      setDraftDirty(false);
      setDraftStatus("saved");
      publishStocktakeUpdate(stocktake.id, "counts-saved");
      notify(locale === "zh-CN" ? "盘点数量已保存" : "Stocktake counts saved", "success");
    } catch (error) {
      notify(error instanceof Error ? error.message : "盘点保存失败", "error");
    } finally {
      resumeDraftAutosave();
      setBusy(false);
    }
  };
  const executeAction = async (reason: string) => {
    if (!pendingAction) return;
    if (
      pendingAction === "confirm" &&
      (hasUnsavedChanges || incompleteCount > 0 || unresolvedCount > 0)
    ) {
      notify(
        locale === "zh-CN"
          ? "请先保存全部实盘数量，并用真实业务单据把剩余差异处理为零。"
          : "Save all counts and resolve every residual with business documents first.",
        "error",
      );
      setPendingAction(undefined);
      return;
    }
    setBusy(true);
    pauseDraftAutosave();
    try {
      await waitForDraftRequests();
      if (pendingAction === "confirm") {
        const result = await api<{ stocktake: any }>(
          `/inventory/stocktakes/${stocktake.id}/confirm`,
          {
            method: "POST",
            body: JSON.stringify({ reason }),
          },
          locale === "zh-CN" ? "盘点确认失败" : "Stocktake confirmation failed",
        );
        await onUpdated(result.stocktake);
        publishStocktakeUpdate(stocktake.id, "confirmed");
        notify(
          locale === "zh-CN"
            ? "盘点已确认，待处理单据已入账且库存与实盘一致"
            : "Stocktake confirmed and pending documents posted",
          "success",
        );
        await onFinished?.("confirmed");
      } else {
        await api(
          `/inventory/stocktakes/${stocktake.id}/cancel`,
          {
            method: "POST",
            body: JSON.stringify({ reason }),
          },
          locale === "zh-CN" ? "盘点取消失败" : "Stocktake cancellation failed",
        );
        await onUpdated(undefined);
        publishStocktakeUpdate(stocktake.id, "cancelled");
        notify(locale === "zh-CN" ? "盘点已取消" : "Stocktake cancelled", "success");
        await onFinished?.("cancelled");
      }
      setPendingAction(undefined);
      await loadArtifacts();
    } catch (error) {
      notify(error instanceof Error ? error.message : "盘点操作失败", "error");
    } finally {
      resumeDraftAutosave();
      setBusy(false);
    }
  };
  const deletePendingDocument = async (document: any) => {
    if (
      !window.confirm(
        locale === "zh-CN"
          ? `确认删除待处理单据 ${document.temporaryNo}？`
          : `Delete pending document ${document.temporaryNo}?`,
      )
    )
      return;
    setBusy(true);
    try {
      const result = await api<{ stocktake: any }>(
        `/inventory/stocktakes/${stocktake.id}/pending-documents/${document.id}`,
        { method: "DELETE", body: "{}" },
        locale === "zh-CN" ? "待处理单据删除失败" : "Pending document deletion failed",
      );
      await onUpdated(result.stocktake);
      publishStocktakeUpdate(stocktake.id, "pending-document-deleted");
      notify(locale === "zh-CN" ? "待处理单据已删除" : "Pending document deleted", "success");
    } catch (error) {
      notify(error instanceof Error ? error.message : "删除失败", "error");
    } finally {
      setBusy(false);
    }
  };
  const regenerateArtifacts = async () => {
    const variant = stocktake.status === "confirmed" ? "confirmed" : "counting";
    if (
      !window.confirm(locale === "zh-CN" ? "确认重新生成盘点文件？" : "Regenerate stocktake files?")
    )
      return;
    setBusy(true);
    try {
      await api(
        `/inventory/stocktakes/${stocktake.id}/artifacts`,
        {
          method: "POST",
          body: JSON.stringify({ variant }),
        },
        locale === "zh-CN" ? "盘点文件生成失败" : "Stocktake file generation failed",
      );
      await loadArtifacts();
      notify(locale === "zh-CN" ? "盘点文件已生成" : "Stocktake files generated", "success");
    } catch (error) {
      notify(error instanceof Error ? error.message : "文件生成失败", "error");
    } finally {
      setBusy(false);
    }
  };
  const documentTypeLabel = (documentType: DocumentType) => {
    const labels: Record<DocumentType, [string, string]> = {
      sale: ["补销售单", "Backdated sale"],
      purchase: ["补进货单", "Backdated purchase"],
      sale_return: ["销售退货", "Sale return"],
      purchase_return: ["进货退货", "Purchase return"],
    };
    return labels[documentType]?.[locale === "zh-CN" ? 0 : 1] ?? documentType;
  };
  const pendingStatusLabel = (document: any) => {
    if (document.status === "posted") {
      return document.officialDocumentNo || (locale === "zh-CN" ? "已入账" : "Posted");
    }
    if (document.status === "converted_to_draft") {
      return locale === "zh-CN" ? "已转草稿" : "Converted to draft";
    }
    return locale === "zh-CN" ? "待盘点确认" : "Pending confirmation";
  };
  const countsMustBeSaved = hasUnsavedChanges || incompleteCount > 0;
  const excludedOriginalDocumentIds = useMemo(
    () => referencedOriginalDocumentIds(pendingDocuments),
    [pendingDocuments],
  );
  const requireSavedCounts = () => {
    if (!countsMustBeSaved) return true;
    notify(
      locale === "zh-CN"
        ? "请先确认保存全部盘点数量，再维护差异处理单据。"
        : "Save every physical count before editing reconciliation documents.",
      "error",
    );
    return false;
  };
  const createPendingDocument = (type: StocktakeDocumentSide) => {
    if (!requireSavedCounts()) return;
    setOrderDialog({
      type,
      seed: {
        token: crypto.randomUUID(),
        mode: "pending",
        sourceStocktakeId: stocktake.id,
        document: {
          sourceStocktakeId: stocktake.id,
          stocktakeNo: stocktake.stocktakeNo,
          documentType: type,
          documentDate: today(),
          lines: [],
        },
      },
    });
  };
  const editPendingDocument = (document: any) => {
    if (!requireSavedCounts()) return;
    setOrderDialog({
      type: String(document.documentType).startsWith("sale") ? "sale" : "purchase",
      seed: { token: crypto.randomUUID(), mode: "pending", document },
    });
  };
  const chooseReturnSource = (type: StocktakeDocumentSide) => {
    if (!requireSavedCounts()) return;
    setReturnSourceType(type);
  };
  const createPendingReturn = (type: StocktakeDocumentSide, document: any) => {
    setReturnSourceType(undefined);
    setOrderDialog({
      type,
      seed: {
        token: crypto.randomUUID(),
        mode: "return",
        sourceStocktakeId: stocktake.id,
        document,
      },
    });
  };
  const completePendingDocument = async (result: any) => {
    setOrderDialog(undefined);
    try {
      const nextStocktake =
        result.stocktake ??
        (await api<{ stocktake: any }>(`/inventory/stocktakes/${stocktake.id}`)).stocktake;
      await onUpdated(nextStocktake);
      publishStocktakeUpdate(stocktake.id, "pending-document-saved");
    } catch (error) {
      notify(error instanceof Error ? error.message : "盘点刷新失败", "error");
    }
  };
  const requestClose = () => {
    if (
      (hasUnsavedChanges || draftDirty || draftStatus !== "saved") &&
      !window.confirm(
        locale === "zh-CN"
          ? draftStatus === "error"
            ? "盘点草稿保存失败，确认关闭工作台？"
            : "盘点数量或草稿有未保存修改，确认关闭工作台？"
          : draftStatus === "error"
            ? "The stocktake draft failed to save. Close the workspace?"
            : "Physical counts or draft changes are not saved. Close the workspace?",
      )
    )
      return;
    onClose();
  };
  const pendingSection = (
    <section className="stocktake-pending-section">
      <div className="stocktake-pending-heading">
        <div>
          <h3>{locale === "zh-CN" ? "差异处理单据" : "Reconciliation documents"}</h3>
          <p className="muted">
            {locale === "zh-CN"
              ? "漏录业务请补真实销售、进货或退货单；这些单据在最终确认盘点前不会影响库存和财务。"
              : "Record omitted sales, purchases or returns. They remain unposted until final confirmation."}
          </p>
        </div>
        {stocktake.status === "active" && (
          <div className="action-row stocktake-pending-create-actions">
            <button
              className="secondary-button"
              type="button"
              disabled={busy || countsMustBeSaved}
              onClick={() => createPendingDocument("sale")}
            >
              <ArrowUpFromLine size={15} />
              {locale === "zh-CN" ? "补销售单" : "Add sale"}
            </button>
            <button
              className="secondary-button"
              type="button"
              disabled={busy || countsMustBeSaved}
              onClick={() => createPendingDocument("purchase")}
            >
              <ArrowDownToLine size={15} />
              {locale === "zh-CN" ? "补进货单" : "Add purchase"}
            </button>
            <button
              className="secondary-button"
              type="button"
              disabled={busy || countsMustBeSaved}
              onClick={() => chooseReturnSource("sale")}
            >
              <RotateCcw size={15} />
              {locale === "zh-CN" ? "销售退货" : "Sale return"}
            </button>
            <button
              className="secondary-button"
              type="button"
              disabled={busy || countsMustBeSaved}
              onClick={() => chooseReturnSource("purchase")}
            >
              <RotateCcw size={15} />
              {locale === "zh-CN" ? "进货退货" : "Purchase return"}
            </button>
          </div>
        )}
      </div>
      <div className="stocktake-pending-summary">
        <span>
          {locale === "zh-CN" ? "待入账" : "Pending"}
          <strong>{openPendingDocuments.length}</strong>
        </span>
        <span>
          {locale === "zh-CN" ? "未保存数量" : "Unsaved counts"}
          <strong>{hasUnsavedChanges || incompleteCount > 0 ? incompleteCount || 1 : 0}</strong>
        </span>
        <span>
          {locale === "zh-CN" ? "剩余差异商品" : "Unresolved products"}
          <strong className={unresolvedCount > 0 ? "negative" : "stock-value"}>
            {unresolvedCount}
          </strong>
        </span>
      </div>
      <div className="stocktake-pending-table-wrap">
        <table className="data-table compact stocktake-pending-table">
          <thead>
            <tr>
              <th>{locale === "zh-CN" ? "临时号 / 正式号" : "Temporary / official no."}</th>
              <th>{locale === "zh-CN" ? "业务日期" : "Business date"}</th>
              <th>{locale === "zh-CN" ? "类型" : "Type"}</th>
              <th>{locale === "zh-CN" ? "对象" : "Party"}</th>
              <th>{locale === "zh-CN" ? "金额" : "Amount"}</th>
              <th>{locale === "zh-CN" ? "状态" : "Status"}</th>
              <th>{tr(locale, "action")}</th>
            </tr>
          </thead>
          <tbody>
            {pendingDocuments.map((document: any) => (
              <tr key={document.id} data-stocktake-pending-document-id={document.id}>
                <td>
                  <strong>{document.temporaryNo}</strong>
                  {document.officialDocumentNo && <small>{document.officialDocumentNo}</small>}
                </td>
                <td>
                  {document.documentDate}
                  {document.businessPeriodKey !== document.postingPeriodKey && (
                    <small>
                      {document.businessPeriodKey} → {document.postingPeriodKey}
                    </small>
                  )}
                </td>
                <td data-label={locale === "zh-CN" ? "类型" : "Type"}>
                  {documentTypeLabel(document.documentType)}
                </td>
                <td data-label={locale === "zh-CN" ? "对象" : "Party"}>{document.partyName}</td>
                <td data-label={locale === "zh-CN" ? "金额" : "Amount"}>
                  {money(Number(document.finalTotalKhr))} KHR
                </td>
                <td data-label={locale === "zh-CN" ? "状态" : "Status"}>
                  <span
                    className={`status-pill ${
                      document.status === "posted"
                        ? "success-pill"
                        : document.status === "pending"
                          ? "warning-pill"
                          : "danger-pill"
                    }`}
                  >
                    {pendingStatusLabel(document)}
                  </span>
                </td>
                <td data-label={tr(locale, "action")}>
                  {document.status === "pending" ? (
                    <div className="stocktake-pending-row-actions">
                      <button
                        className="icon-button"
                        type="button"
                        title={locale === "zh-CN" ? "修改待处理单据" : "Edit pending document"}
                        aria-label={locale === "zh-CN" ? "修改待处理单据" : "Edit pending document"}
                        disabled={busy || countsMustBeSaved}
                        onClick={() => editPendingDocument(document)}
                      >
                        <FilePenLine size={15} />
                      </button>
                      <button
                        className="icon-button danger-icon-button"
                        type="button"
                        title={locale === "zh-CN" ? "删除待处理单据" : "Delete pending document"}
                        aria-label={
                          locale === "zh-CN" ? "删除待处理单据" : "Delete pending document"
                        }
                        disabled={busy || countsMustBeSaved}
                        onClick={() => void deletePendingDocument(document)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ) : (
                    <span className="muted">-</span>
                  )}
                </td>
              </tr>
            ))}
            {!pendingDocuments.length && (
              <tr>
                <td className="empty-cell" colSpan={7}>
                  {locale === "zh-CN" ? "暂无差异处理单据" : "No reconciliation documents"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
  const confirmDisabled = busy || hasUnsavedChanges || incompleteCount > 0 || unresolvedCount > 0;
  return (
    <section className="stocktake-workspace">
      <div className="drawer-heading">
        <div>
          <div className="eyebrow">{stocktake.stocktakeNo}</div>
          <h2>{stocktake.stocktakeDate}</h2>
          <p className="muted">
            {stocktake.status} · {stocktake.periodKey}
          </p>
        </div>
        <div className="action-row stocktake-heading-actions">
          <button
            className="secondary-button"
            type="button"
            disabled={busy}
            onClick={regenerateArtifacts}
          >
            <RefreshCw size={15} />
            {locale === "zh-CN" ? "生成文件" : "Generate files"}
          </button>
          {showClose && (
            <button
              className="icon-button"
              type="button"
              title={locale === "zh-CN" ? "关闭盘点标签页" : "Close stocktake tab"}
              aria-label={locale === "zh-CN" ? "关闭盘点标签页" : "Close stocktake tab"}
              onClick={requestClose}
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>
      <div className="stocktake-overview">
        <span>
          {locale === "zh-CN" ? "状态" : "Status"}
          <strong>{stocktake.status}</strong>
        </span>
        <span>
          {locale === "zh-CN" ? "盘点进度" : "Counted"}
          <strong>
            {(stocktake.lines ?? []).length - incompleteCount}/{(stocktake.lines ?? []).length}
          </strong>
        </span>
        <span>
          {locale === "zh-CN" ? "待处理单据" : "Pending documents"}
          <strong>{openPendingDocuments.length}</strong>
        </span>
        <span>
          {locale === "zh-CN" ? "剩余差异商品" : "Unresolved products"}
          <strong className={unresolvedCount > 0 ? "negative" : "stock-value"}>
            {unresolvedCount}
          </strong>
        </span>
      </div>

      <section className="stocktake-count-section">
        <div className="section-heading-row stocktake-count-heading">
          <div>
            <h3>{locale === "zh-CN" ? "盘点数量" : "Physical counts"}</h3>
            <p className="muted">
              {locale === "zh-CN"
                ? "系统已按账面库存预填包装数量，只修改实物有差异的商品。"
                : "Packaging counts start from the ledger balance; change only physical differences."}
            </p>
          </div>
          <span className="count-tag">
            {(stocktake.lines ?? []).length - incompleteCount}/{(stocktake.lines ?? []).length}
          </span>
        </div>
        <div className="stocktake-table-wrap">
          <table className="data-table stocktake-table">
            <thead>
              <tr>
                <th>{locale === "zh-CN" ? "产品" : "Product"}</th>
                <th>{locale === "zh-CN" ? "系统库存" : "System stock"}</th>
                <th>{locale === "zh-CN" ? "实盘数量" : "Physical count"}</th>
                <th>{locale === "zh-CN" ? "对账结果" : "Reconciliation"}</th>
                <th>{locale === "zh-CN" ? "盘点说明" : "Count note"}</th>
              </tr>
            </thead>
            <tbody>
              {(stocktake.lines ?? []).map((line: any) => {
                const pendingMilli = Number(line.pendingQuantityMilli ?? 0);
                const expectedMilli = Number(line.systemQuantityMilli) + pendingMilli;
                const remainingMilli = residualMilli(line);
                return (
                  <tr key={line.productId} data-stocktake-product-id={line.productId}>
                    <td data-label={locale === "zh-CN" ? "产品" : "Product"}>
                      <strong>
                        {line.code} · {line.name}
                      </strong>
                      <small>{line.baseUnit}</small>
                    </td>
                    <td data-label={locale === "zh-CN" ? "系统库存" : "System stock"}>
                      <strong>{line.packaging?.text}</strong>
                      <small>
                        {Number(line.systemQuantityMilli) / 1000} {line.baseUnit}
                      </small>
                    </td>
                    <td data-label={locale === "zh-CN" ? "实盘数量" : "Physical count"}>
                      {stocktake.status === "active" ? (
                        <div className="stocktake-count-grid">
                          {activeUnits(line).map((unit) => (
                            <label
                              className="stocktake-count-field"
                              data-stocktake-unit-id={unit.id}
                              key={unit.id}
                            >
                              <input
                                aria-label={`${line.code} ${line.name} ${unit.unitName}`}
                                name={`counts.${line.productId}.${unit.id}`}
                                data-stocktake-count-input="true"
                                type="number"
                                min="0"
                                step="1"
                                disabled={busy || draftPaused}
                                value={counts[line.productId]?.[unit.id] ?? ""}
                                onChange={(event) => {
                                  draftEditRevisionRef.current += 1;
                                  setCounts((current) => ({
                                    ...current,
                                    [line.productId]: {
                                      ...current[line.productId],
                                      [unit.id]: event.target.value,
                                    },
                                  }));
                                  setDraftDirty(true);
                                }}
                              />
                              <span className="stocktake-count-unit">{unit.unitName}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <strong>{line.countedPackaging?.text ?? "-"}</strong>
                      )}
                    </td>
                    <td
                      className="stocktake-reconciliation-cell"
                      data-label={locale === "zh-CN" ? "对账结果" : "Reconciliation"}
                    >
                      <span className="stocktake-reconciliation-metric">
                        <span>{locale === "zh-CN" ? "待处理净变动" : "Pending net"}</span>
                        <strong
                          className={
                            pendingMilli < 0 ? "negative" : pendingMilli > 0 ? "stock-value" : ""
                          }
                        >
                          {pendingMilli > 0 ? "+" : ""}
                          {pendingMilli / 1000} {line.baseUnit}
                        </strong>
                      </span>
                      <span className="stocktake-reconciliation-metric">
                        <span>{locale === "zh-CN" ? "入账后应有" : "Expected"}</span>
                        <strong className={expectedMilli < 0 ? "negative" : ""}>
                          {line.expectedPackaging?.text ??
                            `${expectedMilli / 1000} ${line.baseUnit}`}
                        </strong>
                      </span>
                      <span className="stocktake-reconciliation-metric">
                        <span>{locale === "zh-CN" ? "剩余差异" : "Residual"}</span>
                        <div
                          className="stocktake-residual-wrap"
                          onMouseEnter={() => setResidualOpenProductId(line.productId)}
                          onMouseLeave={() => setResidualOpenProductId(undefined)}
                        >
                          <strong
                            data-stocktake-residual-value={remainingMilli / 1000}
                            className={remainingMilli === 0 ? "stock-value" : "negative"}
                            tabIndex={0}
                            role="button"
                            aria-expanded={residualOpenProductId === line.productId}
                            onFocus={() => setResidualOpenProductId(line.productId)}
                            onClick={() =>
                              setResidualOpenProductId((current) =>
                                current === line.productId ? undefined : line.productId,
                              )
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setResidualOpenProductId((current) =>
                                  current === line.productId ? undefined : line.productId,
                                );
                              }
                            }}
                          >
                            {remainingMilli > 0 ? "+" : ""}
                            {remainingMilli / 1000} {line.baseUnit}
                          </strong>
                          {residualOpenProductId === line.productId && (
                            <div className="stocktake-residual-popover" role="tooltip">
                              <strong>
                                {line.code} · {line.name}
                              </strong>
                              <span>
                                {locale === "zh-CN" ? "系统库存" : "System"}: {line.packaging?.text}
                              </span>
                              <span>
                                {locale === "zh-CN" ? "实盘数量" : "Physical"}:{" "}
                                {countedMilli(line) / 1000} {line.baseUnit}
                              </span>
                              <span>
                                {locale === "zh-CN" ? "待处理净变动" : "Pending net"}:{" "}
                                {pendingMilli > 0 ? "+" : ""}
                                {pendingMilli / 1000} {line.baseUnit}
                              </span>
                              <div className="stocktake-residual-documents">
                                {line.reconciliationDocuments?.length ? (
                                  line.reconciliationDocuments.map((document: any) => (
                                    <div
                                      className="stocktake-residual-document"
                                      key={`${document.pendingDocumentId}-${document.productId ?? line.productId}`}
                                    >
                                      <strong>{document.documentNo}</strong>
                                      <span>
                                        {documentTypeLabel(document.documentType)} ·{" "}
                                        {document.documentDate} · {document.partyName}
                                      </span>
                                      <span>
                                        {locale === "zh-CN" ? "数量" : "Qty"}: {document.quantity}{" "}
                                        {line.baseUnit}（{document.unitName}） ·{" "}
                                        {locale === "zh-CN" ? "库存变动" : "Stock"}:{" "}
                                        {document.inventoryDelta > 0 ? "+" : ""}
                                        {document.inventoryDelta} {line.baseUnit}
                                      </span>
                                      <small>{pendingStatusLabel(document)}</small>
                                    </div>
                                  ))
                                ) : (
                                  <span className="muted">
                                    {locale === "zh-CN"
                                      ? "尚无差异处理单据"
                                      : "No reconciliation document yet"}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </span>
                    </td>
                    <td data-label={locale === "zh-CN" ? "盘点说明" : "Count note"}>
                      {stocktake.status === "active" ? (
                        <input
                          aria-label={`${line.code} ${line.name} ${locale === "zh-CN" ? "盘点说明" : "count note"}`}
                          name={`reasons.${line.productId}`}
                          placeholder={locale === "zh-CN" ? "可选" : "Optional"}
                          disabled={busy || draftPaused}
                          value={reasons[line.productId] ?? ""}
                          onChange={(event) => {
                            draftEditRevisionRef.current += 1;
                            setReasons((current) => ({
                              ...current,
                              [line.productId]: event.target.value,
                            }));
                            setDraftDirty(true);
                          }}
                        />
                      ) : (
                        line.varianceReason || "-"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      {stocktake.status === "active" && (
        <div className="stocktake-save-band">
          <div className="stocktake-save-statuses" aria-live="polite">
            <div className="stocktake-confirm-state">
              {countsMustBeSaved ? (
                <>
                  <CircleAlert size={16} />
                  <span>
                    {locale === "zh-CN"
                      ? "确认保存后，才可在下方维护差异处理单据。"
                      : "Save the counts before editing reconciliation documents below."}
                  </span>
                </>
              ) : (
                <>
                  <Check size={16} />
                  <span>
                    {locale === "zh-CN" ? "盘点数量已保存。" : "Physical counts are saved."}
                  </span>
                </>
              )}
            </div>
            <span
              className={`stocktake-draft-status ${draftStatus === "error" ? "error-text" : ""}`}
            >
              {draftStatus === "saving"
                ? locale === "zh-CN"
                  ? "草稿保存中..."
                  : "Saving draft..."
                : draftStatus === "error"
                  ? locale === "zh-CN"
                    ? "草稿未保存，请重试"
                    : "Draft not saved; retry"
                  : locale === "zh-CN"
                    ? "草稿已保存"
                    : "Draft saved"}
            </span>
          </div>
          <div className="action-row stocktake-save-actions">
            <button
              className="secondary-button"
              disabled={busy || draftStatus === "saving" || !draftDirty}
              onClick={() => void persistDraft(true)}
            >
              <FilePenLine size={15} />
              {locale === "zh-CN" ? "保存盘点草稿" : "Save draft"}
            </button>
            <button
              className="secondary-button"
              disabled={busy || !countsMustBeSaved}
              onClick={() => void saveCounts()}
            >
              <Save size={15} />
              {locale === "zh-CN" ? "确认保存数量" : "Save counts"}
            </button>
          </div>
        </div>
      )}
      {pendingSection}
      {stocktake.status === "active" && (
        <div className="stocktake-action-band stocktake-final-action-band">
          <div className="stocktake-confirm-state" aria-live="polite">
            {hasUnsavedChanges || incompleteCount > 0 ? (
              <>
                <CircleAlert size={16} />
                <span>
                  {locale === "zh-CN" ? "先保存全部实盘数量。" : "Save all physical counts first."}
                </span>
              </>
            ) : unresolvedCount > 0 ? (
              <>
                <CircleAlert size={16} />
                <span>
                  {locale === "zh-CN"
                    ? `还有 ${unresolvedCount} 个商品的差异未由业务单据解释。`
                    : `${unresolvedCount} products still have unexplained differences.`}
                </span>
              </>
            ) : (
              <>
                <Check size={16} />
                <span>
                  {locale === "zh-CN"
                    ? "所有差异已闭环，可以最终确认。"
                    : "All differences are reconciled and ready to confirm."}
                </span>
              </>
            )}
          </div>
          <div className="action-row stocktake-actions">
            <button
              className="danger-button"
              disabled={busy}
              onClick={() => setPendingAction("cancel")}
            >
              <X size={15} />
              {locale === "zh-CN" ? "取消盘点" : "Cancel"}
            </button>
            <button
              className="primary-button"
              disabled={confirmDisabled}
              title={
                confirmDisabled
                  ? locale === "zh-CN"
                    ? "需先保存数量并把剩余差异处理为零"
                    : "Save counts and resolve all residuals first"
                  : undefined
              }
              onClick={() => setPendingAction("confirm")}
            >
              <Check size={16} />
              {locale === "zh-CN" ? "确认完成盘点" : "Confirm stocktake"}
            </button>
          </div>
        </div>
      )}
      <section className="stocktake-artifacts">
        <div className="section-heading-row">
          <h3>{locale === "zh-CN" ? "盘点文件" : "Stocktake files"}</h3>
          <span className="count-tag">{artifacts.length}</span>
        </div>
        <div className="artifact-list">
          {artifacts.map((artifact) => (
            <button
              className="secondary-button"
              type="button"
              key={artifact.id}
              onClick={() =>
                window.open(
                  `/api/v1/inventory/stocktake-artifacts/${artifact.id}`,
                  "_blank",
                  "noopener",
                )
              }
            >
              {artifact.format === "html" ? <Printer size={14} /> : <FileSpreadsheet size={14} />}
              {artifact.fileName}
            </button>
          ))}
        </div>
      </section>
      <ReasonConfirmDialog
        open={Boolean(pendingAction)}
        locale={locale}
        busy={busy}
        title={
          pendingAction === "cancel"
            ? locale === "zh-CN"
              ? "确认取消盘点"
              : "Confirm stocktake cancellation"
            : locale === "zh-CN"
              ? "确认完成盘点"
              : "Confirm stocktake"
        }
        description={
          pendingAction === "cancel"
            ? locale === "zh-CN"
              ? `取消后不会调整库存；${openPendingDocuments.length} 张待处理单据会转为普通草稿。`
              : `Cancelling will not adjust inventory; ${openPendingDocuments.length} pending documents will become drafts.`
            : locale === "zh-CN"
              ? `确认后将原子入账 ${openPendingDocuments.length} 张待处理单据，同时更新库存、应收应付和资金流水；不会生成盘点调整记录。`
              : `This atomically posts ${openPendingDocuments.length} pending documents and updates inventory and finance without a stocktake adjustment.`
        }
        confirmLabel={locale === "zh-CN" ? "确认执行" : "Confirm"}
        danger={pendingAction === "cancel"}
        onCancel={() => !busy && setPendingAction(undefined)}
        onConfirm={(reason) => void executeAction(reason)}
      />
      {returnSourceType && (
        <StocktakeReturnSourceDialog
          type={returnSourceType}
          locale={locale}
          notify={notify}
          excludedDocumentIds={excludedOriginalDocumentIds}
          onClose={() => setReturnSourceType(undefined)}
          onSelect={(document) => createPendingReturn(returnSourceType, document)}
        />
      )}
      {orderDialog && (
        <StocktakeOrderDialog
          type={orderDialog.type}
          seed={orderDialog.seed}
          locale={locale}
          notify={notify}
          onClose={() => setOrderDialog(undefined)}
          onCompleted={completePendingDocument}
        />
      )}
    </section>
  );
}

function InventoryMovementDrawer({ item, locale, notify, onClose }: any) {
  const [movements, setMovements] = useState<any[]>();
  useEffect(() => {
    api<{ items: any[] }>(`/inventory/${item.id}/movements`)
      .then((data) => setMovements(data.items))
      .catch((error) => {
        notify(error.message, "error");
        setMovements([]);
      });
  }, [item.id, notify]);
  const movementLabel = (movementType: string) => {
    const labels: Record<string, [string, string]> = {
      opening: ["期初", "Opening"],
      sale: ["销售出库", "Sale"],
      purchase: ["进货入库", "Purchase"],
      sale_return: ["销售退货", "Sale return"],
      purchase_return: ["进货退货", "Purchase return"],
      void: ["作废冲回", "Void reversal"],
      revision: ["改单调整", "Revision"],
      stocktake_adjustment: ["盘点调整", "Stocktake adjustment"],
    };
    return labels[movementType]?.[locale === "zh-CN" ? 0 : 1] ?? movementType;
  };
  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="drawer inventory-drawer" onClick={(event) => event.stopPropagation()}>
        <div className="drawer-heading">
          <div>
            <div className="eyebrow">{item.code}</div>
            <h2>{item.name}</h2>
            <p className="muted">
              {locale === "zh-CN" ? "当前库存" : "Current balance"}: {item.packaging?.text}
              {" · "}
              {item.quantity} {item.baseUnit}
            </p>
          </div>
          <button
            className="icon-button"
            type="button"
            title={locale === "zh-CN" ? "关闭" : "Close"}
            aria-label={locale === "zh-CN" ? "关闭" : "Close"}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>
        {movements ? (
          <div className="movement-list">
            {movements.map((movement) => (
              <div className="movement-row" key={movement.id}>
                <div>
                  <strong>{movementLabel(movement.movementType)}</strong>
                  <span>{formatDateTimeInTimeZone(movement.createdAt, locale)}</span>
                </div>
                <div>
                  <strong
                    className={`movement-delta ${Number(movement.quantity) >= 0 ? "positive" : "negative"}`}
                  >
                    {Number(movement.quantity) > 0 ? "+" : ""}
                    {movement.quantity} {item.baseUnit}
                  </strong>
                  <span>{movement.documentNo || movement.periodKey}</span>
                </div>
              </div>
            ))}
            {!movements.length && <div className="empty-cell">{tr(locale, "empty")}</div>}
          </div>
        ) : (
          <div className="loading-line">
            <RefreshCw className="spin" size={17} />
            Loading
          </div>
        )}
      </aside>
    </div>
  );
}

function PartyEditor({
  kind,
  party,
  locale,
  onSaved,
  notify,
}: {
  kind: "customers" | "suppliers";
  party?: Party;
  locale: Locale;
  onSaved: () => void;
  notify: (message: string, kind?: "success" | "error") => void;
}) {
  const fieldPrefix = `${kind}-${party?.id ?? "new"}`;
  const [form, setForm] = useState({
    name: party?.name ?? "",
    contactName: party?.contactName ?? "",
    phone: party?.phone ?? "",
    address: party?.address ?? "",
    notes: party?.notes ?? "",
    active: party?.active === undefined ? true : Boolean(party.active),
  });
  const save = async () => {
    if (!form.name.trim()) {
      notify(locale === "zh-CN" ? "名称不能为空" : "Name is required", "error");
      return;
    }
    if (
      !window.confirm(
        locale === "zh-CN" ? "确认保存这条基础资料？" : "Save this master-data record?",
      )
    )
      return;
    try {
      await api(
        `/masters/${kind}${party ? `/${party.id}` : ""}`,
        {
          method: party ? "PUT" : "POST",
          body: JSON.stringify(form),
        },
        locale === "zh-CN" ? "基础资料保存失败" : "Master-data save failed",
      );
      notify(locale === "zh-CN" ? "基础资料已保存" : "Master data saved");
      onSaved();
    } catch (error) {
      notify(error instanceof Error ? error.message : "保存失败", "error");
    }
  };
  return (
    <section className="form-panel master-editor">
      <h2>
        {party ? (locale === "zh-CN" ? "编辑" : "Edit") : locale === "zh-CN" ? "新增" : "New"}{" "}
        {kind === "customers"
          ? locale === "zh-CN"
            ? "客户"
            : "customer"
          : locale === "zh-CN"
            ? "供应商"
            : "supplier"}
      </h2>
      <div className="two-column-form">
        <label>
          {locale === "zh-CN" ? "名称" : "Name"}
          <input
            id={`${fieldPrefix}-name`}
            name="name"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </label>
        <label>
          {locale === "zh-CN" ? "联系人" : "Contact"}
          <input
            id={`${fieldPrefix}-contact-name`}
            name="contactName"
            value={form.contactName}
            onChange={(event) => setForm({ ...form, contactName: event.target.value })}
          />
        </label>
        <label>
          {locale === "zh-CN" ? "联系方式" : "Phone"}
          <input
            id={`${fieldPrefix}-phone`}
            name="phone"
            value={form.phone}
            onChange={(event) => setForm({ ...form, phone: event.target.value })}
          />
        </label>
        <label>
          {locale === "zh-CN" ? "地址" : "Address"}
          <input
            id={`${fieldPrefix}-address`}
            name="address"
            value={form.address}
            onChange={(event) => setForm({ ...form, address: event.target.value })}
          />
        </label>
      </div>
      <label>
        {locale === "zh-CN" ? "备注" : "Notes"}
        <input
          id={`${fieldPrefix}-notes`}
          name="notes"
          value={form.notes}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
        />
      </label>
      <label className="check-label">
        <input
          id={`${fieldPrefix}-active`}
          name="active"
          type="checkbox"
          checked={form.active}
          onChange={(event) => setForm({ ...form, active: event.target.checked })}
        />
        {locale === "zh-CN" ? "启用" : "Active"}
      </label>
      <button className="primary-button" onClick={save}>
        <Save size={16} />
        {locale === "zh-CN" ? "确认保存" : "Save"}
      </button>
    </section>
  );
}

function ProductEditor({
  product,
  suppliers,
  locale,
  onSaved,
  notify,
}: {
  product?: Product;
  suppliers: Party[];
  locale: Locale;
  onSaved: () => void;
  notify: (message: string, kind?: "success" | "error") => void;
}) {
  const fieldPrefix = `product-${product?.id ?? "new"}`;
  type ProductForm = {
    code: string;
    name: string;
    alias: string;
    defaultSupplierId: string | null;
    baseUnit: string;
    notes: string;
    active: boolean;
    maintenanceStatus: string;
    sourceCode: string;
    units: EditableUnit[];
  };
  const initialUnits = (): EditableUnit[] => {
    if (product?.units?.length) {
      const source = product.units.map((unit, index) => ({
        ...unit,
        id: unit.id ?? crypto.randomUUID(),
        factor: Number(unit.factor ?? 0),
        parentUnitId: unit.parentUnitId ?? null,
        unitsPerParent:
          unit.unitsPerParent === null || unit.unitsPerParent === undefined
            ? null
            : Number(unit.unitsPerParent),
        salePriceKhr: Number(unit.salePriceKhr ?? 0),
        purchasePriceKhr: Number(unit.purchasePriceKhr ?? 0),
        active: Boolean(unit.active),
        sortOrder: index + 1,
      }));
      // Older API responses may not contain the new relation fields yet. Keep
      // those products editable by inferring the same nearest divisor locally.
      return source.map((unit, index) => {
        if (index === 0) {
          return {
            ...unit,
            unitName: product.baseUnit,
            factor: 1,
            parentUnitId: null,
            unitsPerParent: null,
          };
        }
        if (unit.parentUnitId && unit.unitsPerParent) return unit;
        const parent = source
          .slice(0, index)
          .filter((candidate) => candidate.factor > 0 && unit.factor > candidate.factor)
          .filter((candidate) => unit.factor % candidate.factor === 0)
          .sort((left, right) => right.factor - left.factor)[0];
        return {
          ...unit,
          parentUnitId: parent?.id ?? null,
          unitsPerParent: parent ? unit.factor / parent.factor : null,
        };
      });
    }
    return [
      {
        id: crypto.randomUUID(),
        unitName: "",
        factor: 1,
        parentUnitId: null,
        unitsPerParent: null,
        salePriceKhr: 0,
        purchasePriceKhr: 0,
        active: true,
        sortOrder: 1,
      },
    ];
  };
  const [form, setForm] = useState<ProductForm>(() => ({
    code: product?.code ?? "",
    name: product?.name ?? "",
    alias: product?.alias ?? "",
    defaultSupplierId: product?.defaultSupplierId ?? null,
    baseUnit: product?.baseUnit ?? "",
    notes: product?.notes ?? "",
    active: product?.active === undefined ? true : Boolean(product.active),
    maintenanceStatus: product?.maintenanceStatus ?? "verified",
    sourceCode: product?.sourceCode ?? "",
    units: initialUnits(),
  }));
  const [pendingConversionSave, setPendingConversionSave] = useState<{
    units: EditableUnit[];
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const recalculateUnits = (units: EditableUnit[]) => {
    const next = units.map((unit, index) => {
      if (index === 0) {
        return { ...unit, factor: 1, parentUnitId: null, unitsPerParent: null, sortOrder: 1 };
      }
      const parent = units.find(
        (candidate, candidateIndex) => candidateIndex < index && candidate.id === unit.parentUnitId,
      );
      const ratio = Number(unit.unitsPerParent ?? 0);
      return {
        ...unit,
        factor: parent && Number.isInteger(ratio) && ratio >= 2 ? parent.factor * ratio : 0,
        sortOrder: index + 1,
      };
    });
    return next;
  };

  const setUnit = (index: number, patch: Partial<EditableUnit>) =>
    setForm((current) => ({
      ...current,
      units: recalculateUnits(
        current.units.map((unit, unitIndex) =>
          unitIndex === index ? { ...unit, ...patch } : unit,
        ),
      ),
    }));

  const addUnit = () =>
    setForm((current) => ({
      ...current,
      units: [
        ...current.units,
        {
          id: crypto.randomUUID(),
          unitName: "",
          factor: 0,
          parentUnitId: null,
          unitsPerParent: null,
          salePriceKhr: 0,
          purchasePriceKhr: 0,
          active: true,
          sortOrder: current.units.length + 1,
        },
      ],
    }));

  const deleteUnit = (index: number) => {
    if (index === 0) {
      notify(
        locale === "zh-CN" ? "最小单位不能删除。" : "The smallest unit cannot be deleted.",
        "error",
      );
      return;
    }
    const unit = form.units[index];
    if (
      unit?.id &&
      form.units.some(
        (candidate, candidateIndex) => candidateIndex > index && candidate.parentUnitId === unit.id,
      )
    ) {
      notify(
        locale === "zh-CN"
          ? "该单位已经被后面的单位引用，请先修改子单位的父单位。"
          : "This unit is used by a later child unit. Change the child parent first.",
        "error",
      );
      return;
    }
    setForm((current) => ({
      ...current,
      units: recalculateUnits(current.units.filter((_, unitIndex) => unitIndex !== index)),
    }));
  };

  const setBaseUnit = (value: string) =>
    setForm((current) => ({
      ...current,
      baseUnit: value,
      units: current.units.map((unit, index) =>
        index === 0
          ? { ...unit, unitName: value, factor: 1, parentUnitId: null, unitsPerParent: null }
          : unit,
      ),
    }));

  const buildCleanUnits = () => {
    if (form.units.some((unit, index) => !unit.unitName.trim() && index < form.units.length - 1)) {
      notify(
        locale === "zh-CN"
          ? "请先填写或删除中间的空白单位行。"
          : "Fill or remove blank unit rows before continuing.",
        "error",
      );
      return null;
    }
    const cleanUnits = form.units
      .filter((unit) => unit.unitName.trim())
      .map((unit, index) => ({
        ...unit,
        unitName: unit.unitName.trim(),
        factor: Number(unit.factor),
        unitsPerParent:
          index === 0 || unit.unitsPerParent === null ? null : Number(unit.unitsPerParent),
        salePriceKhr: Math.round(Number(unit.salePriceKhr)),
        purchasePriceKhr: Math.round(Number(unit.purchasePriceKhr)),
        sortOrder: index + 1,
      }));
    if (!form.code.trim() || !form.name.trim() || !form.baseUnit.trim() || !cleanUnits.length) {
      notify(
        locale === "zh-CN"
          ? "请填写编码、品名、基本单位和单位换算。"
          : "Code, name, base unit and units are required.",
        "error",
      );
      return null;
    }
    try {
      return resolveUnitHierarchy(cleanUnits, form.baseUnit.trim());
    } catch (error) {
      notify(
        error instanceof UnitHierarchyError
          ? error.message
          : locale === "zh-CN"
            ? "单位换算无效。"
            : "Unit conversion is invalid.",
        "error",
      );
      return null;
    }
  };

  const performSave = async (cleanUnits: EditableUnit[], conversionChangeReason = "") => {
    setSaving(true);
    try {
      await api(
        `/masters/products${product ? `/${product.id}` : ""}`,
        {
          method: product ? "PUT" : "POST",
          body: JSON.stringify({
            code: form.code.trim(),
            name: form.name.trim(),
            alias: form.alias,
            defaultSupplierId: form.defaultSupplierId,
            baseUnit: form.baseUnit.trim(),
            notes: form.notes,
            active: form.active,
            maintenanceStatus: form.maintenanceStatus,
            sourceCode: form.sourceCode,
            conversionChangeReason,
            units: cleanUnits,
          }),
        },
        locale === "zh-CN" ? "产品保存失败" : "Product save failed",
      );
      notify(locale === "zh-CN" ? "产品已保存" : "Product saved");
      onSaved();
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.code === "UNIT_CONVERSION_REASON_REQUIRED" &&
        !conversionChangeReason
      ) {
        setPendingConversionSave({ units: cleanUnits });
      } else {
        notify(error instanceof Error ? error.message : "保存失败", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    const cleanUnits = buildCleanUnits();
    if (!cleanUnits) return;
    if (
      !window.confirm(
        locale === "zh-CN"
          ? "确认保存产品和单位换算？"
          : "Save this product and its unit conversions?",
      )
    )
      return;
    if (product && unitConversionChanged(product.units, cleanUnits)) {
      setPendingConversionSave({ units: cleanUnits });
      return;
    }
    void performSave(cleanUnits);
  };
  return (
    <section className="form-panel master-editor product-editor">
      <h2>
        {product
          ? locale === "zh-CN"
            ? "编辑产品"
            : "Edit product"
          : locale === "zh-CN"
            ? "新增产品"
            : "New product"}
      </h2>
      <div className="two-column-form">
        <label>
          {locale === "zh-CN" ? "产品编码" : "Code"}
          <input
            id={`${fieldPrefix}-code`}
            name="code"
            value={form.code}
            onChange={(event) => setForm({ ...form, code: event.target.value })}
          />
        </label>
        <label>
          {locale === "zh-CN" ? "品名" : "Name"}
          <input
            id={`${fieldPrefix}-name`}
            name="name"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </label>
        <label>
          {locale === "zh-CN" ? "别名" : "Alias"}
          <input
            id={`${fieldPrefix}-alias`}
            name="alias"
            value={form.alias}
            onChange={(event) => setForm({ ...form, alias: event.target.value })}
          />
        </label>
        <label>
          {locale === "zh-CN" ? "供应商" : "Supplier"}
          <SearchableSelect
            id={`${fieldPrefix}-supplier`}
            name="defaultSupplierId"
            ariaLabel={locale === "zh-CN" ? "供应商" : "Supplier"}
            value={form.defaultSupplierId ?? ""}
            onChange={(supplierId) => setForm({ ...form, defaultSupplierId: supplierId || null })}
            placeholder="--"
            searchPlaceholder={locale === "zh-CN" ? "搜索供应商" : "Search suppliers"}
            emptyText={locale === "zh-CN" ? "没有匹配供应商" : "No matching suppliers"}
            options={suppliers.map((supplier) => ({
              value: supplier.id,
              label: supplier.name,
              keywords: [supplier.contactName, supplier.phone, supplier.address, supplier.notes]
                .filter(Boolean)
                .join(" "),
            }))}
          />
        </label>
        <label>
          {locale === "zh-CN" ? "最小/基本单位" : "Base unit"}
          <input
            id={`${fieldPrefix}-base-unit`}
            name="baseUnit"
            value={form.baseUnit}
            onChange={(event) => setBaseUnit(event.target.value)}
          />
        </label>
        <label>
          {locale === "zh-CN" ? "备注" : "Notes"}
          <input
            id={`${fieldPrefix}-notes`}
            name="notes"
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
          />
        </label>
      </div>
      <div className="unit-editor">
        <div className="panel-header">
          <h2>{locale === "zh-CN" ? "单位换算与价格" : "Units and prices"}</h2>
          <button className="secondary-button" type="button" onClick={addUnit}>
            <Plus size={15} />
            {locale === "zh-CN" ? "增加单位" : "Add unit"}
          </button>
        </div>
        <div className="unit-row unit-row-heading" aria-hidden="true">
          <span>{locale === "zh-CN" ? "新单位" : "New unit"}</span>
          <span>{locale === "zh-CN" ? "数字" : "Quantity"}</span>
          <span>{locale === "zh-CN" ? "已有单位" : "Parent unit"}</span>
          <span>{locale === "zh-CN" ? "累计换算" : "Total factor"}</span>
          <span>{locale === "zh-CN" ? "销售默认价" : "Sale price"}</span>
          <span>{locale === "zh-CN" ? "进货默认价" : "Purchase price"}</span>
          <span />
        </div>
        {form.units.map((unit, index) => (
          <div className="unit-row" key={unit.id ?? index}>
            <input
              id={`${fieldPrefix}-unit-${index}-name`}
              name={`units.${index}.unitName`}
              aria-label="Unit name"
              placeholder={locale === "zh-CN" ? "单位，如 箱/小包/瓶" : "Unit"}
              value={unit.unitName}
              readOnly={index === 0}
              onChange={(event) => setUnit(index, { unitName: event.target.value })}
            />
            <input
              id={`${fieldPrefix}-unit-${index}-ratio`}
              name={`units.${index}.unitsPerParent`}
              aria-label="Quantity per parent unit"
              type="number"
              min="2"
              step="1"
              disabled={index === 0}
              placeholder={index === 0 ? "-" : locale === "zh-CN" ? "例如 12" : "e.g. 12"}
              value={index === 0 ? "" : unit.unitsPerParent || ""}
              onChange={(event) =>
                setUnit(index, {
                  unitsPerParent: event.target.value === "" ? null : Number(event.target.value),
                })
              }
            />
            <SearchableSelect
              id={`${fieldPrefix}-unit-${index}-parent`}
              name={`units.${index}.parentUnitId`}
              ariaLabel={locale === "zh-CN" ? "已有单位" : "Parent unit"}
              value={index === 0 ? "" : (unit.parentUnitId ?? "")}
              disabled={index === 0}
              placeholder={
                index === 0 ? "-" : locale === "zh-CN" ? "选择已有单位" : "Select parent"
              }
              searchPlaceholder={locale === "zh-CN" ? "搜索已有单位" : "Search parent units"}
              emptyText={locale === "zh-CN" ? "没有可用单位" : "No available units"}
              onChange={(parentUnitId) => setUnit(index, { parentUnitId: parentUnitId || null })}
              options={form.units
                .slice(0, index)
                .filter((candidate) => candidate.unitName.trim() && candidate.factor > 0)
                .map((candidate) => ({
                  value: candidate.id ?? "",
                  label: `${candidate.unitName}（${candidate.factor} ${form.baseUnit || ""}）`,
                  keywords: String(candidate.factor),
                }))}
            />
            <input
              id={`${fieldPrefix}-unit-${index}-factor`}
              name={`units.${index}.factor`}
              aria-label="Total factor"
              type="number"
              value={unit.factor || ""}
              readOnly
              placeholder="-"
            />
            <input
              id={`${fieldPrefix}-unit-${index}-sale-price`}
              name={`units.${index}.salePriceKhr`}
              aria-label="Sale price"
              type="number"
              min="0"
              step="1"
              value={unit.salePriceKhr}
              onChange={(event) => setUnit(index, { salePriceKhr: Number(event.target.value) })}
            />
            <input
              id={`${fieldPrefix}-unit-${index}-purchase-price`}
              name={`units.${index}.purchasePriceKhr`}
              aria-label="Purchase price"
              type="number"
              min="0"
              step="1"
              value={unit.purchasePriceKhr}
              onChange={(event) => setUnit(index, { purchasePriceKhr: Number(event.target.value) })}
            />
            <button
              className="icon-button danger"
              type="button"
              title={tr(locale, "delete")}
              disabled={index === 0}
              onClick={() => deleteUnit(index)}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
      <label className="check-label">
        <input
          id={`${fieldPrefix}-active`}
          name="active"
          type="checkbox"
          checked={form.active}
          onChange={(event) => setForm({ ...form, active: event.target.checked })}
        />
        {locale === "zh-CN" ? "启用" : "Active"}
      </label>
      <button className="primary-button" type="button" disabled={saving} onClick={save}>
        <Save size={16} />
        {saving ? "..." : locale === "zh-CN" ? "确认保存" : "Save"}
      </button>
      <ReasonConfirmDialog
        open={Boolean(pendingConversionSave)}
        locale={locale}
        busy={saving}
        title={locale === "zh-CN" ? "确认修改单位换算" : "Confirm unit conversion change"}
        description={
          locale === "zh-CN"
            ? "该产品可能已有单据或库存流水。确认后只影响以后新开的单据；历史单据、库存流水和历史打印不会自动重算。请填写修改原因。"
            : "This product may have documents or stock movements. The change affects future documents only; historical documents, stock movements and print snapshots are not recalculated. Enter a reason."
        }
        confirmLabel={locale === "zh-CN" ? "确认修改" : "Confirm change"}
        onCancel={() => !saving && setPendingConversionSave(null)}
        onConfirm={(reason) => {
          const pending = pendingConversionSave;
          if (pending) void performSave(pending.units, reason);
        }}
      />
    </section>
  );
}

function MastersPage({
  locale,
  notify,
}: {
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<Party[]>([]);
  const [suppliers, setSuppliers] = useState<Party[]>([]);
  const [tab, setTab] = useState<"products" | "customers" | "suppliers">("products");
  const [selectedId, setSelectedId] = useState<string>();
  const [editorVersion, setEditorVersion] = useState(0);
  const load = () =>
    Promise.all([
      api<{ items: Product[] }>("/masters/products"),
      api<{ items: Party[] }>("/masters/customers"),
      api<{ items: Party[] }>("/masters/suppliers"),
    ])
      .then(([p, c, s]) => {
        setProducts(p.items);
        setCustomers(c.items);
        setSuppliers(s.items);
      })
      .catch((error) => notify(error.message, "error"));
  useEffect(() => {
    Promise.all([
      api<{ items: Product[] }>("/masters/products"),
      api<{ items: Party[] }>("/masters/customers"),
      api<{ items: Party[] }>("/masters/suppliers"),
    ])
      .then(([p, c, s]) => {
        setProducts(p.items);
        setCustomers(c.items);
        setSuppliers(s.items);
      })
      .catch((error) => notify(error.message, "error"));
  }, [notify]);
  const changeTab = (next: typeof tab) => {
    setTab(next);
    setSelectedId(undefined);
    setEditorVersion((current) => current + 1);
  };
  const items = tab === "products" ? products : tab === "customers" ? customers : suppliers;
  const saved = async () => {
    setSelectedId(undefined);
    setEditorVersion((current) => current + 1);
    await load();
  };
  const newEntry = () => {
    setSelectedId(undefined);
    setEditorVersion((current) => current + 1);
  };
  return (
    <div className="page-stack">
      <div className="page-heading">
        <div>
          <div className="eyebrow">REFERENCE DATA</div>
          <h1>{tr(locale, "masters")}</h1>
          <p className="muted">
            {locale === "zh-CN"
              ? "在一个页面维护产品、包装换算、供应商和客户联系方式。"
              : "Maintain products, package conversions, suppliers and customers in one place."}
          </p>
        </div>
        <button className="secondary-button" onClick={load}>
          <RefreshCw size={16} />
          {tr(locale, "refresh")}
        </button>
      </div>
      <div className="segmented-control">
        <button
          className={tab === "products" ? "active" : ""}
          onClick={() => changeTab("products")}
        >
          Products · 产品
        </button>
        <button
          className={tab === "customers" ? "active" : ""}
          onClick={() => changeTab("customers")}
        >
          Customers · 客户
        </button>
        <button
          className={tab === "suppliers" ? "active" : ""}
          onClick={() => changeTab("suppliers")}
        >
          Suppliers · 供应商
        </button>
      </div>
      <div className="master-workspace">
        <section className="table-panel">
          <div className="panel-header">
            <h2>{tab}</h2>
            <button className="secondary-button" onClick={newEntry}>
              <Plus size={15} />
              {locale === "zh-CN" ? "新增" : "New"}
            </button>
          </div>
          <table className="data-table compact">
            <thead>
              <tr>
                <th>{tab === "products" ? "Code" : "Name"}</th>
                <th>{tab === "products" ? "Name / Units" : "Contact"}</th>
                <th>{tr(locale, "status")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((entry: any) => (
                <tr
                  key={entry.id}
                  className={selectedId === entry.id ? "selected-row" : ""}
                  onClick={() => setSelectedId(entry.id)}
                >
                  <td>
                    <strong>{tab === "products" ? entry.code : entry.name}</strong>
                  </td>
                  <td>
                    {tab === "products" ? (
                      <>
                        {entry.name}
                        <small>
                          {entry.units
                            .filter((unit: Unit) => unit.active)
                            .map((unit: Unit) => {
                              const parent = entry.units.find(
                                (candidate: Unit) => candidate.id === unit.parentUnitId,
                              );
                              return unit.parentUnitId && parent
                                ? `1 ${unit.unitName} = ${unit.unitsPerParent ?? "?"} ${parent.unitName}`
                                : `1 ${unit.unitName}`;
                            })
                            .join(" · ")}
                        </small>
                      </>
                    ) : (
                      <>
                        {entry.contactName || "-"}
                        <small>{entry.phone || entry.notes || ""}</small>
                      </>
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-pill ${entry.active ? "success-pill" : "danger-pill"}`}
                    >
                      {entry.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr>
                  <td className="empty-cell" colSpan={3}>
                    {tr(locale, "empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
        {tab === "products" ? (
          <ProductEditor
            key={`${selectedId ?? "new-product"}-${editorVersion}`}
            product={products.find((product) => product.id === selectedId)}
            suppliers={suppliers}
            locale={locale}
            onSaved={saved}
            notify={notify}
          />
        ) : (
          <PartyEditor
            key={`${tab}-${selectedId ?? "new"}-${editorVersion}`}
            kind={tab}
            party={(tab === "customers" ? customers : suppliers).find(
              (party) => party.id === selectedId,
            )}
            locale={locale}
            onSaved={saved}
            notify={notify}
          />
        )}
      </div>
    </div>
  );
}

function formatByteSize(value: number | undefined) {
  if (value === undefined || !Number.isFinite(value)) return "-";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
}

function OperationsPage({
  locale,
  notify,
}: {
  locale: Locale;
  notify: (message: string, kind?: "success" | "error") => void;
}) {
  type RankingItem = { key: string; count: number };
  const [data, setData] = useState<any>();
  const [days, setDays] = useState("30");
  const [severity, setSeverity] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [path, setPath] = useState("");
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ days, severity, errorCode, path, limit: "100" });
    try {
      setData(await api<any>(`/operations/logs?${params.toString()}`));
    } catch (error) {
      notify(error instanceof Error ? error.message : "操作日志读取失败", "error");
    } finally {
      setLoading(false);
    }
  }, [days, errorCode, notify, path, severity]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const rankingRows = (items: RankingItem[] | undefined) =>
    (items ?? []).map((item) => (
      <tr key={item.key}>
        <td>{item.key}</td>
        <td className="numeric">{item.count}</td>
      </tr>
    ));
  const failureMessage = (failure: any) =>
    [failure.message, failure.errorCode, failure.statusCode ? `HTTP ${failure.statusCode}` : ""]
      .filter(Boolean)
      .join(" · ") || "-";
  const rankingCards: Array<{ title: string; rows: RankingItem[] | undefined }> = [
    {
      title: locale === "zh-CN" ? "错误码排行" : "Error codes",
      rows: data?.rankings?.errorCodes,
    },
    {
      title: locale === "zh-CN" ? "接口排行" : "Paths",
      rows: data?.rankings?.paths,
    },
    {
      title: locale === "zh-CN" ? "字段排行" : "Fields",
      rows: data?.rankings?.fields,
    },
  ];
  return (
    <div className="page-stack">
      <div className="page-heading">
        <div>
          <div className="eyebrow">AUDIT</div>
          <h1>{tr(locale, "operations")}</h1>
          <p className="muted">
            {locale === "zh-CN"
              ? "查看业务失败、字段排行和最近成功操作。"
              : "Review rejected requests, field rankings and recent successful operations."}
          </p>
        </div>
        <button
          className="secondary-button"
          type="button"
          disabled={loading}
          onClick={() => void load()}
        >
          <RefreshCw className={loading ? "spin" : ""} size={16} />
          {tr(locale, "refresh")}
        </button>
      </div>
      <div className="toolbar operations-toolbar">
        <label>
          {locale === "zh-CN" ? "时间范围" : "Period"}
          <select value={days} onChange={(event) => setDays(event.target.value)}>
            <option value="7">7 {locale === "zh-CN" ? "天" : "days"}</option>
            <option value="30">30 {locale === "zh-CN" ? "天" : "days"}</option>
            <option value="90">90 {locale === "zh-CN" ? "天" : "days"}</option>
            <option value="365">365 {locale === "zh-CN" ? "天" : "days"}</option>
          </select>
        </label>
        <label>
          {locale === "zh-CN" ? "严重级别" : "Severity"}
          <select value={severity} onChange={(event) => setSeverity(event.target.value)}>
            <option value="">{locale === "zh-CN" ? "全部" : "All"}</option>
            <option value="warning">warning</option>
            <option value="error">error</option>
          </select>
        </label>
        <label>
          {locale === "zh-CN" ? "错误码" : "Error code"}
          <input value={errorCode} onChange={(event) => setErrorCode(event.target.value)} />
        </label>
        <label>
          {locale === "zh-CN" ? "接口路径" : "Path"}
          <input value={path} onChange={(event) => setPath(event.target.value)} />
        </label>
        <button
          className="secondary-button operations-filter-button"
          type="button"
          onClick={() => void load()}
        >
          <PackageSearch size={15} />
          {locale === "zh-CN" ? "筛选" : "Filter"}
        </button>
      </div>
      <div className="summary-strip operations-summary">
        <div>
          {locale === "zh-CN" ? "错误总数" : "Rejected requests"}
          <strong>{Number(data?.summary?.errorCount ?? 0)}</strong>
        </div>
        <div>
          {locale === "zh-CN" ? "未捕获异常" : "Unhandled exceptions"}
          <strong
            className={Number(data?.summary?.unhandledCount ?? 0) ? "negative" : "stock-value"}
          >
            {Number(data?.summary?.unhandledCount ?? 0)}
          </strong>
        </div>
        <div>
          {locale === "zh-CN" ? "查询起点" : "Since"}
          <strong className="operations-summary-date">
            {formatDateTimeInTimeZone(data?.filters?.startAt, locale)}
          </strong>
        </div>
      </div>
      <div className="operations-ranking-grid">
        {rankingCards.map(({ title, rows }) => (
          <section className="table-panel" key={title}>
            <div className="panel-header">
              <h2>{title}</h2>
            </div>
            <table className="data-table compact">
              <thead>
                <tr>
                  <th>{locale === "zh-CN" ? "项目" : "Item"}</th>
                  <th>{locale === "zh-CN" ? "次数" : "Count"}</th>
                </tr>
              </thead>
              <tbody>{rankingRows(rows)}</tbody>
            </table>
            {!rows?.length && <div className="empty-cell">{tr(locale, "empty")}</div>}
          </section>
        ))}
      </div>
      <section className="table-panel operations-log-panel">
        <div className="panel-header">
          <div>
            <h2>{locale === "zh-CN" ? "最近失败" : "Recent failures"}</h2>
            <span className="muted">{data?.recentFailures?.length ?? 0}</span>
          </div>
        </div>
        <table className="data-table compact">
          <thead>
            <tr>
              <th>{locale === "zh-CN" ? "时间" : "Time"}</th>
              <th>{locale === "zh-CN" ? "操作者" : "Actor"}</th>
              <th>{locale === "zh-CN" ? "原因" : "Reason"}</th>
              <th>{locale === "zh-CN" ? "接口" : "Path"}</th>
              <th>{locale === "zh-CN" ? "请求编号" : "Request"}</th>
            </tr>
          </thead>
          <tbody>
            {(data?.recentFailures ?? []).map((failure: any) => (
              <tr key={failure.id}>
                <td>{formatDateTimeInTimeZone(failure.createdAt, locale)}</td>
                <td>{failure.actorUsername || "-"}</td>
                <td>
                  <details>
                    <summary>{failureMessage(failure)}</summary>
                    {failure.fields?.length ? <small>{failure.fields.join(" · ")}</small> : null}
                  </details>
                </td>
                <td>
                  <strong>{failure.requestMethod}</strong> {failure.requestPath}
                </td>
                <td>{failure.requestId || failure.cfRay || "-"}</td>
              </tr>
            ))}
            {!data?.recentFailures?.length && (
              <tr>
                <td colSpan={5} className="empty-cell">
                  {tr(locale, "empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
      <div className="operations-secondary-grid">
        <section className="table-panel">
          <div className="panel-header">
            <div>
              <h2>{locale === "zh-CN" ? "最近成功操作" : "Recent successful operations"}</h2>
              <span className="muted">{data?.recentSuccesses?.length ?? 0}</span>
            </div>
          </div>
          <table className="data-table compact">
            <thead>
              <tr>
                <th>{locale === "zh-CN" ? "时间" : "Time"}</th>
                <th>{locale === "zh-CN" ? "操作" : "Action"}</th>
                <th>{locale === "zh-CN" ? "对象" : "Entity"}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recentSuccesses ?? []).map((item: any) => (
                <tr key={item.id}>
                  <td>{formatDateTimeInTimeZone(item.createdAt, locale)}</td>
                  <td>{item.action}</td>
                  <td>
                    {item.entityType} · {item.entityId || "-"}
                  </td>
                </tr>
              ))}
              {!data?.recentSuccesses?.length && (
                <tr>
                  <td colSpan={3} className="empty-cell">
                    {tr(locale, "empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
        <section className="table-panel">
          <div className="panel-header">
            <div>
              <h2>{locale === "zh-CN" ? "重复修改排行" : "Repeated edits"}</h2>
              <span className="muted">{data?.repeatedEntities?.length ?? 0}</span>
            </div>
          </div>
          <table className="data-table compact">
            <thead>
              <tr>
                <th>{locale === "zh-CN" ? "对象" : "Entity"}</th>
                <th>{locale === "zh-CN" ? "操作" : "Action"}</th>
                <th>{locale === "zh-CN" ? "次数" : "Count"}</th>
              </tr>
            </thead>
            <tbody>
              {(data?.repeatedEntities ?? []).map((item: any) => (
                <tr key={`${item.entityType}-${item.entityId}-${item.action}`}>
                  <td>
                    {item.entityType} · {item.entityId}
                  </td>
                  <td>{item.action}</td>
                  <td className="numeric">{item.count}</td>
                </tr>
              ))}
              {!data?.repeatedEntities?.length && (
                <tr>
                  <td colSpan={3} className="empty-cell">
                    {tr(locale, "empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

function SettingsPage({ locale, user, environment }: any) {
  const [backupStatus, setBackupStatus] = useState<any>();
  const [backupError, setBackupError] = useState("");
  const [backupLoading, setBackupLoading] = useState(true);
  const refreshBackupStatus = useCallback(async () => {
    setBackupLoading(true);
    setBackupError("");
    try {
      setBackupStatus(await api<any>("/operations/backup-status"));
    } catch (error) {
      setBackupError(error instanceof Error ? error.message : "备份状态读取失败");
    } finally {
      setBackupLoading(false);
    }
  }, []);
  useEffect(() => {
    const timer = window.setTimeout(() => void refreshBackupStatus(), 0);
    return () => window.clearTimeout(timer);
  }, [refreshBackupStatus]);
  const freshness = backupStatus?.freshness;
  const freshnessClass =
    freshness?.state === "healthy"
      ? "success-pill"
      : freshness?.state === "warning"
        ? "warning-pill"
        : "danger-pill";
  const freshnessLabel =
    freshness?.state === "healthy"
      ? locale === "zh-CN"
        ? "正常"
        : "Current"
      : freshness?.state === "warning"
        ? locale === "zh-CN"
          ? "需关注"
          : "Attention"
        : locale === "zh-CN"
          ? "需处理"
          : "Action required";
  const ageText =
    typeof freshness?.ageHours === "number"
      ? locale === "zh-CN"
        ? `${freshness.ageHours.toFixed(1)} 小时前`
        : `${freshness.ageHours.toFixed(1)} hours ago`
      : "-";
  return (
    <div className="page-stack">
      <div className="page-heading">
        <div>
          <div className="eyebrow">SYSTEM</div>
          <h1>{tr(locale, "settings")}</h1>
          <p className="muted">{user.username}</p>
        </div>
      </div>
      <div className="settings-grid">
        <section className="form-panel">
          <h2>{locale === "zh-CN" ? "登录账号" : "Account"}</h2>
          <div className="settings-facts">
            <span>
              {locale === "zh-CN" ? "用户名" : "Username"}
              <strong>{user.username}</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "显示名称" : "Display name"}
              <strong>{user.displayName || user.username}</strong>
            </span>
            <span>
              {locale === "zh-CN" ? "权限" : "Role"}
              <strong>{user.role}</strong>
            </span>
          </div>
        </section>
        <section className="form-panel">
          <h2>{locale === "zh-CN" ? "运行环境" : "Environment"}</h2>
          <div className="settings-facts">
            <span>
              Worker
              <strong>{String(environment || "unknown").toUpperCase()}</strong>
            </span>
            <span>
              Database
              <strong>Cloudflare D1</strong>
            </span>
            <span>
              Files
              <strong>Private R2</strong>
            </span>
          </div>
        </section>
        <section className="form-panel backup-status-panel">
          <div className="backup-status-heading">
            <div>
              <h2>{locale === "zh-CN" ? "备份状态" : "Backup status"}</h2>
              <span className={`status-pill ${freshnessClass}`}>{freshnessLabel}</span>
            </div>
            <button
              className="icon-button"
              type="button"
              title={locale === "zh-CN" ? "刷新备份状态" : "Refresh backup status"}
              aria-label={locale === "zh-CN" ? "刷新备份状态" : "Refresh backup status"}
              disabled={backupLoading}
              onClick={() => void refreshBackupStatus()}
            >
              <RefreshCw className={backupLoading ? "spin" : ""} size={17} />
            </button>
          </div>
          {backupError ? (
            <div className="error-box">{backupError}</div>
          ) : (
            <div className="settings-facts backup-status-facts">
              <span>
                {locale === "zh-CN" ? "最近成功备份" : "Last successful backup"}
                <strong>
                  {formatDateTimeInTimeZone(backupStatus?.latestSuccessful?.completedAt, locale)}
                </strong>
              </span>
              <span>
                {locale === "zh-CN" ? "备份新鲜度" : "Backup freshness"}
                <strong>{ageText}</strong>
              </span>
              <span>
                {locale === "zh-CN" ? "最近失败" : "Latest failure"}
                <strong>
                  {backupStatus?.latestFailed
                    ? formatDateTimeInTimeZone(backupStatus.latestFailed.completedAt, locale)
                    : locale === "zh-CN"
                      ? "无"
                      : "None"}
                </strong>
              </span>
              <span>
                {locale === "zh-CN" ? "备份对象" : "Backup objects"}
                <strong>
                  {backupStatus?.storage?.state === "available"
                    ? `${backupStatus.storage.objectCount} · ${formatByteSize(backupStatus.storage.sizeBytes)}`
                    : "-"}
                </strong>
              </span>
              <span>
                {locale === "zh-CN" ? "自动保留" : "Automatic retention"}
                <strong>
                  {backupStatus?.retention
                    ? locale === "zh-CN"
                      ? `日 ${backupStatus.retention.dailyDays} 天 · 周 ${backupStatus.retention.weeklyDays} 天 · 月 ${backupStatus.retention.monthly === "permanent" ? "永久" : backupStatus.retention.monthly}`
                      : `Daily ${backupStatus.retention.dailyDays}d · Weekly ${backupStatus.retention.weeklyDays}d · Monthly ${backupStatus.retention.monthly}`
                    : "-"}
                </strong>
              </span>
              <span>
                {locale === "zh-CN" ? "发布快照" : "Release snapshots"}
                <strong>
                  {backupStatus?.retention
                    ? backupStatus.retention.release === "permanent"
                      ? locale === "zh-CN"
                        ? "永久保留"
                        : "Permanent"
                      : backupStatus.retention.release
                    : "-"}
                </strong>
              </span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function StocktakeWindowApp({
  stocktakeId,
  locale,
  setLocale,
  environment,
  notify,
}: {
  stocktakeId: string;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  environment: string;
  notify: (message: string, kind?: "success" | "error") => void;
}) {
  const [stocktake, setStocktake] = useState<any>();
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      const result = await api<{ stocktake: any }>(`/inventory/stocktakes/${stocktakeId}`);
      setStocktake(result.stocktake);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "盘点读取失败");
    }
  }, [stocktakeId]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  useEffect(() => {
    if (!stocktake?.stocktakeNo) return;
    const previousTitle = document.title;
    document.title = `${stocktake.stocktakeNo} · YQ 盘点`;
    return () => {
      document.title = previousTitle;
    };
  }, [stocktake?.stocktakeNo]);
  const closeWindow = () => {
    window.close();
    window.setTimeout(() => {
      if (!window.closed) window.location.assign("/");
    }, 250);
  };
  const finishAndClose = async () => {
    closeWindow();
  };
  return (
    <main className="stocktake-window-shell">
      <header className="stocktake-window-topbar">
        <div className="stocktake-window-brand">
          <img className="brand-logo small" src="/brand/yq-depo-logo.png" alt="YQ Depo" />
          <div>
            <strong>{locale === "zh-CN" ? "库存盘点工作台" : "Stocktake workspace"}</strong>
            <span>{stocktake?.stocktakeNo ?? stocktakeId}</span>
          </div>
        </div>
        <div className="top-actions">
          <span className="env-tag">{String(environment || "unknown").toUpperCase()}</span>
          <button
            className="icon-button"
            type="button"
            title="Switch language"
            onClick={() => setLocale(locale === "zh-CN" ? "en" : "zh-CN")}
          >
            <Languages size={18} />
            <span>{locale === "zh-CN" ? "EN" : "中"}</span>
          </button>
          <button
            className="icon-button"
            type="button"
            title={locale === "zh-CN" ? "关闭盘点标签页" : "Close stocktake tab"}
            aria-label={locale === "zh-CN" ? "关闭盘点标签页" : "Close stocktake tab"}
            onClick={closeWindow}
          >
            <X size={18} />
          </button>
        </div>
      </header>
      <div className="stocktake-window-content">
        {stocktake ? (
          <StocktakeWorkspace
            stocktake={stocktake}
            locale={locale}
            notify={notify}
            onClose={closeWindow}
            onUpdated={async (next: any | undefined) => {
              if (next) setStocktake(next);
            }}
            onFinished={finishAndClose}
            showClose={false}
          />
        ) : error ? (
          <section className="stocktake-window-error">
            <CircleAlert size={22} />
            <strong>{error}</strong>
            <div className="action-row">
              <button className="secondary-button" type="button" onClick={() => void load()}>
                <RefreshCw size={15} />
                {tr(locale, "refresh")}
              </button>
              <button className="primary-button" type="button" onClick={closeWindow}>
                <X size={15} />
                {locale === "zh-CN" ? "关闭" : "Close"}
              </button>
            </div>
          </section>
        ) : (
          <div className="boot-screen stocktake-window-loading">
            <RefreshCw className="spin" size={20} />
            Loading
          </div>
        )}
      </div>
    </main>
  );
}

function App() {
  const stocktakeId = stocktakeIdFromPath(window.location.pathname);
  const [locale, setLocaleState] = useState<Locale>(
    (localStorage.getItem("yq_locale") as Locale) || "zh-CN",
  );
  const [user, setUser] = useState<any>();
  const [environment, setEnvironment] = useState("unknown");
  const [page, setPage] = useState("sales");
  const [toast, setToast] = useState<{ message: string; kind: string }>();
  const [orderSeed, setOrderSeed] = useState<OrderSeed>();
  const [orderDirty, setOrderDirty] = useState(false);
  const setLocale = (value: Locale) => {
    setLocaleState(value);
    localStorage.setItem("yq_locale", value);
    if (user)
      api("/auth/locale", { method: "PATCH", body: JSON.stringify({ locale: value }) }).catch(
        () => undefined,
      );
  };
  const notify = useCallback((message: string, kind: "success" | "error" = "success") => {
    setToast({ message, kind });
    window.setTimeout(() => setToast(undefined), 4500);
  }, []);
  useEffect(() => {
    api<{ environment: string }>("/health")
      .then((data) => setEnvironment(data.environment))
      .catch(() => setEnvironment("unknown"));
    api<{ user: any }>("/auth/me")
      .then((data) => setUser(data.user))
      .catch(() => setUser(null));
    if ("serviceWorker" in navigator)
      navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  }, []);
  const navigate = useCallback(
    (nextPage: string) => {
      if (nextPage === page) return true;
      if (
        (page === "sales" || page === "purchase") &&
        orderDirty &&
        !window.confirm(
          locale === "zh-CN"
            ? "当前单据有未保存修改，确认离开此页面？"
            : "This document has unsaved changes. Leave this page?",
        )
      )
        return false;
      setOrderSeed(undefined);
      setOrderDirty(false);
      setPage(nextPage);
      return true;
    },
    [locale, orderDirty, page],
  );
  useEffect(() => {
    if (!orderDirty) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [orderDirty]);
  const logout = async () => {
    if (
      orderDirty &&
      !window.confirm(
        locale === "zh-CN"
          ? "当前单据有未保存修改，确认退出登录？"
          : "This document has unsaved changes. Sign out anyway?",
      )
    )
      return;
    await api("/auth/logout", { method: "POST", body: "{}" }).catch(() => undefined);
    setOrderDirty(false);
    setOrderSeed(undefined);
    setUser(null);
  };
  const editDocument = (mode: "revise" | "return", document: any) => {
    setOrderDirty(false);
    setOrderSeed({ token: crypto.randomUUID(), mode, document });
    setPage(document.documentType.startsWith("sale") ? "sales" : "purchase");
  };
  const clearOrderSeed = useCallback(() => setOrderSeed(undefined), []);
  if (user === undefined)
    return (
      <div className="boot-screen">
        <RefreshCw className="spin" size={20} />
        Loading
      </div>
    );
  if (!user) return <Login locale={locale} setLocale={setLocale} onLogin={setUser} />;
  return (
    <>
      {stocktakeId ? (
        <StocktakeWindowApp
          stocktakeId={stocktakeId}
          locale={locale}
          setLocale={setLocale}
          environment={environment}
          notify={notify}
        />
      ) : (
        <Shell
          user={user}
          locale={locale}
          setLocale={setLocale}
          onLogout={logout}
          page={page}
          onNavigate={navigate}
          environment={environment}
        >
          {page === "sales" && (
            <OrderPage
              key={orderSeed?.token ?? "sale-new"}
              type="sale"
              locale={locale}
              notify={notify}
              seed={orderSeed}
              onSeedConsumed={clearOrderSeed}
              onDirtyChange={setOrderDirty}
            />
          )}
          {page === "purchase" && (
            <OrderPage
              key={orderSeed?.token ?? "purchase-new"}
              type="purchase"
              locale={locale}
              notify={notify}
              seed={orderSeed}
              onSeedConsumed={clearOrderSeed}
              onDirtyChange={setOrderDirty}
            />
          )}
          {page === "history" && (
            <HistoryPage locale={locale} notify={notify} onEdit={editDocument} />
          )}
          {page === "inventory" && <InventoryPage locale={locale} notify={notify} />}
          {page === "receivable" && (
            <SettlementAccountsPage side="receivable" locale={locale} notify={notify} />
          )}
          {page === "payable" && (
            <SettlementAccountsPage side="payable" locale={locale} notify={notify} />
          )}
          {page === "finance" && <FinancePage locale={locale} notify={notify} />}
          {page === "operations" && user.role === "admin" && (
            <OperationsPage locale={locale} notify={notify} />
          )}
          {page === "masters" && <MastersPage locale={locale} notify={notify} />}
          {page === "settings" && (
            <SettingsPage locale={locale} user={user} environment={environment} />
          )}
        </Shell>
      )}
      {toast && (
        <div className={`toast ${toast.kind}`}>
          <Check size={16} />
          {toast.message}
        </div>
      )}
    </>
  );
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
